/*
@license
  Shaazin by Team Shaazin (https://shaazin.net)
  Access unminified JS in assets/theme.js

  Use this event listener to run your own JS outside of this file.
  Documentation - https://shaazin.net/docs/javascript-events-for-developers

  document.addEventListener('page:loaded', function() {
    // Page has loaded and theme assets are ready
  });
*/

window.theme = window.theme || {};
window.Shopify = window.Shopify || {};

theme.config = {
  hasSessionStorage: true,
  hasLocalStorage: true,
  mqlSmall: false,
  mediaQuerySmall: 'screen and (max-width: 767px)',
  motionReduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  isTouch: ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0),
  rtl: document.documentElement.getAttribute('dir') === 'rtl' ? true : false
};

theme.supportsPassive = false;

try {
  const opts = Object.defineProperty({}, 'passive', {
    get: function () {
      theme.supportsPassive = true;
    }
  });
  window.addEventListener('testPassive', null, opts);
  window.removeEventListener('testPassive', null, opts);
} catch (e) { }

document.documentElement.classList.add(theme.config.isTouch ? 'touch' : 'no-touch');

// Touch press feedback: mobile browsers don't reliably apply :active for
// touch, so press state is driven by touch events. Press-in on touchstart,
// released on end/cancel — and on touchmove, so scroll-intent never leaves
// a control stuck pressed.
if (theme.config.isTouch) {
  (function () {
    const selector = 'button, .button, .cart-remove, a[is="hover-link"], a[is="magnet-link"], .dock__item, .drawer__menu-item, .color-swatch, .label-swatch';

    let pressStartX = 0, pressStartY = 0;

    document.addEventListener('touchstart', (event) => {
      const control = event.target.closest(selector);
      if (!control || control.disabled || control.getAttribute('aria-disabled') === 'true') return;
      pressStartX = event.changedTouches[0].clientX;
      pressStartY = event.changedTouches[0].clientY;
      control.classList.add('is-pressed');
    }, { passive: true });

    const release = () => {
      document.querySelectorAll('.is-pressed').forEach((el) => el.classList.remove('is-pressed'));
    };
    document.addEventListener('touchend', release, { passive: true });
    document.addEventListener('touchcancel', release, { passive: true });

    // Touch slop: fingers roll a few px during a normal tap (especially on
    // small targets like the qty +/- pills) — only treat it as scroll
    // intent once movement exceeds the tolerance.
    document.addEventListener('touchmove', (event) => {
      const t = event.changedTouches[0];
      if (Math.abs(t.clientX - pressStartX) > 8 || Math.abs(t.clientY - pressStartY) > 8) release();
    }, { passive: true });
  })();
}

(function () {
  /*============================================================================
    Things that require DOM to be ready
  ==============================================================================*/
  theme.DOMready = function (callback) {
    if (document.readyState != 'loading') callback();
    else document.addEventListener('DOMContentLoaded', callback);
  }

  theme.headerGroup = {
    rounded: () => {
      return Array.from(document.querySelectorAll('.shopify-section-group-header-group .section--rounded'));
    },
    sections: () => {
      return Array.from(document.querySelectorAll('.shopify-section-group-header-group'));
    },
    init: () => {
      let previousSection = null;
      const rounded = theme.headerGroup.rounded();
      const sections = theme.headerGroup.sections();

      sections.forEach((shopifySection, index) => {
        const section = shopifySection.querySelector('.section');
        if (section) {
          section.classList.remove('section--next-rounded');
          section.classList.remove('section--first-rounded');
          section.classList.remove('section--last-rounded');

          if (section.classList.contains('section--rounded')) {
            if (index === 0) {
              section.classList.add('section--first-rounded');
            }

            if (index === rounded.length - 1) {
              section.classList.add('section--last-rounded');
            }

            if (previousSection && previousSection.classList.contains('section--rounded')) {
              previousSection.classList.add('section--next-rounded');
            }
          }

          if (index === sections.length - 1) {
            const nextSection = document.querySelector('.main-content .shopify-section:first-child .section');
            if (nextSection && nextSection.classList.contains('section--rounded')) {
              section.classList.add('section--next-rounded');
            }
          }

          previousSection = section;
        }
      });
    }
  };

  theme.a11y = {
    trapFocusHandlers: {},

    // Input-modality tracker: removeTrapFocus restores focus to the drawer's
    // trigger programmatically, and Safari/Firefox match :focus-visible on
    // script focus — a click-close painted the keyboard ring on the trigger
    // (View All pill, cart icon, ...). Keyboard closes must keep the ring.
    lastInputWasKeyboard: false,

    getFocusableElements: (container) => {
      return Array.from(
        container.querySelectorAll(
          'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
        )
      );
    },

    trapFocus: (container, elementToFocus = container) => {
      const elements = theme.a11y.getFocusableElements(container);
      const first = elements[0];
      const last = elements[elements.length - 1];

      theme.a11y.removeTrapFocus();

      theme.a11y.trapFocusHandlers.focusin = (event) => {
        if (event.target !== container && event.target !== last && event.target !== first) return;

        document.addEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);
      };

      theme.a11y.trapFocusHandlers.focusout = function () {
        document.removeEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);
      };

      theme.a11y.trapFocusHandlers.keydown = function (event) {
        if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
        // On the last focusable element and tab forward, focus the first element.
        if (event.target === last && !event.shiftKey) {
          event.preventDefault();
          first.focus();
        }

        //  On the first focusable element and tab backward, focus the last element.
        if ((event.target === container || event.target === first) && event.shiftKey) {
          event.preventDefault();
          last.focus();
        }
      };

      document.addEventListener('focusout', theme.a11y.trapFocusHandlers.focusout);
      document.addEventListener('focusin', theme.a11y.trapFocusHandlers.focusin);

      elementToFocus.focus();

      if (elementToFocus.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
      }
    },

    removeTrapFocus: (elementToFocus = null) => {
      document.removeEventListener('focusin', theme.a11y.trapFocusHandlers.focusin);
      document.removeEventListener('focusout', theme.a11y.trapFocusHandlers.focusout);
      document.removeEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);

      // preventScroll: restoring focus to a control that is currently
      // off-screen (e.g. inside the smart header while it is hidden) must
      // not yank the page — closing a drawer should leave scroll exactly
      // where the unlock restored it.
      if (elementToFocus && typeof elementToFocus.focus === 'function') {
        elementToFocus.focus({ preventScroll: true });
        if (!theme.a11y.lastInputWasKeyboard && elementToFocus.classList) {
          elementToFocus.classList.add('focus-quiet');
          elementToFocus.addEventListener('blur', () => elementToFocus.classList.remove('focus-quiet'), { once: true });
        }
      }
    }
  };

  document.addEventListener('keydown', () => { theme.a11y.lastInputWasKeyboard = true; }, true);
  document.addEventListener('pointerdown', () => { theme.a11y.lastInputWasKeyboard = false; }, true);

  theme.utils = {
    // Start loading images that are still lazy inside `root`, at low priority.
    // Native lazy-loading only looks a short way past the viewport, and for a
    // horizontal rail (or a hidden tab panel) that means the shopper waits for
    // the fetch at the moment they swipe or switch. Warming ahead of them is
    // the same trick the infinite-scroll prefetch uses: do the work while they
    // are still looking at what is already on screen. `limit` caps how many are
    // warmed so a rail never pulls its whole tail at once, and images are
    // marked so repeat calls are cheap.
    //
    // fetchpriority stays LOW: this must never compete with the page's own LCP
    // image. Skipped entirely when the shopper has asked to save data.
    warmImages: (root, limit) => {
      if (!root) return 0;
      if (navigator.connection && navigator.connection.saveData) return 0;

      const images = root.querySelectorAll('img[loading="lazy"]:not([data-warmed])');
      const max = limit === undefined ? images.length : limit;
      let warmed = 0;
      for (let i = 0; i < images.length && warmed < max; i++) {
        const image = images[i];
        image.setAttribute('data-warmed', '');
        image.setAttribute('fetchpriority', 'low');
        image.loading = 'eager';
        warmed++;
      }
      return warmed;
    },
    // requestIdleCallback where it exists, a timeout where it does not.
    onIdle: (callback, timeout = 2000) => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback, { timeout });
        return;
      }
      setTimeout(callback, 200);
    },
    throttle: (callback) => {
      let requestId = null, lastArgs;
      const later = (context) => () => {
        requestId = null;
        callback.apply(context, lastArgs);
      };
      const throttled = (...args) => {
        lastArgs = args;
        if (requestId === null) {
          requestId = requestAnimationFrame(later(this));
        }
      };
      throttled.cancel = () => {
        cancelAnimationFrame(requestId);
        requestId = null;
      };
      return throttled;
    },

    debounce: (fn, wait) => {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    waitForEvent: (element, eventName) => {
      return new Promise((resolve) => {
        const done = (event) => {
          if (event.target === element) {
            element.removeEventListener(eventName, done);
            resolve(event);
          }
        };
        element.addEventListener(eventName, done);
      });
    },

    fetchConfig: (type = 'json', method = 'POST') => {
      return {
        method: method,
        headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
      };
    },

    postLink: (path, options) => {
      options = options || {};
      const method = options['method'] || 'post';
      const params = options['parameters'] || {};

      const form = document.createElement("form");
      form.setAttribute("method", method);
      form.setAttribute("action", path);

      for (const key in params) {
        const hiddenField = document.createElement("input");
        hiddenField.setAttribute("type", "hidden");
        hiddenField.setAttribute("name", key);
        hiddenField.setAttribute("value", params[key]);
        form.appendChild(hiddenField);
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    postLink2: (path, options) => {
      options = options || {};
      const method = options['method'] || 'post';
      const params = options['parameters'] || {};

      const form = document.createElement("form");
      form.setAttribute("method", method);
      form.setAttribute("action", path);

      for (const key in params) {
        if (typeof params[key] === 'string') {
          const hiddenField = document.createElement("input");
          hiddenField.setAttribute("type", "hidden");
          hiddenField.setAttribute("name", key);
          hiddenField.setAttribute("value", params[key]);
          form.appendChild(hiddenField);
        }
        else {
          for (const index in params[key]) {
            for (const key2 in params[key][index]) {
              const hiddenField = document.createElement("input");
              hiddenField.setAttribute("type", "hidden");
              hiddenField.setAttribute("name", `${key}[${index}][${key2}]`);
              hiddenField.setAttribute("value", params[key][index][key2]);
              form.appendChild(hiddenField);
            }
          }
        }
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    sectionId: (element) => {
      if (element.hasAttribute('data-section-id')) return element.getAttribute('data-section-id');

      element = element.classList.contains('shopify-section') ? element : element.closest('.shopify-section');
      return element.id.replace('shopify-section-', '');
    },

    imageLoaded: (imageOrArray) => {
      if (!imageOrArray) {
        return Promise.resolve();
      }
      imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);
      return Promise.all(imageOrArray.map((image) => {
        return new Promise((resolve) => {
          if (image.tagName === "IMG" && image.complete || !image.offsetParent) {
            resolve();
          } else {
            image.onload = () => resolve();
          }
        });
      }));
    },

    visibleMedia: (media) => {
      return Array.from(media).find((item) => {
        const style = window.getComputedStyle(item);
        return style.display !== 'none';
      });
    },

    setScrollbarWidth: () => {
      const scrollbarWidth = window.innerWidth - document.body.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth > 0 ? scrollbarWidth : 0}px`);
    },

    externalLinksNewTab: () => {
      if (theme.settings.externalLinksNewTab) {
        document.addEventListener('click', (evt) => {
          const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
          if (link && link.tagName === 'A' && window.location.hostname !== new URL(link.href).hostname) {
            link.target = '_blank';
          }
        });
      }
    }
  };

  theme.HTMLUpdateUtility = {
    /**
     * Used to swap an HTML node with a new node.
     * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
     *
     * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
     */
    viewTransition: (oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) => {
      preProcessCallbacks?.forEach((callback) => callback(newContent));

      const newNodeWrapper = document.createElement('div');
      theme.HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
      const newNode = newNodeWrapper.firstChild;

      // dedupe IDs
      const uniqueKey = Date.now();
      oldNode.querySelectorAll('[id], [form]').forEach((element) => {
        element.id && (element.id = `${element.id}-${uniqueKey}`);
        element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
      });

      oldNode.parentNode.insertBefore(newNode, oldNode);
      oldNode.style.display = 'none';

      postProcessCallbacks?.forEach((callback) => callback(newNode));

      setTimeout(() => oldNode.remove(), 500);
    },

    // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    setInnerHTML: (element, html) => {
      element.innerHTML = html;
      element.querySelectorAll('script').forEach((oldScriptTag) => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach((attribute) => {
          newScriptTag.setAttribute(attribute.name, attribute.value);
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }
  };

  theme.Currency = {
    formatMoney: (cents, format = "") => {
      if (typeof cents === "string") {
        cents = cents.replace(".", "");
      }
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || theme.settings.moneyFormat;
      function defaultTo(value2, defaultValue) {
        return value2 == null || value2 !== value2 ? defaultValue : value2;
      }
      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultTo(precision, 2);
        thousands = defaultTo(thousands, ",");
        decimal = defaultTo(decimal, ".");
        if (isNaN(number) || number == null) {
          return 0;
        }
        number = (number / 100).toFixed(precision);
        let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
        if (theme.settings && theme.settings.priceHideDecimals && parts[1] === "00") {
          centsAmount = "";
        }
        return dollarsAmount + centsAmount;
      }
      let value = "";
      switch (formatString.match(placeholderRegex)[1]) {
        case "amount":
          value = formatWithDelimiters(cents, 2);
          break;
        case "amount_no_decimals":
          value = formatWithDelimiters(cents, 0);
          break;
        case "amount_with_space_separator":
          value = formatWithDelimiters(cents, 2, " ", ".");
          break;
        case "amount_with_comma_separator":
          value = formatWithDelimiters(cents, 2, ".", ",");
          break;
        case "amount_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 2, "'", ".");
          break;
        case "amount_no_decimals_with_comma_separator":
          value = formatWithDelimiters(cents, 0, ".", ",");
          break;
        case "amount_no_decimals_with_space_separator":
          value = formatWithDelimiters(cents, 0, " ");
          break;
        case "amount_no_decimals_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 0, "'");
          break;
        default:
          value = formatWithDelimiters(cents, 2);
          break;
      }
      if (formatString.indexOf("with_comma_separator") !== -1) {
        return formatString.replace(placeholderRegex, value);
      } else {
        return formatString.replace(placeholderRegex, value);
      }
    }
  };

  theme.pubsub = {
    PUB_SUB_EVENTS: {
      cartUpdate: 'cartUpdate',
      quantityUpdate: 'quantityUpdate',
      variantChange: 'variantChange',
      cartError: 'cartError',
      discountUpdate: 'discountUpdate',
      facetUpdate: 'facetUpdate',
      quantityRules: 'quantityRules',
      quantityBoundries: 'quantityBoundries',
      optionValueSelectionChange: 'optionValueSelectionChange',
    },

    subscribers: {},

    subscribe: (eventName, callback) => {
      if (theme.pubsub.subscribers[eventName] === undefined) {
        theme.pubsub.subscribers[eventName] = [];
      }

      theme.pubsub.subscribers[eventName] = [...theme.pubsub.subscribers[eventName], callback];

      return function unsubscribe() {
        theme.pubsub.subscribers[eventName] = theme.pubsub.subscribers[eventName].filter((cb) => {
          return cb !== callback
        });
      }
    },

    publish: (eventName, data) => {
      if (theme.pubsub.subscribers[eventName]) {
        theme.pubsub.subscribers[eventName].forEach((callback) => {
          callback(data);
        })
      }
    }
  };

  theme.scrollShadow = {
    Updater: (function () {
      class Updater {
        constructor(targetElement) {
          this.cb = () => this.update(targetElement);
          this.rO = new ResizeObserver(this.cb);
          this.mO = new MutationObserver(() => this.on(this.el));
        }
        on(element) {
          if (this.el) {
            this.el.removeEventListener("scroll", this.cb);
            this.rO.disconnect();
            this.mO.disconnect();
          }
          if (!element)
            return;
          if (element.nodeName === "TABLE" && !/scroll|auto/.test(getComputedStyle(element).getPropertyValue("overflow"))) {
            this.rO.observe(element);
            element = element.tBodies[0];
          }
          element.addEventListener("scroll", this.cb);
          [element, ...element.children].forEach((el) => this.rO.observe(el));
          this.mO.observe(element, { childList: true });
          this.el = element;
        }
        update(targetElement) {
          const scrollTop = this.el.scrollTop;
          const scrollBottom = this.el.scrollHeight - this.el.offsetHeight - this.el.scrollTop;
          const scrollLeft = this.el.scrollLeft;
          const scrollRight = this.el.scrollWidth - this.el.offsetWidth - this.el.scrollLeft * (theme.config.rtl ? -1 : 1);
          let cssText = `--t: ${Math.floor(scrollTop) > 0 ? 1 : 0}; --b: ${Math.floor(scrollBottom) > 0 ? 1 : 0}; --l: ${Math.floor(scrollLeft) > 0 ? 1 : 0}; --r: ${Math.floor(scrollRight) > 0 ? 1 : 0};`;
          if (this.el.nodeName === "TBODY") {
            const clientRect = this.el.getBoundingClientRect();
            const rootClientRect = this.el.parentElement.getBoundingClientRect();
            cssText += `top: ${clientRect.top - rootClientRect.top}px; bottom: ${rootClientRect.bottom - clientRect.bottom}px; left: ${clientRect.left - rootClientRect.left}px; right: ${rootClientRect.right - clientRect.right}px;`;
          }
          requestAnimationFrame(() => {
            targetElement.style.cssText = cssText;
          });
        }
      }

      return Updater;
    })()
  };

  theme.cookiesEnabled = function () {
    let cookieEnabled = navigator.cookieEnabled;

    if (!cookieEnabled) {
      document.cookie = 'testcookie';
      cookieEnabled = document.cookie.indexOf('testcookie') !== -1;
    }
    return cookieEnabled;
  };

  theme.isStorageSupported = function (type) {
    // Return false if we are in an iframe without access to sessionStorage
    if (window.self !== window.top) {
      return false;
    }

    const testKey = 'test';
    let storage;
    if (type === 'session') {
      storage = window.sessionStorage;
    }
    if (type === 'local') {
      storage = window.localStorage;
    }

    try {
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return true;
    }
    catch (error) {
      // Do nothing, this may happen in Safari in incognito mode
      return false;
    }
  };

  theme.getElementWidth = function (element, value) {
    const style = window.getComputedStyle(element);

    const text = document.createElement('span');
    text.style.fontFamily = style.fontFamily;
    text.style.fontSize = style.fontSize;
    text.style.fontWeight = style.fontWeight;
    text.style.visibility = 'hidden';
    text.style.position = 'absolute';
    text.innerHTML = value;

    document.body.appendChild(text);
    const width = text.getBoundingClientRect().width;
    text.remove();

    return width;
  };

  theme.HoverButton = (function () {
    class HoverButton {
      constructor(container) {
        this.container = container;
      }

      get btnFill() {
        return this.container.querySelector('[data-fill]');
      }

      load() {
        if (theme.config.isTouch) return;

        const hoverMode = document.body.getAttribute('data-button-hover');
        if (hoverMode === 'none') return;

        if (hoverMode === 'spotlight') {
          this.container.addEventListener('mouseenter', this.onSpotlightEnter.bind(this));
          this.container.addEventListener('mouseleave', this.onSpotlightLeave.bind(this));
        } else if (hoverMode === 'standard') {
          this.container.addEventListener('mouseenter', this.onEnterHandler.bind(this));
          this.container.addEventListener('mouseleave', this.onLeaveHandler.bind(this));
        }
        // swipe, diagonal, and smoosh are pure CSS transitions; spotlight uses JS only to place the origin
      }

      onEnterHandler() {
        if (this.btnFill) {
          Motion.animate(this.btnFill, { y: ['76%', '0%'] }, { duration: 0.6 });
        }
      }

      onLeaveHandler() {
        if (this.btnFill) {
          Motion.animate(this.btnFill, { y: '-76%' }, { duration: 0.6 });
        }
      }

      onSpotlightEnter(event) {
        if (!this.btnFill) return;
        const rect = this.container.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        this.btnFill.style.left = x + 'px';
        this.btnFill.style.top = y + 'px';
        this.container.classList.add('hover-active');
      }

      onSpotlightLeave(event) {
        if (!this.btnFill) return;
        const rect = this.container.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        this.btnFill.style.left = x + 'px';
        this.btnFill.style.top = y + 'px';
        this.container.classList.remove('hover-active');
      }

      unload() {
        // todo
      }
    }

    return HoverButton;
  })();

  theme.MagnetButton = (function () {
    const config = {
      magnet: 10
    };

    class MagnetButton {
      constructor(container) {
        this.container = container;
        this.magnet = container.hasAttribute('data-magnet') ? parseInt(container.getAttribute('data-magnet')) : config.magnet;
      }

      get btnText() {
        return this.container.querySelector('[data-text]');
      }

      get bounding() {
        return this.container.getBoundingClientRect();
      }

      load() {
        if (theme.config.isTouch || document.body.getAttribute('data-button-hover') === 'none') return;
        // Theme settings › Buttons › Icon hover (v2.10.74): Magnetic (the default, as before),
        // Animated (v2.10.75: each icon plays its own CSS animation instead — owner: replace, not
        // combine) or None. Only Magnetic keeps the pull.
        if (['none', 'animated'].includes(document.body.getAttribute('data-icon-hover'))) return;

        this.container.addEventListener('mousemove', this.onMoveHandler.bind(this));
        this.container.addEventListener('mouseleave', this.onLeaveHandler.bind(this));
      }

      onMoveHandler(event) {
        if (theme.config.motionReduced) return;
        if (this.magnet === 0) return;

        if (this.btnText) {
          Motion.animate(this.btnText, {
            x: ((event.clientX - this.bounding.left) / this.container.offsetWidth - 0.5) * this.magnet,
            y: ((event.clientY - this.bounding.top) / this.container.offsetHeight - 0.5) * this.magnet,
          }, { duration: 1.5, easing: Motion.spring() });
        }
        else {
          Motion.animate(this.container, {
            x: ((event.clientX - this.bounding.left) / this.container.offsetWidth - 0.5) * this.magnet,
            y: ((event.clientY - this.bounding.top) / this.container.offsetHeight - 0.5) * this.magnet,
          }, { duration: 1.5, easing: Motion.spring() });
        }
      }

      onLeaveHandler() {
        if (theme.config.motionReduced) return;
        if (this.magnet === 0) return;

        if (this.btnText) {
          Motion.animate(this.btnText, { x: 0, y: 0 }, { duration: 1.5, easing: Motion.spring() });
        }
        else {
          Motion.animate(this.container, { x: 0, y: 0 }, { duration: 1.5, easing: Motion.spring() });
        }
      }

      unload() {
        // todo
      }
    }

    return MagnetButton;
  })();

  theme.RevealButton = (function () {
    const config = {
      magnet: 25
    };

    class RevealButton {
      constructor(container) {
        this.container = container;
        this.magnet = container.hasAttribute('data-magnet') ? parseInt(container.getAttribute('data-magnet')) : config.magnet;
      }

      get btnText() {
        return this.container.querySelector('[data-text]');
      }

      get btnReveal() {
        return this.container.querySelector('[data-reveal]');
      }

      get bounding() {
        return this.container.getBoundingClientRect();
      }

      load() {
        if (theme.config.isTouch) return;

        this.container.addEventListener('mousemove', this.onMoveHandler.bind(this));
        this.container.addEventListener('mouseleave', this.onLeaveHandler.bind(this));
      }

      onMoveHandler(event) {
        if (this.btnReveal) {
          Motion.animate(this.btnReveal, {
            opacity: 1,
            x: ((event.clientX - this.bounding.left) / this.container.offsetWidth - 0.5) * this.magnet,
            y: ((event.clientY - this.bounding.top) / this.container.offsetHeight - 0.5) * this.magnet,
          }, { duration: 0.2, easing: 'steps(2, start)' });
        }
        else if (this.btnText) {
          Motion.animate(this.btnText, {
            x: ((event.clientX - this.bounding.left) / this.container.offsetWidth - 0.5) * this.magnet,
            y: ((event.clientY - this.bounding.top) / this.container.offsetHeight - 0.5) * this.magnet,
          }, { duration: 1.5, easing: Motion.spring() });
        }
        else {
          Motion.animate(this.container, {
            x: ((event.clientX - this.bounding.left) / this.container.offsetWidth - 0.5) * this.magnet,
            y: ((event.clientY - this.bounding.top) / this.container.offsetHeight - 0.5) * this.magnet,
          }, { duration: 1.5, easing: Motion.spring() });
        }
      }

      onLeaveHandler() {
        if (this.btnReveal) {
          Motion.animate(this.btnReveal, { x: 0, y: 0, opacity: 0 }, { duration: 0.2, easing: [0.61, 1, 0.88, 1] });
        }
        else if (this.btnText) {
          Motion.animate(this.btnText, { x: 0, y: 0 }, { duration: 1.5, easing: Motion.spring() });
        }
        else {
          Motion.animate(this.container, { x: 0, y: 0 }, { duration: 1.5, easing: Motion.spring() });
        }
      }

      unload() {
        // todo
      }
    }

    return RevealButton;
  })();

  theme.Animation = (function () {
    class Animation {
      constructor(container) {
        this.container = container;
      }

      get immediate() {
        return this.container.hasAttribute('data-immediate');
      }

      get media() {
        return Array.from(this.container.querySelectorAll('img, iframe, svg, g-map'));
      }

      get type() {
        return this.container.hasAttribute('data-animate') ? this.container.getAttribute('data-animate') : 'fade-up';
      }

      get delay() {
        return this.container.hasAttribute('data-animate-delay') ? parseInt(this.container.getAttribute('data-animate-delay')) / 1000 : 0;
      }

      get paused() {
        return this.container.hasAttribute('paused');
      }

      beforeLoad() {
        if (this.type === 'none' || this.paused) return;

        switch (this.type) {
          case 'fade-in':
            Motion.animate(this.container, { opacity: 0 }, { duration: 0 });
            break;

          case 'fade-up':
            Motion.animate(this.container, { transform: 'translateY(min(2rem, 90%))', opacity: 0 }, { duration: 0 });
            break;

          case 'fade-up-large':
            Motion.animate(this.container, { transform: 'translateY(90%)', opacity: 0 }, { duration: 0 });
            break;

          case 'zoom-out':
            Motion.animate(this.container, { transform: 'scale(1.3)' }, { duration: 0 });
            break;
        }
      }

      async load() {
        if (this.type === 'none' || this.paused) return;

        switch (this.type) {
          case 'fade-in':
            await Motion.animate(this.container, { opacity: 1 }, { duration: 1.5, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'fade-up':
            await Motion.animate(this.container, { transform: 'translateY(0)', opacity: 1 }, { duration: 1.5, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'fade-up-large':
            await Motion.animate(this.container, { transform: 'translateY(0)', opacity: 1 }, { duration: 1, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'zoom-out':
            await Motion.animate(this.container, { transform: 'scale(1)' }, { duration: 1.3, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
            break;
        }

        this.container.classList.add('animated');
      }

      async reset(duration) {
        if (this.type === 'none') return;

        switch (this.type) {
          case 'fade-in':
            await Motion.animate(this.container, { opacity: 0 }, { duration: duration ? duration : 1.5, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'fade-up':
            await Motion.animate(this.container, { transform: 'translateY(max(-2rem, -90%))', opacity: 0 }, { duration: duration ? duration : 1.5, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'fade-up-large':
            await Motion.animate(this.container, { transform: 'translateY(-90%)', opacity: 0 }, { duration: duration ? duration : 1, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'zoom-out':
            await Motion.animate(this.container, { transform: 'scale(0)' }, { duration: duration ? duration : 1.3, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
            break;
        }

        this.container.classList.remove('animated');
      }

      reload() {
        if (this.type === 'none') return;

        this.container.removeAttribute('paused');
        this.beforeLoad();
        this.load();
      }

      unload() {
        // todo
      }
    }

    return Animation;
  })();

  theme.Carousel = (function () {
    class Carousel {
      constructor(container, options, navigation) {
        this.container = container;
        this.options = options;
        this.prevButton = navigation.previous;
        this.nextButton = navigation.next;
      }

      load() {
        this.slider = new Flickity(this.container, this.options);

        this.prevButton.addEventListener('click', this.onPrevButtonClick.bind(this));
        this.nextButton.addEventListener('click', this.onNextButtonClick.bind(this));

        this.slider.on('dragStart', this.onDragStartHandler.bind(this));
        this.slider.on('select', this.onSelectHandler.bind(this));
      }

      onDragStartHandler() {
        this.prevRemoveTransform();
        this.nextRemoveTransform();
      }

      onSelectHandler() {
        if (!this.slider.slides[this.slider.selectedIndex - 1]) {
          this.prevButton.disabled = true;
          this.nextButton.disabled = false;
        }
        else if (!this.slider.slides[this.slider.selectedIndex + 1]) {
          this.prevButton.disabled = false;
          this.nextButton.disabled = true;
        }
        else {
          this.prevButton.disabled = false;
          this.nextButton.disabled = false;
        }
      }

      onPrevButtonClick(event) {
        event.preventDefault();

        this.slider.previous();
        this.nextRemoveTransform();
      }

      onNextButtonClick(event) {
        event.preventDefault();

        this.slider.next();
        this.prevRemoveTransform();
      }

      prevRemoveTransform() {
        this.prevButton.style.transform = null;
        this.prevButton.querySelector('[data-fill]').style.transform = null;
      }

      nextRemoveTransform() {
        this.nextButton.style.transform = null;
        this.nextButton.querySelector('[data-fill]').style.transform = null;
      }

      unload() {
        // todo
      }
    }

    return Carousel;
  })();

  // Delay JavaScript until user interaction
  theme.initWhenVisible = (function () {
    class ScriptLoader {
      constructor(callback, delay = 5000) {
        this.callback = callback;
        this.triggered = false;
        this.timeoutId = null;

        this.interactionEvents = ['click', 'mousemove', 'keydown', 'touchstart', 'touchmove', 'wheel'];
        this.handleInteraction = this.handleInteraction.bind(this);

        this.interactionEvents.forEach(eventType => {
          window.addEventListener(eventType, this.handleInteraction, { passive: true });
        });

        this.timeoutId = setTimeout(() => {
          if (!this.triggered) {
            this.handleInteraction({ type: 'timeout' });
          }
        }, delay);
      }

      handleInteraction(event) {
        if (this.triggered) return;

        this.triggered = true;
        this.callback(event);
        this.cleanup();
      }

      cleanup() {
        this.interactionEvents.forEach(eventType => {
          window.removeEventListener(eventType, this.handleInteraction, { passive: true });
        });

        clearTimeout(this.timeoutId);
        this.timeoutId = null;
        this.callback = null;
      }
    }

    return ScriptLoader;
  })();

  // Improve initial load time by skipping the rendering of offscreen content
  new theme.initWhenVisible(() => {
    document.body.removeAttribute('data-page-rendering');
  });

  // Improve iOS Safari resizing
  theme.windowWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    const { innerWidth } = window;
    if (innerWidth !== theme.windowWidth) {
      theme.windowWidth = innerWidth;
      document.dispatchEvent(new CustomEvent('window:resize'));
    }
  });

  theme.DOMready(theme.headerGroup.init);
  theme.DOMready(theme.utils.setScrollbarWidth);
  theme.DOMready(theme.utils.externalLinksNewTab);
  document.addEventListener('window:resize', theme.utils.throttle(theme.utils.setScrollbarWidth));

  /*============================================================================
    Things that don't require DOM to be ready
  ==============================================================================*/

  theme.config.hasSessionStorage = theme.isStorageSupported('session');
  theme.config.hasLocalStorage = theme.isStorageSupported('local');

  // Trigger events when going between breakpoints
  const mql = window.matchMedia(theme.config.mediaQuerySmall);
  theme.config.mqlSmall = mql.matches;
  mql.onchange = (mql) => {
    if (mql.matches) {
      theme.config.mqlSmall = true;
      document.dispatchEvent(new CustomEvent('matchSmall'));
    }
    else {
      theme.config.mqlSmall = false;
      document.dispatchEvent(new CustomEvent('unmatchSmall'));
    }
  }

  // Page has loaded and theme assets are ready
  window.addEventListener('DOMContentLoaded', () => {
    document.body.classList.add('loaded');
    document.dispatchEvent(new CustomEvent('page:loaded'));
  });
  window.addEventListener('pageshow', (event) => {
    // Removes unload class when returning to page via history
    if (event.persisted) {
      document.body.classList.remove('unloading');
    }
  });
  window.addEventListener('beforeunload', () => {
    document.body.classList.add('unloading');
  });
})();

// Page transitions: the motion styles (fade / slide-up / zoom / curtain) use the
// native cross-document View Transitions API — see snippets/page-transition.liquid
// (the @view-transition opt-in + per-style ::view-transition animations) and the
// `@supports not (view-transition-name)` enter-fallback in theme.css.
//
// There is intentionally NO click interceptor here: it previously held back
// `window.location.href` by 320–570ms to play an exit animation, which added that
// latency to every internal click, applied unevenly (it skipped is=/aria-controls
// links), and could strand the page in a "leaving" state. View Transitions give a
// smooth exit+enter with zero delay, uniformly across every navigation, and
// degrade to instant navigation on unsupported browsers. spinner/progress are
// shown via the body 'unloading' class + CSS; "none" does nothing.

// Touch over a Flickity carousel: ONE direction per gesture (v2.10.69).
// Flickity starts dragging after 3px of sideways movement whatever the
// vertical movement, so a slightly diagonal scroll used to scroll the page AND
// change the slide (Versa, Oct 2026; the June axis check only stopped the page
// scroll being cancelled). Now the carousel is held at touch-down; once the
// finger has moved DECIDE px the gesture is locked:
//   vertical   — the page scrolls, the carousel stays held until the finger lifts;
//   horizontal — the carousel is released and drags, the page stays still
//                (touchmove cancelled, as in Concept).
// Window capture listeners run before Flickity's own (pointer or touch) ones.
new theme.initWhenVisible(() => {
  const DECIDE = 6;
  let gesture = null;

  const start = (target, x, y) => {
    if (gesture) return;
    const el = target && target.closest ? target.closest('.flickity-enabled') : null;
    const flkty = el && window.Flickity ? Flickity.data(el) : null;
    if (!flkty || !flkty.options) return;
    gesture = { flkty, x, y, axis: null, saved: flkty.options.dragThreshold };
    flkty.options.dragThreshold = Infinity;
  };

  const decide = (x, y) => {
    if (!gesture || gesture.axis) return;
    const dx = Math.abs(x - gesture.x);
    const dy = Math.abs(y - gesture.y);
    if (dx < DECIDE && dy < DECIDE) return;
    gesture.axis = dx > dy ? 'x' : 'y';
    if (gesture.axis === 'x') gesture.flkty.options.dragThreshold = gesture.saved;
  };

  const end = () => {
    if (!gesture) return;
    gesture.flkty.options.dragThreshold = gesture.saved;
    gesture = null;
  };

  const capture = { capture: true, passive: true };
  window.addEventListener('pointerdown', (event) => { if (event.pointerType === 'touch') start(event.target, event.clientX, event.clientY); }, capture);
  window.addEventListener('touchstart', (event) => { if (event.touches.length === 1) start(event.target, event.touches[0].clientX, event.touches[0].clientY); else end(); }, capture);
  window.addEventListener('pointermove', (event) => { if (event.pointerType === 'touch') decide(event.clientX, event.clientY); }, capture);
  window.addEventListener('touchmove', (event) => {
    if (!gesture) return;
    decide(event.touches[0].clientX, event.touches[0].clientY);
  }, capture);
  // Not pointercancel: the browser sends it when it takes over a vertical
  // scroll while the finger is still moving, and the hold must outlast that.
  ['pointerup', 'touchend', 'touchcancel'].forEach((type) => window.addEventListener(type, end, capture));

  // A horizontal gesture keeps the page still while the carousel drags.
  document.body.addEventListener('touchmove', (event) => {
    if (gesture && gesture.axis === 'x' && event.cancelable) event.preventDefault();
  }, theme.supportsPassive ? { passive: false } : false);
});



class MouseCursor extends HTMLElement {
  constructor() {
    super();

    if (theme.config.isTouch) return;

    this.config = {
      posX: 0,
      posY: 0,
    };

    document.addEventListener('mousemove', (event) => {
      this.config.posX += (event.clientX - this.config.posX) / 4;
      this.config.posY += (event.clientY - this.config.posY) / 4;

      this.style.setProperty('--x', `${this.config.posX}px`);
      this.style.setProperty('--y', `${this.config.posY}px`);

      // Off the overlay, the close cursor is off (v2.10.73). It is switched on by an
      // overlay's mousemove and off by its mouseleave, but no mouseleave comes when the
      // overlay disappears under a still mouse (ONEi, Chrome on Windows: the round close
      // stayed in the middle of the page after the menu drawer closed).
      if (this.classList.contains('active') && !event.target.closest?.('overlay-element')) this.reset();
    });
  }

  reset() {
    this.classList.remove('active', 'pressed');
    this.setAttribute('aria-hidden', 'true');
  }
}
customElements.define('mouse-cursor', MouseCursor);

class CustomHeader extends HTMLElement {
  constructor() {
    super();
  }

  get allowTransparent() {
    if (document.querySelector('.shopify-section:first-child [allow-transparent-header], .header-section + .shopify-section [allow-transparent-header]')) {
      return true;
    }

    return false;
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }

    if (Shopify.designMode) {
      // Re-evaluate header transparency whenever ANY section loads/unloads/reorders,
      // not just the header's own. Transparency depends on the FIRST section's
      // [allow-transparent-header] attribute, so toggling e.g. a collection banner's
      // image must update the header live in the editor without needing a Save.
      document.addEventListener('shopify:section:load', this.init.bind(this));
      document.addEventListener('shopify:section:unload', this.init.bind(this));
      document.addEventListener('shopify:section:reorder', this.init.bind(this));
    }
  }

  init() {
    this.setHeight();

    if (this.allowTransparent) {
      this.headerSection.classList.add('header-transparent');
      this.headerSection.classList.add('no-animate');

      setTimeout(() => {
        this.headerSection.classList.remove('no-animate');
      }, 500);
    }
    else {
      this.headerSection.classList.remove('header-transparent');
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty('--header-height', this.getBoundingClientRect().height.toFixed(1) + 'px');

      if (this.classList.contains('header--center') && document.documentElement.style.getPropertyValue('--header-nav-height').length === 0) {
        document.documentElement.style.setProperty('--header-nav-height', Math.round(document.getElementById('MenuToggle')?.clientHeight) + 'px');
      }
    });
  }
}
customElements.define('custom-header', CustomHeader, { extends: 'header' });

class StickyHeader extends CustomHeader {
  constructor() {
    super();
    this.isScrolled = false; // Track scroll state to prevent class toggling
    this.buffer = 10; // Pixel buffer to create smooth transition zone
  }

  get isAlwaysSticky() {
    return this.getAttribute('data-sticky-type') === 'always';
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize scroll tracking values
    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;
    this.initialHeaderHeight = this.getBoundingClientRect().height; // Cache initial height
    this.headerBounds = this.headerSection.getBoundingClientRect();

    this.headerSection.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'padding-block-end' || event.propertyName === 'padding-bottom') {
        this.updateStickyHeight();
      }
    });

    this.beforeInit();
  }

  beforeInit() {
    this.headerSection.classList.add('header-sticky');
    this.headerSection.setAttribute('data-sticky-type', this.getAttribute('data-sticky-type'));

    if (this.isAlwaysSticky) {
      this.headerSection.classList.add('header-sticky');
    }

    window.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)), false);
    this.updateDockProgress(window.scrollY);
  }

  updateStickyHeight() {
    if (this.isScrolled) {
      document.documentElement.style.setProperty('--sticky-header-height', this.getBoundingClientRect().height.toFixed(1) + 'px');
    }
  }

  updateDockProgress(scrollTop) {
    // Scroll-linked corner morph: 0 (resting) -> 1 (docked). Reaches 1 at
    // the exact scroll position where the sticky bar touches the viewport
    // top, so the radius is always in sync with the scroll — a timed
    // transition would lag behind fast scrolling.
    const stickPoint = this.headerBounds.top + this.firstScrollTop;
    const dockRange = Math.max(stickPoint, this.initialHeaderHeight * 0.5);
    const progress = Math.min(Math.max(scrollTop / dockRange, 0), 1);
    this.headerSection.style.setProperty('--header-dock', progress.toFixed(3));
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;

    // While a locking modal is open the viewport is frozen — any scroll
    // event that still slips through (engine quirks, focus nudges) is
    // synthetic. Freeze the header state and re-sync the baseline so
    // Smart never reads it as real scrolling.
    if (document.body.classList.contains('has-modal-open') || document.body.classList.contains('has-modal-opening')) {
      this.currentScrollTop = scrollTop;
      return;
    }

    this.updateDockProgress(scrollTop);

    // Scrolled state engages at the exact point the sticky header touches
    // the viewport top (its natural document offset) so the corner/padding
    // morph aligns with the moment of docking; nav hide threshold unchanged
    const scrollThreshold = this.headerBounds.top + this.firstScrollTop;
    const scrollThresholdNav = this.headerBounds.top + this.firstScrollTop + this.initialHeaderHeight * 2;

    // Hysteresis: Different thresholds for scrolling down vs up prevents rapid toggling
    const scrollDownThreshold = scrollThreshold + this.buffer;
    const scrollUpThreshold = scrollThreshold - this.buffer;

    // Toggle 'header-scrolled' class with buffer zone
    if (!this.isScrolled && scrollTop > scrollDownThreshold) {
      // Scrolling down past threshold
      this.isScrolled = true;
      this.headerSection.classList.add('header-scrolled');
      document.dispatchEvent(new CustomEvent('header:scrolled', { bubbles: true, detail: { scrolled: true } }));
    }
    else if (this.isScrolled && (scrollTop < scrollUpThreshold || scrollTop <= 0)) {
      // Scrolling up past threshold
      this.isScrolled = false;
      this.headerSection.classList.remove('header-scrolled');
      this.headerSection.classList.remove('header-nav-scrolled');
      document.dispatchEvent(new CustomEvent('header:scrolled', { bubbles: true, detail: { scrolled: false } }));
    }

    // Add 'header-nav-scrolled' when scrolled past 2x header height
    if (this.isScrolled && scrollTop > scrollThresholdNav) {
      this.headerSection.classList.add('header-nav-scrolled');

      // Close any open dropdown menus
      this.headerSection.querySelectorAll('details').forEach((details) => {
        if (details.hasAttribute('open')) {
          details.open = false;
        }
      });
    }

    // Hide-on-scroll is the "Smart" behaviour ONLY (data-sticky-type=on-scroll-up).
    // For "always", we never add .header-hidden, so the header stays pinned on
    // BOTH desktop and mobile — and everything that keys off .header-hidden
    // (header transform, the dock, comparison/scrollspy/filters/product-form and
    // the tabbed-products tabs) consistently does nothing for "always" too.
    if (this.getAttribute('data-sticky-type') === 'on-scroll-up') {
      // Hide header when scrolling down, show when scrolling up
      if (scrollTop > (this.headerBounds.bottom + this.firstScrollTop + 100)) {
        if (scrollTop > this.currentScrollTop) {
          // Scrolling down - hide header
          this.headerSection.classList.add('header-hidden');
        }
        else {
          // Scrolling up - show header
          this.headerSection.classList.remove('header-hidden');
        }
      }
      else {
        // Near top of page - always show header
        this.headerSection.classList.remove('header-hidden');
      }
    }
    else if (this.headerSection.classList.contains('header-hidden')) {
      // Safety: never leave a non-smart header hidden.
      this.headerSection.classList.remove('header-hidden');
    }

    // Update current scroll position for next comparison
    this.currentScrollTop = scrollTop;
  }
}
customElements.define('sticky-header', StickyHeader, { extends: 'header' });

class RevealLink extends HTMLAnchorElement {
  constructor() {
    super();

    this.revealButton = new theme.RevealButton(this);
    this.revealButton.load();
  }
}
customElements.define('reveal-link', RevealLink, { extends: 'a' });

class HoverLink extends HTMLAnchorElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();
  }
}
customElements.define('hover-link', HoverLink, { extends: 'a' });

class MagnetLink extends HoverLink {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-link', MagnetLink, { extends: 'a' });

class HoverButton extends HTMLButtonElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();

    if (this.type === 'submit' && this.form) {
      this.form.addEventListener('submit', () => this.setAttribute('aria-busy', 'true'));
    }
    window.addEventListener('pageshow', () => this.removeAttribute('aria-busy'));

    Motion.inView(this, this.init.bind(this));
  }

  static get observedAttributes() {
    return ['aria-busy'];
  }

  get contentElement() {
    return this._contentElement = this._contentElement || this.querySelector('.btn-text');
  }

  get animationElement() {
    return this._animationElement = this._animationElement || document.createRange().createContextualFragment(`
      <span class="btn-loader">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `).firstElementChild;
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  init() {
    this.append(this.animationElement);
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (newValue === 'true') {
      Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this.animationElement, { opacity: 1 }, { duration: 0.15 }]
      ]);
      Motion.animate(this.animationElement.children, { transform: ['scale(1.6)', 'scale(0.6)'] }, { duration: 0.35, delay: Motion.stagger(0.35 / 2), direction: 'alternate', repeat: Infinity });
    }
    else {
      Motion.timeline([
        [this.animationElement, { opacity: 0 }, { duration: 0.15 }],
        [this.contentElement, { opacity: 1 }, { duration: 0.15 }]
      ]);
    }
  }
}
customElements.define('hover-button', HoverButton, { extends: 'button' });

class MagnetButton extends HoverButton {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-button', MagnetButton, { extends: 'button' });

class HoverElement extends HTMLElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();
  }
}
customElements.define('hover-element', HoverElement);

class MagnetElement extends HoverElement {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-element', MagnetElement);

// Enhance Shopify payment buttons with the same hover effects as regular buttons
(function () {
  if (typeof theme === 'undefined' || theme.config.isTouch) return;

  const hoverMode = document.body.getAttribute('data-button-hover');
  if (!hoverMode || hoverMode === 'none') return;

  function enhancePaymentButton(btn) {
    if (btn.dataset.hoverEnhanced) return;
    btn.dataset.hoverEnhanced = 'true';

    // Add button--secondary class so all CSS hover rules apply
    btn.classList.add('button--secondary');

    // Inject .btn-fill element
    const fill = document.createElement('span');
    fill.className = 'btn-fill';
    fill.setAttribute('data-fill', '');

    // Wrap existing text content in .btn-text
    const textWrapper = document.createElement('span');
    textWrapper.className = 'btn-text';
    while (btn.firstChild) {
      textWrapper.appendChild(btn.firstChild);
    }

    btn.appendChild(fill);
    btn.appendChild(textWrapper);

    // Initialize theme.HoverButton for spotlight/standard positioning
    const hoverButton = new theme.HoverButton(btn);
    hoverButton.load();
  }

  function scanForPaymentButtons() {
    document.querySelectorAll('button.shopify-payment-button__button--unbranded:not([data-hover-enhanced])').forEach(enhancePaymentButton);
  }

  // Initial scan
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scanForPaymentButtons);
  } else {
    scanForPaymentButtons();
  }

  // Watch for dynamically rendered payment buttons (Shopify renders them async)
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('button.shopify-payment-button__button--unbranded')) {
          enhancePaymentButton(node);
        }
        // Also check children (payment button may be inside a wrapper)
        node.querySelectorAll?.('button.shopify-payment-button__button--unbranded:not([data-hover-enhanced])').forEach(enhancePaymentButton);
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();

class AnimateElement extends HTMLElement {
  constructor() {
    super();

    this.loadPromise = new Promise((resolve) => {
      this.resolveLoad = resolve;
    });
  }

  connectedCallback() {
    if (theme.config.motionReduced) {
      this.resolveLoad();
      return;
    }

    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    Motion.inView(this, async () => {
      if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media);
      setTimeout(this.resolveLoad, 500);
      await this.animation.load();
      this.parentElement.classList.add('animated');
    });
  }

  reset() {
    this.animation?.reset();
  }

  refresh() {
    this.animation?.reload();
  }
}
customElements.define('animate-element', AnimateElement);

class AnimatePicture extends HTMLPictureElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (theme.config.motionReduced) return;

    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    Motion.inView(this, async () => {
      if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media);
      await this.animation.load();
    });
  }
}
customElements.define('animate-picture', AnimatePicture, { extends: 'picture' });

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get speed() {
    return this.hasAttribute('autoplay') ? parseInt(this.getAttribute('autoplay-speed')) * 1000 : 5000;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (this.items.length > 1) {
      this.slider = new Flickity(this, {
        accessibility: false,
        fade: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        autoPlay: this.autoplay ? this.speed : false,
        on: {
          ready: function () {
            setTimeout(() => this.element.setAttribute('loaded', ''));
          }
        }
      });

      this.slider.on('change', this.onChange.bind(this));
      this.addEventListener('slider:previous', () => this.slider.previous());
      this.addEventListener('slider:next', () => this.slider.next());
      this.addEventListener('slider:play', () => this.slider.playPlayer());
      this.addEventListener('slider:pause', () => this.slider.pausePlayer());

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.slider.select(this.items.indexOf(event.target)));
      }
    }
  }

  disconnectedCallback() {
    if (this.slider) this.slider.destroy();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const focusableEvents = 'button, [href]';

      const fromElement = this.items[parseInt(oldValue)];
      const toElement = this.items[parseInt(newValue)];

      fromElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      toElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.removeAttribute('tabindex');
      });
    }
  }

  onChange() {
    this.selectedIndex = this.slider.selectedIndex;
    this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { currentPage: this.slider.selectedIndex } }));
  }
}
customElements.define('announcement-bar', AnnouncementBar);

class AccordionsDetails extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('toggle', this.onToggle);
  }

  get items() {
    return this._items = this._items || Array.from(this.querySelectorAll('details[is="accordion-details"]'));
  }

  onToggle(event) {
    const { current: target, open } = event.detail;
    this.items.forEach((item) => {
      if (item !== target) {
        item.close();
      }
    });

    if (open) {
      let headerHeight = 0;
      if (!theme.config.mqlSmall && document.querySelector('header.header')) {
        headerHeight = Math.round(document.querySelector('header.header').clientHeight);
      }
      setTimeout(() => {
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.scrollY - headerHeight,
          behavior: theme.config.motionReduced ? 'auto' : 'smooth'
        });
      }, 250);
    }
  }
}
customElements.define('accordions-details', AccordionsDetails);

class AccordionDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this._open = this.hasAttribute('open');
    this.summaryElement = this.querySelector('summary');
    this.contentElement = this.querySelector('summary + *');
    this.setAttribute('aria-expanded', this._open ? 'true' : 'false');

    this.summaryElement.addEventListener('click', this.onSummaryClick.bind(this));
    this.summaryElement.addEventListener('mousedown', this.onSummaryMouseDown);

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        if (this.designModeActive) this.open = true;
      });
      this.addEventListener('shopify:block:deselect', () => {
        if (this.designModeActive) this.open = false;
      });
    }
  }

  connectedCallback() {
    this.dataset.accordionDepth = `${this.depth}`;
    this.style.setProperty('--accordion-depth', this.depth);
  }

  get designModeActive() {
    return true;
  }

  get controlledElement() {
    return this.closest('accordions-details');
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      this.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false');
      this.summaryElement?.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false');
    }
  }

  get depth() {
    let depth = 0;
    let parentAccordion = this.parentElement?.closest('details[is="accordion-details"]');

    while (parentAccordion) {
      depth += 1;
      parentAccordion = parentAccordion.parentElement?.closest('details[is="accordion-details"]');
    }

    return depth;
  }

  get motionItems() {
    if (!this.contentElement) {
      return [];
    }

    const children = Array.from(this.contentElement.children);
    const scrollable = children.find((child) => child.classList?.contains('drawer__scrollable'));
    const items = scrollable ? Array.from(scrollable.children) : [];

    return [...items, ...children.filter((child) => child !== scrollable)];
  }

  get open() {
    return this._open;
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      }
      else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }

    this.setAttribute('aria-expanded', value ? 'true' : 'false');
    this.summaryElement?.setAttribute('aria-expanded', value ? 'true' : 'false');
    this.dispatchEventHandler();
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.open = !this.open;
  }

  // Opening and closing a row quickly is a double or triple click to the browser, which
  // selected the heading or the text under it (v2.11.30, Zing Gems). Clicks still toggle.
  onSummaryMouseDown(event) {
    if (event.detail > 1) event.preventDefault();
  }

  close() {
    this._open = false;
    this.transition(false);
  }

  clearMotionStyles() {
    [this.contentElement, ...this.motionItems].forEach((element) => {
      if (!(element instanceof HTMLElement)) {
        return;
      }

      element.style.removeProperty('opacity');
      element.style.removeProperty('transform');
      element.style.removeProperty('filter');
    });
  }

  async transition(value) {
    this.style.overflow = 'hidden';
    this.classList.add('is-transitioning');
    this.classList.toggle('is-opening', value);

    const motionItems = this.motionItems.filter((element) => element instanceof HTMLElement);
    const closedHeight = `${this.summaryElement.clientHeight}px`;
    const rise = `${Math.max(10, 18 - this.depth * 2)}px`;
    const blur = `${Math.max(4, 10 - this.depth * 2)}px`;

    try {
      if (value) {
        this.setAttribute('open', '');
        const openHeight = `${this.scrollHeight}px`;

        if (theme.config.motionReduced) {
          this.style.height = 'auto';
          return;
        }

        const timeline = [
          [this, { height: [closedHeight, openHeight] }, { duration: 0.42, easing: [0.16, 1, 0.3, 1] }],
          [this.contentElement, { opacity: [0.5, 1] }, { duration: 0.24, at: '-0.28', easing: [0.16, 1, 0.3, 1] }]
        ];

        if (motionItems.length) {
          timeline.push([
            motionItems,
            { opacity: [0, 1], transform: [`translateY(${rise})`, 'translateY(0)'], filter: [`blur(${blur})`, 'blur(0px)'] },
            { duration: 0.34, at: '-0.16', delay: Motion.stagger(this.depth > 0 ? 0.025 : 0.04), easing: [0.16, 1, 0.3, 1] }
          ]);
        }

        await Motion.timeline(timeline).finished;
      }
      else {
        if (theme.config.motionReduced) {
          this.removeAttribute('open');
          return;
        }

        const timeline = [];

        if (motionItems.length) {
          timeline.push([
            motionItems,
            { opacity: [1, 0], transform: ['translateY(0)', 'translateY(0.85rem)'], filter: ['blur(0px)', `blur(${Math.max(3, parseInt(blur) - 2)}px)`] },
            { duration: 0.18, easing: [0.4, 0, 1, 1] }
          ]);
        }

        timeline.push(
          [this.contentElement, { opacity: [1, 0.55] }, { duration: 0.14, at: motionItems.length ? '<' : 0 }],
          [this, { height: [`${this.clientHeight}px`, closedHeight] }, { duration: 0.32, at: '<', easing: [0.4, 0, 0.2, 1] }]
        );

        await Motion.timeline(timeline).finished;
        this.removeAttribute('open');
      }
    }
    finally {
      this.clearMotionStyles();
      this.classList.remove('is-transitioning', 'is-opening');
      this.style.height = 'auto';
      this.style.overflow = 'visible';
    }
  }

  dispatchEventHandler() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('toggle', { bubbles: true, detail: { current: this, open: this.open } }));
  }
}
customElements.define('accordion-details', AccordionDetails, { extends: 'details' });

class FooterDetails extends AccordionDetails {
  constructor() {
    super();

    this.load();
    document.addEventListener('matchSmall', this.load.bind(this));
    document.addEventListener('unmatchSmall', this.load.bind(this));
  }

  get designModeActive() {
    return theme.config.mqlSmall;
  }

  get openDefault() {
    return this.hasAttribute('data-opened');
  }

  load() {
    if (!theme.config.mqlSmall || this.openDefault) {
      this.open = true;
    }
    else {
      if (this.open && !this.openDefault) {
        this.open = false;
      }
    }
  }

  // Override transition on mobile: smooth CSS height animation.
  //
  // WHY CSS TRANSITION INSTEAD OF Motion.animate():
  //   CSS transitions run on the compositor thread — immune to JS jank.
  //
  // THE CORRECT SEQUENCE (critical ordering):
  //   1. Pin height = closedHeight BEFORE setAttribute('open')
  //      → content is clipped from the very first frame, no flash
  //   2. Single rAF to let browser reflow the opened content
  //   3. Set transition property
  //   4. void this.offsetHeight  ← CRITICAL: forces browser to register the
  //      transition BEFORE we change height. Without this, the browser batches
  //      the transition+height changes into one update and skips the animation.
  //   5. Set height = openHeight → animation fires
  async transition(value) {
    if (!theme.config.mqlSmall) {
      return super.transition(value);
    }

    this.classList.add('is-transitioning');
    this.classList.toggle('is-opening', value);

    const closedHeight = `${this.summaryElement.clientHeight}px`;

    try {
      if (value) {
        // ── OPENING ──────────────────────────────────────────────────────
        // Pin height and clip overflow BEFORE opening so content is clipped
        // from frame zero — prevents any flash of full-height content.
        this.style.transition = 'none';
        this.style.height = closedHeight;
        this.style.overflow = 'hidden';

        // Open the <details> — content enters DOM, clipped to closedHeight
        this.setAttribute('open', '');

        if (theme.config.motionReduced) {
          this.style.height = 'auto';
          this.style.overflow = '';
          return;
        }

        // Single rAF: wait one paint cycle for the browser to reflow content.
        // (We already pinned the height so content is safe — no double rAF needed)
        const openHeight = await new Promise((resolve) => {
          requestAnimationFrame(() => {
            void this.offsetHeight; // flush any pending style recalcs
            resolve(`${this.scrollHeight}px`);
          });
        });

        // Arm the transition, then force a reflow so the browser registers it.
        // Without this flush, the browser batches transition+height into one
        // style update and the animation never fires (height just snaps).
        this.style.transition = 'height 0.42s cubic-bezier(0.16, 1, 0.3, 1)';
        void this.offsetHeight; // ← the missing piece that makes it actually animate
        this.style.height = openHeight; // ← NOW this triggers the transition

        // Wait for the CSS transition to complete before cleaning up
        await new Promise((resolve) => {
          const onEnd = (e) => {
            if (e.target === this && e.propertyName === 'height') {
              this.removeEventListener('transitionend', onEnd);
              resolve();
            }
          };
          this.addEventListener('transitionend', onEnd);
          setTimeout(resolve, 500); // safety fallback
        });

      } else {
        // ── CLOSING ───────────────────────────────────────────────────────
        if (theme.config.motionReduced) {
          this.removeAttribute('open');
          this.style.height = 'auto';
          this.style.overflow = '';
          return;
        }

        // Capture current rendered height first (no transition yet)
        this.style.transition = 'none';
        this.style.overflow = 'hidden';
        this.style.height = `${this.offsetHeight}px`;

        // Force flush so the captured height is committed before transition starts
        void this.offsetHeight;

        this.style.transition = 'height 0.28s cubic-bezier(0.4, 0, 0.2, 1)';
        this.style.height = closedHeight; // animate down

        await new Promise((resolve) => {
          const onEnd = (e) => {
            if (e.target === this && e.propertyName === 'height') {
              this.removeEventListener('transitionend', onEnd);
              resolve();
            }
          };
          this.addEventListener('transitionend', onEnd);
          setTimeout(resolve, 400); // safety fallback
        });

        this.removeAttribute('open');
      }
    }
    finally {
      this.clearMotionStyles();
      this.classList.remove('is-transitioning', 'is-opening');
      this.style.transition = '';
      this.style.height = 'auto';
      this.style.overflow = '';
    }
  }
}
customElements.define('footer-details', FooterDetails, { extends: 'details' });

class GestureElement extends HTMLElement {
  constructor() {
    super();
    this.config = {
      thresholdY: Math.max(25, Math.floor(0.15 * window.innerHeight)),
      velocityThreshold: 10,
      disregardVelocityThresholdY: Math.floor(0.5 * this.clientHeight),
      pressThreshold: 8,
      diagonalSwipes: false,
      diagonalLimit: Math.tan(((45 * 1.5) / 180) * Math.PI),
      longPressTime: 500,
    };

    this.handlers = {
      panstart: [],
      panmove: [],
      panend: [],
      swipeup: [],
      swipedown: [],
      longpress: [],
    };

    Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.addEventListener('touchstart', this.onTouchStart.bind(this), theme.supportsPassive ? { passive: true } : false);
    this.addEventListener('touchmove', this.onTouchMove.bind(this), theme.supportsPassive ? { passive: true } : false);
    this.addEventListener('touchend', this.onTouchEnd.bind(this), theme.supportsPassive ? { passive: true } : false);
  }

  on(type, fn) {
    if (this.handlers[type]) {
      this.handlers[type].push(fn);
      return {
        type,
        fn,
        cancel: () => this.off(type, fn),
      };
    }
  }

  off(type, fn) {
    if (this.handlers[type]) {
      const idx = this.handlers[type].indexOf(fn);
      if (idx !== -1) {
        this.handlers[type].splice(idx, 1);
      }
    }
  }

  fire(type, event) {
    for (let i = 0; i < this.handlers[type].length; i++) {
      this.handlers[type][i](event);
    }
  }

  onTouchStart(event) {
    this.thresholdY = this.config.thresholdY;
    this.disregardVelocityThresholdY = this.config.disregardVelocityThresholdY;
    this.touchStartY = event.changedTouches[0].screenY;
    this.touchMoveY = null;
    this.touchEndY = null;
    this.swipingDirection = null;

    // Long press.
    this.longPressTimer = setTimeout(() => this.fire('longpress', event), this.config.longPressTime);
    this.fire('panstart', event);
  }

  onTouchMove(event) {
    const touchMoveY = event.changedTouches[0].screenY - (this.touchStartY ?? 0);
    this.velocityY = touchMoveY - (this.touchMoveY ?? 0);
    this.touchMoveY = touchMoveY;
    const absTouchMoveY = Math.abs(this.touchMoveY);
    this.swipingVertical = absTouchMoveY > this.thresholdY;
    this.swipingDirection = this.swipingVertical ? 'vertical' : 'pre-vertical';

    if (absTouchMoveY > this.config.pressThreshold) {
      clearTimeout(this.longPressTimer ?? undefined);
    }
    this.fire('panmove', event);
  }

  onTouchEnd(event) {
    this.touchEndY = event.changedTouches[0].screenY;
    this.fire('panend', event);
    clearTimeout(this.longPressTimer ?? undefined);

    const y = this.touchEndY - (this.touchStartY ?? 0);
    const absY = Math.abs(y);

    if (absY > this.thresholdY) {
      this.swipedVertical = this.config.diagonalSwipes ? y <= this.config.diagonalLimit : absY > this.thresholdY;
      if (this.swipedVertical) {
        if (y < 0) {
          // Upward swipe.
          if ((this.velocityY ?? 0) < -this.config.velocityThreshold || y < -this.disregardVelocityThresholdY) {
            this.fire('swipeup', event);
          }
        } else {
          // Downward swipe.
          if ((this.velocityY ?? 0) > this.config.velocityThreshold || y > this.disregardVelocityThresholdY) {
            this.fire('swipedown', event);
          }
        }
      }
    }
  }
}
customElements.define('gesture-element', GestureElement);

class OverlayElement extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('mousemove', this.onMouseMove);
    this.addEventListener('mouseleave', this.onMouseLeave);
    this.addEventListener('mousedown', this.onMouseDown);
    this.addEventListener('mouseup', this.onMouseUp);
  }

  get cursor() {
    return document.querySelector('mouse-cursor');
  }

  onMouseMove() {
    this.cursor.classList.add('active');
    this.cursor.setAttribute('aria-hidden', 'false');
  }
  onMouseLeave() {
    this.cursor.classList.remove('active');
    this.cursor.setAttribute('aria-hidden', 'true');
  }
  onMouseDown() {
    this.cursor.classList.add('pressed');
  }
  onMouseUp() {
    this.cursor.classList.remove('pressed');
  }
}
customElements.define('overlay-element', OverlayElement);

const lockLayerCount = new WeakMap();
class ModalElement extends HTMLElement {
  constructor() {
    super();

    this.events = {
      afterHide: 'modal:afterHide',
      afterShow: 'modal:afterShow',
      closeAll: 'modal:closeAll'
    };

    this.classes = {
      open: 'has-modal-open',
      opening: 'has-modal-opening'
    };
  }

  static get observedAttributes() {
    return ['id', 'open'];
  }

  get shouldLock() {
    return false;
  }

  get shouldAppendToBody() {
    return false;
  }

  get shouldCloseAll() {
    return false;
  }

  get open() {
    return this.hasAttribute('open');
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get overlay() {
    return this._overlay = this._overlay || this.querySelector('.fixed-modal');
  }

  get gesture() {
    return this._gesture = this._gesture || this.querySelector('gesture-element');
  }

  get designMode() {
    return this.hasAttribute('shopify-design-mode');
  }

  get focusElement() {
    return this.querySelector('gesture-element') || this.querySelector('button');
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.controls.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this), { signal: this.abortController.signal }));
    document.addEventListener('keyup', (event) => event.code === 'Escape' && this.hide(), { signal: this.abortController.signal });
    document.addEventListener(this.events.closeAll, () => this.hide(), { signal: this.abortController.signal });

    if (this.gesture) {
      this.gestureConfig = {
        animationFrame: null,
        moveY: 0,
        maxGestureDistance: 0,
        endPoint: 0,
        layerHeight: null
      };

      this.gestureWrap = this.gesture.parentElement;

      setTimeout(() => {
        this.gesture.on('panstart', this.onPanStart.bind(this));
        this.gesture.on('panmove', this.onPanMove.bind(this));
        this.gesture.on('panend', this.onPanEnd.bind(this));
      }, 75);

      // Tap the handle strip (the empty top of the header, where the chevron is)
      // to close — for users who tap instead of swipe. A real swipe moves too
      // far to fire a click, so this never conflicts with the drag-to-dismiss.
      this.gesture.addEventListener('click', (event) => {
        if (event.target !== this.gesture) return;
        const rect = this.gesture.getBoundingClientRect();
        if (event.clientY - rect.top <= 44) this.hide();
      }, { signal: this.abortController.signal });
    }

    if (Shopify.designMode && this.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:select', (event) => this.show(null, !event.detail.load), { signal: this.abortController.signal });
      section.addEventListener('shopify:section:deselect', this.hide.bind(this), { signal: this.abortController.signal });
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();

    if (Shopify.designMode) {
      document.body.classList.remove(this.classes.open);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'));

        if (oldValue === null && (newValue === "" || newValue === 'immediate')) {
          this.hidden = false;
          this.removeAttribute('inert');

          this.originalParentBeforeAppend = null;
          if (this.shouldAppendToBody && this.parentElement !== document.body) {
            this.originalParentBeforeAppend = this.parentElement;
            document.body.append(this);
          }
          const showTransitionPromise = this.showTransition(newValue !== 'immediate') || Promise.resolve();
          showTransitionPromise.then(() => {
            this.afterShow();
            this.dispatchEvent(new CustomEvent(this.events.afterShow, { bubbles: true }));
          });
        }
        else if (oldValue !== null && newValue === null) {
          this.setAttribute('inert', '');
          // Closing takes the overlay's close cursor with it (v2.10.73), however it closed.
          document.querySelector('mouse-cursor')?.reset?.();

          const hideTransitionPromise = this.hideTransition() || Promise.resolve();
          hideTransitionPromise.then(() => {
            this.afterHide();

            if (!this.hasAttribute('inert')) return;

            if (this.parentElement === document.body && this.originalParentBeforeAppend) {
              this.originalParentBeforeAppend.appendChild(this);
              this.originalParentBeforeAppend = null;
            }
            this.dispatchEvent(new CustomEvent(this.events.afterHide, { bubbles: true }));

            this.hidden = true;
          });
        }
        this.dispatchEvent(new CustomEvent('toggle', { bubbles: true }));

        break;
    }
  }

  onButtonClick(event) {
    event.preventDefault();

    this.open ? this.hide() : this.show(event.currentTarget);
  }

  hide() {
    if (!this.open) return;

    this.beforeHide();
    this.resetGesture();
    this.removeAttribute('open');

    return theme.utils.waitForEvent(this, this.events.afterHide);
  }
  show(activeElement = null, animate = true) {
    if (this.open) return;

    if (this.shouldCloseAll) {
      document.dispatchEvent(new CustomEvent(this.events.closeAll), { bubbles: true });
    }

    this.beforeShow();
    this.activeElement = activeElement;
    this.setAttribute('open', animate ? '' : 'immediate');

    if (this.shouldLock) {
      document.body.classList.add(this.classes.opening);
      document.documentElement.classList.add('modal-locked');
    }

    return theme.utils.waitForEvent(this, this.events.afterShow);
  }

  beforeHide() { }
  beforeShow() { }

  afterHide() {
    // Same reason as DetailsDropdown: the mobile drawer's localization sheets
    // are modals, so a list left filtered would reopen showing only the last
    // search's matches. No-op for every other modal.
    this.querySelector('form[is="localization-listbox"]')?.resetSearch?.();

    setTimeout(() => {
      theme.a11y.removeTrapFocus(this.activeElement);
      // _holdsLock pairs lock/unlock exactly per instance — afterShow and
      // afterHide resolve from independent transition promises, so a modal
      // closed mid-open can see them land in either order.
      if (this.shouldLock && this._holdsLock) {
        this._holdsLock = false;
        lockLayerCount.set(ModalElement, lockLayerCount.get(ModalElement) - 1);

        const stillLocked = lockLayerCount.get(ModalElement) > 0;
        document.body.classList.toggle(this.classes.open, stillLocked);
        document.documentElement.classList.toggle('modal-locked', stillLocked);
      }
    });
  }
  afterShow() {
    if (!this.open) {
      // Closed again before the open transition finished — never lock.
      document.body.classList.remove(this.classes.opening);
      document.documentElement.classList.toggle('modal-locked', lockLayerCount.get(ModalElement) > 0);
      return;
    }
    theme.a11y.trapFocus(this, this.focusElement);
    if (this.shouldLock && !this._holdsLock) {
      this._holdsLock = true;
      lockLayerCount.set(ModalElement, lockLayerCount.get(ModalElement) + 1);

      // No body pin: position:fixed on the body detaches position:sticky
      // in WebKit, so the header vanished while a drawer was open. The
      // scroll lock is html.modal-locked { overflow: hidden } on the ROOT
      // (respected by modern iOS — the infamous ignore applies to
      // body-level overflow) plus touch-action/overscroll none via
      // .touch .has-modal-open. Scroll position never moves, so nothing
      // needs restoring and the header never detaches.

      document.body.classList.remove(this.classes.opening);
      document.body.classList.add(this.classes.open);
    }
  }

  showTransition() {
    setTimeout(() => {
      this.setAttribute('active', '');
    }, 75);
    return new Promise((resolve) => {
      const computedStyle = window.getComputedStyle(this.overlay);
      const hasTransition = computedStyle.transitionProperty !== 'none' && parseFloat(computedStyle.transitionDuration) > 0;

      if (!hasTransition) {
        resolve();
        return;
      }

      this.overlay.addEventListener('transitionend', resolve, { once: true });
    });
  }
  hideTransition() {
    this.removeAttribute('active');
    return new Promise((resolve) => {
      this.overlay.addEventListener('transitionend', resolve, { once: true });
    });
  }

  resetGesture() {
    if (this.gesture) {
      this.gestureWrap.style.transform = '';
      this.gestureWrap.style.transition = '';
      this.overlay.style.opacity = '';
      this.overlay.style.transition = '';
      this.gestureWrap.style.removeProperty('--drawer-arrow');
    }
  }

  onPanStart() {
    this.removeTransition(this.gestureWrap, 'transform');

    if (this.hasAttribute('open')) {
      if (this.gestureConfig.layerHeight === null) {
        this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
      }
      this.gestureConfig.maxGestureDistance = this.gestureConfig.layerHeight - 50;
      this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3;
    }
  }

  onPanMove() {
    if (this.gestureConfig.animationFrame) return;

    this.gestureConfig.animationFrame = requestAnimationFrame(() => {
      const clamp = (a, min = 0, max = 1) => Math.min(max, Math.max(min, a));
      const invlerp = (x, y, a) => (y === x) ? 0 : clamp((a - x) / (y - x));

      this.gestureWrap.style.transition = 'transform .1s linear';
      this.overlay.style.transition = 'opacity .1s linear';

      if (this.hasAttribute('open')) {
        if (this.gestureConfig.layerHeight === null) {
          this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
        }

        this.gestureConfig.moveY = this.gesture.touchMoveY;
        this.gestureConfig.maxGestureDistance = this.gestureConfig.layerHeight - 50;

        if (this.gestureConfig.moveY >= 0) {
          this.gestureWrap.style.transform = `translateY(${Math.min(
            this.gestureConfig.moveY,
            this.gestureConfig.maxGestureDistance
          )}px)`;

          this.overlay.style.opacity = invlerp(this.gestureConfig.layerHeight, 0, this.gestureConfig.moveY);

          // Rotate the handle chevron live: 1 (∨ down) → -1 (∧ up) as it's swiped down
          const progress = clamp(this.gestureConfig.moveY / this.gestureConfig.layerHeight);
          this.gestureWrap.style.setProperty('--drawer-arrow', `${1 - 2 * progress}`);
        }
        else {
          this.gestureWrap.style.transform = 'translateY(0)';
          this.gestureWrap.style.transition = '';
          this.overlay.style.opacity = '1';
          this.gestureWrap.style.setProperty('--drawer-arrow', '1');
        }
      }

      this.gestureConfig.animationFrame = null;
    });
  }

  onPanEnd() {
    this.gestureConfig.animationFrame === null || cancelAnimationFrame(this.gestureConfig.animationFrame);
    this.gestureConfig.animationFrame = null;
    this.gestureWrap.style.transition = 'transform .35s cubic-bezier(.32, .72, 0, 1)';

    if (this.gestureConfig.layerHeight === null) {
      this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
    }

    this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3;

    if (this.hasAttribute('open')) {
      this.gestureConfig.moveY = this.gesture.touchMoveY;

      if (this.gestureConfig.moveY < this.gestureConfig.endPoint) {
        this.gestureWrap.style.transform = 'translateY(0)';
        this.gestureWrap.style.setProperty('--drawer-arrow', '1');
      }
      else {
        this.gestureWrap.style.transform = `translateY(${this.gestureConfig.layerHeight}px)`;
        this.gestureWrap.style.setProperty('--drawer-arrow', '-1');

        this.gestureWrap.addEventListener(
          this.whichTransitionEvent(),
          () => {
            this.hide();
          },
          { once: true }
        );
      }
    }
  }

  removeTransition(node, transition) {
    const match = node.style.transition.match(new RegExp('(?:^|,)\\s*' + transition + '(?:$|\\s|,)[^,]*', 'i'));
    if (match) {
      const transitionArray = node.style.transition.split('');
      transitionArray.splice(match.index, match[0].length);
      node.style.transition = transitionArray.join('');
    }
  }

  whichTransitionEvent() {
    let t;
    const el = document.createElement('fakeelement');
    const transitions = {
      WebkitTransition: 'webkitTransitionEnd',
      MozTransition: 'transitionend',
      MSTransition: 'msTransitionEnd',
      OTransition: 'oTransitionEnd',
      transition: 'transitionEnd',
    };

    for (t in transitions) {
      if (el.style[t] !== undefined) {
        return transitions[t];
      }
    }
  }
}
customElements.define('modal-element', ModalElement);
lockLayerCount.set(ModalElement, 0);

// The footer's mobile country/language sheets. Every other sheet in the theme
// lives inside a drawer, which already locked the page and already sits above
// it; these open straight from a page section, so they have to do both
// themselves. Appending to <body> matters twice over: FooterGroup gives the
// copyright section the LOWEST z-index in the footer stack, so a sheet left
// inside it would be painted under the footer menu above.
class LocalizationSheet extends ModalElement {
  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }
}
customElements.define('localization-sheet', LocalizationSheet);

class DrawerElement extends ModalElement {
  constructor() {
    super();

    this.events = {
      afterHide: 'drawer:afterHide',
      afterShow: 'drawer:afterShow'
    };
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }
}
customElements.define('drawer-element', DrawerElement);

// Save-for-later: localStorage-backed wishlist. State store + the card
// bookmark button + the SavedDrawer list renderer + header count badge.
theme.savedItems = {
  key: 'shaazin:saved-items',

  list() {
    try {
      const items = JSON.parse(localStorage.getItem(this.key));
      return Array.isArray(items) ? items : [];
    } catch (e) {
      return [];
    }
  },

  has(handle) {
    return this.list().some((item) => item.handle === handle);
  },

  toggle(product) {
    let items = this.list();
    const saved = items.some((item) => item.handle === product.handle);

    if (saved) {
      items = items.filter((item) => item.handle !== product.handle);
    } else {
      items.unshift(product);
    }

    try {
      localStorage.setItem(this.key, JSON.stringify(items.slice(0, 100)));
    } catch (e) { }

    document.dispatchEvent(new CustomEvent('saved-items:change', { detail: { items } }));
    return !saved;
  },

  remove(handle) {
    const items = this.list().filter((item) => item.handle !== handle);
    try {
      localStorage.setItem(this.key, JSON.stringify(items));
    } catch (e) { }
    document.dispatchEvent(new CustomEvent('saved-items:change', { detail: { items } }));
  }
};

class SaveButton extends HTMLButtonElement {
  constructor() {
    super();

    this.sync();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      let product;
      try {
        product = JSON.parse(this.getAttribute('data-product'));
      } catch (e) {
        return;
      }

      const nowSaved = theme.savedItems.toggle(product);
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.classList.remove('save-button--press', 'save-button--burst');
        void this.offsetWidth;
        this.classList.add('save-button--press');
        // The heart's pop + dot burst (v2.10.37) plays only when it becomes saved.
        if (nowSaved) this.classList.add('save-button--burst');
      }
    });

    document.addEventListener('saved-items:change', this.sync.bind(this));
  }

  sync() {
    const saved = theme.savedItems.has(this.getAttribute('data-product-handle'));
    this.classList.toggle('saved', saved);
    this.setAttribute('aria-pressed', saved ? 'true' : 'false');
  }
}
customElements.define('save-button', SaveButton, { extends: 'button' });

// "Save for later" product block (v2.10.36, snippets/product-block-save.liquid): the
// product page's full-width save button. The first tap saves (the same theme.savedItems
// entry a card bookmark makes) and, with "Open the saved list after saving" on, opens the
// Saved drawer; a tap while saved opens the list. Removing is done in the list, so a
// shopper never unsaves by tapping the page's main button twice.
class SaveForLater extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.sync = this.sync.bind(this);
    this.onClick = this.onClick.bind(this);
    this.button.addEventListener('click', this.onClick);
    document.addEventListener('saved-items:change', this.sync);
    this.sync();
  }

  disconnectedCallback() {
    if (!this.button) return;
    this.button.removeEventListener('click', this.onClick);
    document.removeEventListener('saved-items:change', this.sync);
  }

  get product() {
    try {
      return JSON.parse(this.getAttribute('data-product'));
    } catch (e) {
      return null;
    }
  }

  onClick(event) {
    event.preventDefault();
    const product = this.product;
    if (!product) return;

    if (!theme.savedItems.has(product.handle)) {
      theme.savedItems.toggle(product);
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.classList.remove('product-save--press');
        void this.offsetWidth;
        this.classList.add('product-save--press');
      }
      if (this.getAttribute('data-open-list') !== 'true') return;
    }
    this.openList();
  }

  openList() {
    const drawer = document.getElementById('SavedDrawer');
    if (drawer && typeof drawer.show === 'function') drawer.show(this.button);
  }

  sync() {
    const saved = theme.savedItems.has(this.getAttribute('data-product-handle'));
    this.classList.toggle('is-saved', saved);
    this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
  }
}
customElements.define('save-for-later', SaveForLater);

class SavedItems extends HTMLElement {
  constructor() {
    super();

    this.refresh = this.refresh.bind(this);
    document.addEventListener('saved-items:change', this.refresh);
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-saved-remove]');
      if (remove) {
        event.preventDefault();

        // Same exit as a cart line item: pin the height, collapse + poof,
        // and only then update storage — the storage change triggers a
        // re-fetch that would otherwise cut the animation short.
        const row = remove.closest('[data-saved-row]');
        const handle = remove.getAttribute('data-product-handle');
        if (row && !row.classList.contains('is-removing')) {
          row.style.height = `${row.offsetHeight}px`;
          row.classList.add('is-removing');
          void row.offsetHeight;
          row.style.height = '0px';
          setTimeout(() => theme.savedItems.remove(handle), 320);
        }
        else if (!row) {
          theme.savedItems.remove(handle);
        }
      }
    });
    this.refresh();
  }

  refresh() {
    const items = theme.savedItems.list();
    const scope = this.closest('.drawer__panel') || this.closest('.saved-drawer');
    const emptyEl = scope ? scope.querySelector('[data-saved-empty]') : null;

    document.querySelectorAll('[data-saved-count]').forEach((count) => {
      count.textContent = items.length;
      count.hidden = items.length === 0;
    });

    const query = items
      .filter((item) => item.id)
      .slice(0, 24)
      .map((item) => `id:${item.id}`)
      .join(' OR ');

    // Nothing saved: hide the list itself. It shares .cart__recent with the
    // recently-viewed panel, whose :empty rule draws a loading bar — on an
    // empty list that bar would spin forever under the empty state.
    if (!query) {
      this.innerHTML = '';
      this.hidden = true;
      if (emptyEl) emptyEl.hidden = false;
      this.syncWhatsapp();
      return;
    }
    this.hidden = false;
    if (emptyEl) emptyEl.hidden = true;

    fetch(this.getAttribute('data-url') + encodeURIComponent(query))
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        const results = sectionInnerHTML && sectionInnerHTML.querySelector('saved-items');
        if (results && results.innerHTML.trim().length) {
          this.innerHTML = results.innerHTML;
        }
        else {
          this.innerHTML = '';
          this.hidden = true;
          if (emptyEl) emptyEl.hidden = false;
        }
        this.syncWhatsapp();
      })
      .catch((e) => console.error(e));
  }

  // "Enquire these products" (v2.10.35, snippets/saved-whatsapp.liquid): one wa.me link
  // carrying the opening message and a numbered list of the rows this list rendered —
  // bold name, price (unless the theme left it out), product link. Built from the
  // rendered rows, not localStorage, so a product that no longer exists is never sent.
  // Shown only while the list has rows.
  syncWhatsapp() {
    const scope = this.closest('.drawer__panel') || this.closest('.saved-drawer');
    const wrap = scope ? scope.querySelector('[data-saved-whatsapp]') : null;
    const link = wrap ? wrap.querySelector('a[data-number]') : null;
    if (!link) return;

    const rows = this.hidden ? [] : [...this.querySelectorAll('[data-saved-row][data-wa-title]')]
      .filter((row) => !row.classList.contains('is-removing'));
    wrap.hidden = rows.length === 0;
    if (!rows.length) return;

    const withPrices = link.dataset.prices === 'true';
    const lines = [];
    const intro = (link.dataset.message || '').trim();
    if (intro) lines.push(intro, '');
    rows.forEach((row, index) => {
      const title = (row.dataset.waTitle || '').replace(/\*/g, '').trim();
      lines.push(`${index + 1}. *${title}*`);
      if (withPrices && row.dataset.waPrice) lines.push(`   ${row.dataset.waPrice}`);
      if (row.dataset.waUrl) lines.push(`   ${row.dataset.waUrl}`);
      lines.push('');
    });

    link.href = `https://wa.me/${link.dataset.number}?text=${encodeURIComponent(lines.join('\n').trim())}`;
  }
}
customElements.define('saved-items', SavedItems);

// Header count badges live outside the drawer — keep them in sync even on
// pages where the drawer markup is absent.
document.addEventListener('saved-items:change', () => {
  const items = theme.savedItems.list();
  document.querySelectorAll('[data-saved-count]').forEach((count) => {
    count.textContent = items.length;
    count.hidden = items.length === 0;
  });
});
/* Theme editor: global settings that the layout turns into <body> data
   attributes (arrow style, button radius, hover style, badge style) do not
   update in the preview until Save, because the editor morphs the page but
   leaves body attributes alone. The css-variables snippet mirrors them as
   --ds-* custom properties, which DO re-render live; watch <head> for that
   re-render and copy the values onto body. Design mode only. */
if (window.Shopify && Shopify.designMode) {
  const dsMap = { 'slider-arrows': 'sliderArrows', 'rounded-button': 'roundedButton', 'rounded-input': 'roundedInput', 'rounded-block': 'roundedBlock', 'rounded-card': 'roundedCard', 'button-hover': 'buttonHover', 'badge-style': 'badgeStyle', 'dropdown-style': 'dropdownStyle' };
  const syncBodyAttrs = () => {
    const cs = getComputedStyle(document.documentElement);
    Object.entries(dsMap).forEach(([prop, key]) => {
      const v = cs.getPropertyValue(`--ds-${prop}`).trim();
      if (v && document.body.dataset[key] !== v) document.body.dataset[key] = v;
    });
  };
  const startSync = () => {
    syncBodyAttrs();
    new MutationObserver(() => requestAnimationFrame(syncBodyAttrs)).observe(document.head, { childList: true, subtree: true, characterData: true });
  };
  if (document.body) startSync(); else document.addEventListener('DOMContentLoaded', startSync, { once: true });
}

document.addEventListener('DOMContentLoaded', () => {
  const items = theme.savedItems.list();
  document.querySelectorAll('[data-saved-count]').forEach((count) => {
    count.textContent = items.length;
    count.hidden = items.length === 0;
  });
});

class MenuDrawer extends DrawerElement {
  constructor() {
    super();

    // Sub-menus arrive with the header section (api-button), which waited until the
    // drawer had slid open: a sub-menu opened straight away was empty for a moment, or
    // seconds on a slow line (v2.11.34, Zing Gems). Fetch when the pointer reaches an
    // opener, a finger presses one or it takes focus, and at the latest on open.
    this.prefetchListener = (event) => {
      if (event.target.closest?.(`[aria-controls="${this.id}"]`)) this.prefetch();
    };
    ['pointerover', 'pointerdown', 'focusin'].forEach((type) => document.addEventListener(type, this.prefetchListener, { passive: true }));
  }

  prefetch() {
    if (this.prefetched) return;
    this.prefetched = true;
    ['pointerover', 'pointerdown', 'focusin'].forEach((type) => document.removeEventListener(type, this.prefetchListener));
    this.querySelectorAll('api-button').forEach((button) => button.init?.());
  }

  // Only the list this screen shows. With Accordion menu on, the menu is written twice
  // (v2.11.33) and the hidden copy's items took their turn in the stagger first: on
  // Zing Gems the desktop items finished appearing 3s after the click (v2.11.34).
  get menuItems() {
    return Array.from(this.querySelectorAll('.drawer__menu:not(.active)'))
      .filter((list) => getComputedStyle(list).display !== 'none')
      .flatMap((list) => Array.from(list.children));
  }

  beforeShow() {
    super.beforeShow();
    this.prefetch();

    if (theme.config.motionReduced) return;

    // Hide items synchronously before the staggered reveal — the
    // animation starts 300ms in (and each item later still, via the
    // stagger), so without this the items render, vanish and re-enter:
    // the flicker. Inline styles are removed when the reveal finishes.
    const items = this.menuItems;
    items.forEach((item) => {
      item.style.opacity = '0';
      item.style.transform = 'translateX(-20px)';
    });

    // 0.1s apart, closer for a long menu: the whole list is in within ~1.5s.
    const gap = Math.min(0.1, 0.6 / Math.max(items.length, 1));
    setTimeout(() => {
      Motion.animate(items, { transform: ['translateX(-20px)', 'translateX(0)'], opacity: [0, 1] }, { duration: 0.6, easing: [.075, .82, .165, 1], delay: Motion.stagger(gap) }).finished.then(() => {
        items.forEach((item) => item.removeAttribute('style'));
      });
    }, 300);
  }

  beforeHide() {
    super.beforeHide();

    setTimeout(() => {
      this.querySelectorAll('details[is=menu-details], details[is=accordion-details]').forEach((subMenu) => {
        if (typeof subMenu.onCloseButtonClick === 'function') {
          subMenu.onCloseButtonClick();
        }
        else if (subMenu.open) {
          subMenu.open = false;
        }
      });
    }, 300);
  }
}
customElements.define('menu-drawer', MenuDrawer);

class ShareDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get menuItems() {
    return this._menuItems = this._menuItems || this.querySelectorAll('.share-buttons>li');
  }

  get urlToShare() {
    const urlInput = this.querySelector('input[type=hidden]');
    return urlInput ? urlInput.value : document.location.href;
  }

  connectedCallback() {
    super.connectedCallback();
    if (navigator.share) {
      this.controls.forEach((button) => button.addEventListener('click', this.shareTo.bind(this), { signal: this.abortController.signal }));
    }
  }

  async shareTo(event) {
    event.preventDefault();
    try {
      await navigator.share({ url: this.urlToShare, title: document.title });
    }
    catch (error) {
      if (error.name === 'AbortError') {
      }
      else {
        console.error(error);
      }
    }
  }

  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
      Motion.animate(this.menuItems, { transform: ['translateY(2.5rem)', 'translateY(0)'], opacity: [0, 1] }, { duration: 0.6, easing: [.075, .82, .165, 1], delay: Motion.stagger(0.1) }).finished.then(() => {
        this.menuItems.forEach((item) => item.removeAttribute('style'));
      });
    }, 300);
  }
}
customElements.define('share-drawer', ShareDrawer);

class BackInStockDrawer extends DrawerElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      this.init();
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get submited() {
    return this.querySelector('.alert') !== null;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    // Open modal if errors or success message exist
    if (this.submited) {
      setTimeout(() => {
        this.show();
      }, 1000);
    }
  }
}
customElements.define('back-in-stock-drawer', BackInStockDrawer);

class MenuDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('[data-close]');
  }

  get focusElement() {
    return this.summary.nextElementSibling.querySelector('button');
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.setAttribute('open', '');
    this.activeElement = event.currentTarget;

    setTimeout(() => {
      this.parent.classList.add('active');
      this.classList.add('active');
      this.summary.setAttribute('aria-expanded', true);
      this.summary.setAttribute('tabindex', '-1');

      setTimeout(() => {
        theme.a11y.trapFocus(this, this.focusElement);
      }, 100);
    }, 100);
  }

  onCloseButtonClick() {
    this.parent.classList.remove('active');
    this.classList.remove('active');
    this.summary.setAttribute('aria-expanded', false);
    this.summary.removeAttribute('tabindex');

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        requestAnimationFrame(handleAnimation);
      }
      else {
        this.removeAttribute('open');
        theme.a11y.removeTrapFocus(this.activeElement);
      }
    }

    requestAnimationFrame(handleAnimation);
  }
}
customElements.define('menu-details', MenuDetails, { extends: 'details' });

class QuantityLabel extends HTMLLabelElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelector('.quantity__rules-cart').append(this.animationElement);
  }

  static get observedAttributes() {
    return ['aria-busy'];
  }

  get contentElement() {
    return this._contentElement = this._contentElement || this.querySelector('.quantity-cart');
  }

  get animationElement() {
    return this._animationElement = this._animationElement || document.createRange().createContextualFragment(`
      <span class="btn-loader">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `).firstElementChild;
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (newValue === 'true') {
      Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this.animationElement, { opacity: 1 }, { duration: 0.15 }]
      ]);
      Motion.animate(this.animationElement.children, { transform: ['scale(1.6)', 'scale(0.6)'] }, { duration: 0.35, delay: Motion.stagger(0.35 / 2), direction: 'alternate', repeat: Infinity });
    }
    else {
      Motion.timeline([
        [this.animationElement, { opacity: 0 }, { duration: 0.15 }],
        [this.contentElement, { opacity: 1 }, { duration: 0.15 }]
      ]);
    }
  }
}
customElements.define('quantity-label', QuantityLabel, { extends: 'label' });

class QuantityInput extends HTMLElement {
  quantityUpdateUnsubscriber = undefined;
  quantityBoundriesUnsubscriber = undefined;
  quantityRulesUnsubscriber = undefined;

  constructor() {
    super();
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  get input() {
    return this.querySelector('input');
  }

  get value() {
    return this.input.value;
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.buttons = Array.from(this.querySelectorAll('button'));
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.input.addEventListener('focus', () => setTimeout(() => this.input.select()));

    this.buttons.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)), { signal: this.abortController.signal });

    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    this.quantityBoundriesUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityBoundries, this.setQuantityBoundries.bind(this));
    this.quantityRulesUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityRules, this.updateQuantityRules.bind(this));
  }

  disconnectedCallback() {
    this.abortController.abort();

    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
    if (this.quantityBoundriesUnsubscriber) {
      this.quantityBoundriesUnsubscriber();
    }
    if (this.quantityRulesUnsubscriber) {
      this.quantityRulesUnsubscriber();
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    if (this.input.disabled) return;
    const previousValue = this.input.value;

    if (event.currentTarget.name === 'plus') {
      if (parseInt(this.input.getAttribute('data-min')) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.getAttribute('data-min');
      }
      else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);

      const bumpClass = event.currentTarget.name === 'plus' ? 'qty-bump-up' : 'qty-bump-down';
      this.input.classList.remove('qty-bump-up', 'qty-bump-down');
      void this.input.offsetWidth;
      this.input.classList.add(bumpClass);
    }

    if (this.input.getAttribute('data-min') === previousValue && event.currentTarget.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  onInputChange() {
    if (this.input.value === '') {
      this.input.value = parseInt(this.input.min);
    }
    this.validateQtyRules();
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector('button[name="minus"]');
      if (buttonMinus) buttonMinus.toggleAttribute('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const buttonPlus = this.querySelector('button[name="plus"]');
      if (buttonPlus) buttonPlus.toggleAttribute('disabled', parseInt(value) >= parseInt(this.input.max));
    }
  }

  updateQuantityRules({ data: { sectionId, productId, parsedHTML } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;

    const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
    const quantityFormUpdated = parsedHTML.getElementById(`QuantityForm-${sectionId}-${this.productId}`);
    const quantityForm = this.closest(`#QuantityForm-${sectionId}-${this.productId}`);
    for (let selector of selectors) {
      const current = quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);
      if (!current || !updated) continue;

      if (selector === '.quantity__input') {
        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
        for (let attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) {
            current.setAttribute(attribute, valueUpdated);
          }
          else {
            current.removeAttribute(attribute);
          }
        }
      }
      else {
        current.innerHTML = updated.innerHTML;
      }
    }
  }

  setQuantityBoundries({ data: { sectionId, productId } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;

    const data = {
      cartQuantity: this.input.hasAttribute('data-cart-quantity') ? parseInt(this.input.getAttribute('data-cart-quantity')) : 0,
      min: this.input.hasAttribute('data-min') ? parseInt(this.input.getAttribute('data-min')) : 1,
      max: this.input.hasAttribute('data-max') ? parseInt(this.input.getAttribute('data-max')) : null,
      step: this.input.hasAttribute('step') ? parseInt(this.input.getAttribute('step')) : 1
    };

    let min = data.min;
    const max = data.max === null ? data.max : data.max - data.cartQuantity;
    if (max !== null) min = Math.min(min, max);
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

    this.input.min = min;

    // A max of 0 (the cart already holds the most allowed) is still a max (v2.10.25).
    if (max !== null) {
      this.input.max = max;
    }
    else {
      this.input.removeAttribute('max');
    }

    // Keep the quantity the shopper chose across a variant change or cart update; only
    // clamp it into the new bounds (v2.10.25 — it was reset to the minimum every time).
    let value = parseInt(this.input.value);
    if (isNaN(value) || value < min) value = min;
    if (max !== null && value > max) value = Math.max(max, 0);
    if (data.step > 1 && value % data.step !== 0) value = min;
    this.input.value = value;

    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, undefined);
  }

  reset() {
    this.input.value = this.input.defaultValue;
  }
}
customElements.define('quantity-input', QuantityInput);

class CartCount extends HTMLElement {
  constructor() {
    super();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText);
  }

  onCartUpdate(event) {
    if (event.cart.errors) return;

    const changed = this.itemCount !== event.cart.item_count;
    this.innerText = event.cart.item_count;
    this.hidden = this.itemCount === 0;

    if (changed && !this.hidden) {
      this.classList.remove('count-pop');
      void this.offsetWidth;
      this.classList.add('count-pop');
    }
  }
}
customElements.define('cart-count', CartCount);

class CustomerDiscountPreviewSync {
  constructor() {
    this.refreshRequestId = 0;
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
  }

  disconnect() {
    this.cartUpdateUnsubscriber?.();
  }

  async onCartUpdate(event) {
    if (event?.cart?.errors) return;

    const selectors = ['[data-customer-discount-price]', '[data-customer-discount-badges]'];
    if (!document.querySelector(selectors.join(', '))) return;

    const requestId = ++this.refreshRequestId;

    try {
      const response = await fetch(`${window.location.pathname}${window.location.search}`, {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) return;

      const html = await response.text();
      if (requestId !== this.refreshRequestId) return;

      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      this.replaceNodes(parsedDocument, '[data-customer-discount-price]');
      this.replaceNodes(parsedDocument, '[data-customer-discount-badges]');
    } catch (error) {
      console.warn('Customer discount preview sync failed.', error);
    }
  }

  replaceNodes(parsedDocument, selector) {
    const nextNodes = Array.from(parsedDocument.querySelectorAll(selector));
    if (!nextNodes.length) return;

    const nextNodesByKey = new Map();
    nextNodes.forEach((node) => {
      const key = node.dataset.customerDiscountKey;
      if (!key) return;

      if (!nextNodesByKey.has(key)) {
        nextNodesByKey.set(key, []);
      }

      nextNodesByKey.get(key).push(node);
    });

    document.querySelectorAll(selector).forEach((currentNode) => {
      const key = currentNode.dataset.customerDiscountKey;
      if (!key) return;

      const replacementQueue = nextNodesByKey.get(key);
      if (!replacementQueue?.length) return;

      const replacementNode = replacementQueue.shift();
      currentNode.replaceWith(replacementNode.cloneNode(true));
    });
  }
}

if (!window.themeCustomerDiscountPreviewSync) {
  window.themeCustomerDiscountPreviewSync = new CustomerDiscountPreviewSync();
}

class RecentlyViewed extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.hasLocalStorage) {
      this.hidden = true;
      return;
    }

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
  }

  init() {
    fetch(this.getAttribute('data-url') + this.getQueryString())
      .then(response => response.text())
      .then(responseText => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        const recommendations = sectionInnerHTML.querySelector('recently-viewed');
        // The cart drawer's History tab has an empty screen beside the list (v2.10.45)
        const emptyState = this.parentElement?.querySelector(':scope > [data-recent-empty]');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          if (emptyState) emptyState.hidden = true;
          this.dispatchEvent(new CustomEvent('recentlyViewed:loaded'));
        }
        else {
          if (emptyState) {
            emptyState.hidden = false;
            this.hidden = true;
          }
          this.dispatchEvent(new CustomEvent('is-empty'));
          // In the editor the preview iframe usually has no browsing history, so
          // this resolves empty — keep the section in the DOM so it stays
          // selectable/editable instead of disappearing while tuning settings.
          if (Shopify.designMode) return;
          this.closest('.recently-section')?.remove();
        }
      })
      .catch(e => {
        console.error(e);
      });
  }

  getQueryString() {
    let recentlyViewed = '[]';
    if (theme.config.hasLocalStorage) {
      recentlyViewed = window.localStorage.getItem(`${theme.settings.themeName}:recently-viewed`) || '[]';
    }

    const items = new Set(JSON.parse(recentlyViewed));
    if (this.hasAttribute('data-product-id')) {
      items.delete(parseInt(this.getAttribute('data-product-id')));
    }
    return Array.from(items.values(), (item) => `id:${item}`).slice(0, this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 4).join(' OR ');
  }
}
customElements.define('recently-viewed', RecentlyViewed);

class ProductRecommendations extends HTMLElement {
  // Session cache keyed by recommendations URL. The cart drawer re-renders
  // this block on every cart update — without the cache it flashes in and
  // out while the same answer is re-fetched. The URL embeds the source
  // product id, so the cache self-invalidates when the cart's first
  // product changes.
  static resultCache = {};

  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
  }

  connectedCallback() {
    // Theme editor: skip the cache and always re-fetch. The cache key is the
    // data-url, which only varies by product/section/limit — NOT by columns,
    // heading, alignment, colours, etc. Applying the cache here also sets
    // _resolved, which blocks init()'s fetch, so those setting changes would
    // otherwise show stale content until product_limit (the URL) changed.
    if (Shopify.designMode) return;

    const cached = ProductRecommendations.resultCache[this.getAttribute('data-url')];
    if (cached !== undefined) {
      // Known result — apply synchronously, before paint, so nothing flashes
      this.applyResult(cached);
    }
  }

  init() {
    if (this._resolved) return;

    fetch(this.getAttribute('data-url'))
      .then(response => response.text())
      .then(responseText => {
        if (this._resolved) return;

        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (sectionInnerHTML === null) return;

        const recommendations = sectionInnerHTML.querySelector('product-recommendations');
        const result = recommendations && recommendations.innerHTML.trim().length ? recommendations.innerHTML : null;
        ProductRecommendations.resultCache[this.getAttribute('data-url')] = result;
        this.applyResult(result);
      })
      .catch(e => {
        console.error(e);
      });
  }

  applyResult(result) {
    this._resolved = true;

    if (result !== null) {
      this.innerHTML = result;
      this.dispatchEvent(new CustomEvent('recommendations:loaded'));
    }
    else {
      this.dispatchEvent(new CustomEvent('is-empty'));
      // In the editor, keep the section so it stays selectable/editable while
      // tuning settings (don't yank it out of the DOM on an empty result).
      if (Shopify.designMode) return;
      this.setAttribute('hidden', '');
      this.closest('.recommendations-section')?.remove();
    }
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class ProductComplementary extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get container() {
    return this.closest('product-recommendations');
  }

  get prevButton() {
    return this.container.querySelector('[data-prev-button]');
  }

  get nextButton() {
    return this.container.querySelector('[data-next-button]');
  }

  connectedCallback() {
    if (this.innerHTML.trim().length) {
      if (this.classList.contains('flickity')) {
        this.carousel = new theme.Carousel(this, {
          accessibility: false,
          prevNextButtons: false,
          adaptiveHeight: true,
          pageDots: false,
          contain: true,
          cellAlign: 'center',
          rightToLeft: theme.config.rtl,
        }, {
          previous: this.prevButton,
          next: this.nextButton
        });

        this.carousel.load();
        this.carousel.slider.on('change', this.onChange.bind(this));
      }
    }
    else if (!Shopify.designMode) {
      // Keep the block visible in the editor so it stays selectable/editable;
      // on the storefront, hide when there are no complementary products.
      this.container.hidden = true;
    }
  }

  disconnectedCallback() {
    if (this.carousel) {
      this.carousel.unload();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const focusableEvents = 'button, [href]';

      const fromElement = this.items[parseInt(oldValue)];
      const toElement = this.items[parseInt(newValue)];

      fromElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      toElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.removeAttribute('tabindex');
      });
    }
  }

  onChange() {
    this.selectedIndex = this.carousel.slider.selectedIndex;
  }
}
customElements.define('product-complementary', ProductComplementary);

class MediaElement extends HTMLElement {
  constructor() {
    super();
  }

  get parallax() {
    return this.hasAttribute('data-parallax') ? parseFloat(this.getAttribute('data-parallax')) : false;
  }

  get direction() {
    return this.hasAttribute('data-parallax-dir') ? this.getAttribute('data-parallax-dir') : 'vertical';
  }

  get media() {
    return Array.from(this.querySelectorAll('picture>img, video, iframe, svg, video-media, g-map'));
  }

  connectedCallback() {
    if (theme.config.motionReduced || !this.parallax) return;
    this.setupParallax();
  }

  setupParallax() {
    // Nothing to move (a map section with no image): Motion.animate([]) returns no
    // controls and Motion.scroll then threw "t.pause is not a function" (v2.10.31, ONEi).
    if (!this.media.length) return;
    const [scale, translate] = [1 + this.parallax, this.parallax * 100 / (1 + this.parallax)];

    if (this.direction === 'vertical') {
      Motion.scroll(
        Motion.animate(this.media, { transform: [`scale(${scale}) translateY(0)`, `scale(${scale}) translateY(${translate}%)`], transformOrigin: ['bottom', 'bottom'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
    else if (this.direction === 'horizontal') {
      Motion.scroll(
        Motion.animate(this.media, { transform: [`scale(${scale}) translateX(0)`, `scale(${scale}) translateX(${translate}%)`], transformOrigin: ['right', 'right'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
    else {
      Motion.scroll(
        Motion.animate(this.media, { transform: [`scale(1)`, `scale(${scale})`], transformOrigin: ['center', 'center'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
  }
}
customElements.define('media-element', MediaElement);

class SplitWords extends HTMLElement {
  constructor() {
    super();

    this.loadPromise = new Promise((resolve) => {
      this.resolveLoad = resolve;
    });
  }

  connectedCallback() {
    if (theme.config.motionReduced) {
      this.resolveLoad();
      return;
    }
    if (!document.body.hasAttribute('data-title-animation')) return;

    this.init();
  }

  async init() {
    const splitting = Splitting({ target: this, by: 'words' });

    splitting[0].words.forEach((item, index) => {
      const wrapper = document.createElement('animate-element');
      wrapper.className = 'block';
      wrapper.setAttribute('data-animate', this.getAttribute('data-animate'));
      wrapper.setAttribute('data-animate-delay', (this.hasAttribute('data-animate-delay') ? parseInt(this.getAttribute('data-animate-delay')) : 0) + (index * 30));

      for (const itemContent of item.childNodes) {
        wrapper.appendChild(itemContent);
      }

      item.appendChild(wrapper);
    });
    this.classList.remove('js-invisible');

    const animateElements = this.querySelectorAll('animate-element');
    await Promise.all([...animateElements].map(el => el.loadPromise));
    this.resolveLoad();
  }
}
customElements.define('split-words', SplitWords);

class HighlightedText extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get splitWords() {
    return this.closest('split-words');
  }

  async init() {
    if (this.splitWords) {
      await this.splitWords.loadPromise;
    }
    this.classList.add('animated');
  }
}
customElements.define('highlighted-text', HighlightedText, { extends: 'em' });

class MarqueeElement extends HTMLElement {
  constructor() {
    super();

    if (theme.config.motionReduced) return;

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get childElement() {
    return this.firstElementChild;
  }

  get speed() {
    return this.hasAttribute('data-speed') ? parseInt(this.getAttribute('data-speed')) : 16;
  }

  get maximum() {
    return this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : Math.ceil(this.parentWidth / this.childElementWidth) + 2;
  }

  get direction() {
    return this.hasAttribute('data-direction') ? this.getAttribute('data-direction') : 'left';
  }

  get parallax() {
    return !theme.config.isTouch && this.hasAttribute('data-parallax') ? parseFloat(this.getAttribute('data-parallax')) : false;
  }

  get parentWidth() {
    return this.getWidth(this);
  }

  get childElementWidth() {
    return this.getWidth(this.childElement);
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (this.childElementCount === 1) {
      this.childElement.classList.add('animated');

      const maximum = this.maximum;
      for (let index = 0; index < maximum; index++) {
        this.clone = this.childElement.cloneNode(true);
        this.clone.setAttribute('aria-hidden', 'true');
        this.clone.querySelectorAll('button, [href]').forEach((el) => {
          el.setAttribute('tabindex', '-1');
        });
        this.appendChild(this.clone);
        this.clone.querySelectorAll('.media').forEach((media) => media.classList.remove('loading'));
      }

      this.style.setProperty('--duration', `${(33 - this.speed) * Math.min(2.5, Math.ceil(this.childElementWidth / this.parentWidth))}s`);
    }

    if (this.parallax) {
      let translate = this.parallax * 100 / (1 + this.parallax);
      if (this.direction === 'right') {
        translate = translate * -1;
      }
      if (theme.config.rtl) {
        translate = translate * -1;
      }

      Motion.scroll(
        Motion.animate(this, { transform: [`translateX(${translate}%)`, `translateX(0)`] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
    else {
      // pause when out of view
      const observer = new IntersectionObserver((entries, _observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.classList.remove('paused');
          }
          else {
            this.classList.add('paused');
          }
        });
      }, { rootMargin: '0px 0px 50px 0px' });
      observer.observe(this);
    }
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect();
    return rect.right - rect.left;
  }
}
customElements.define('marquee-element', MarqueeElement);

class ScrolledImages extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get parallax() {
    return this.hasAttribute('data-parallax') ? parseFloat(this.getAttribute('data-parallax')) : false;
  }

  get template() {
    return this.querySelector('template');
  }

  get images() {
    return Array.from(this.template.content.querySelectorAll('.scrolled-images__item'));
  }

  init() {
    this.beforeInit();

    if (theme.config.motionReduced || !this.parallax) return;
    this.setupParallax();
  }

  beforeInit() {
    const main = this.querySelector('.scrolled-images__main');

    for (let i = 0; i < 3; i++) {
      let images = this.shuffle(this.images);

      if (images.length < 8) {
        let start = 0;
        while (images.length < 8) {
          images.push(images[start].cloneNode(true));
          start++;
        }
      }

      const row = document.createElement('div');
      row.classList = 'scrolled-images__row';
      images.forEach((item) => row.appendChild(item.cloneNode(true)));

      main.appendChild(row);
    }
  }

  setupParallax() {
    Array.from(this.querySelectorAll('.scrolled-images__row')).forEach((element, index) => {
      let translate = -1 * this.parallax * 100 / (1 + this.parallax);
      if (theme.config.rtl) {
        translate = translate * -1;
      }

      if (index % 2 === 0) {
        Motion.scroll(
          Motion.animate(element, { transform: [`translateX(${translate}%)`, 'translateX(0)'] }, { easing: 'linear' }),
          { target: this, offset: Motion.ScrollOffset.Any }
        );
      }
      else {
        Motion.scroll(
          Motion.animate(element, { transform: ['translateX(0)', `translateX(${translate}%)`] }, { easing: 'linear' }),
          { target: this, offset: Motion.ScrollOffset.Any }
        );
      }
    });
  }

  shuffle(arr) {
    return arr.sort(() => Math.random() - 0.5);
  }
}
customElements.define('scrolled-images', ScrolledImages);

class DropdownElement extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown'
    };

    this.events = {
      afterHide: 'dropdown:afterHide',
      afterShow: 'dropdown:afterShow'
    };

    this.detectHoverListener = this.detectHover.bind(this);
    this.controls.forEach((button) => {
      button.addEventListener('click', this.onToggleClicked.bind(this));
      button.addEventListener('mouseenter', this.detectHoverListener.bind(this));
      button.addEventListener('mouseleave', this.detectHoverListener.bind(this));
    });

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);
  }

  static get observedAttributes() {
    return ["id", 'open'];
  }

  get open() {
    return this.hasAttribute('open');
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get container() {
    return this.querySelector('*:first-child');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'));
        break;
    }
  }

  show() {
    document.body.classList.add(this.classes.bodyClass);
    this.setAttribute('open', '');

    document.addEventListener('click', this.detectClickOutsideListener);
    document.addEventListener('keyup', this.detectEscKeyboardListener);
    document.addEventListener('focusout', this.detectFocusOutListener);

    this.afterShow();

    return theme.utils.waitForEvent(this, this.events.afterShow);
  }

  hide() {
    document.body.classList.remove(this.classes.bodyClass);
    this.removeAttribute('open');

    document.removeEventListener('click', this.detectClickOutsideListener);
    document.removeEventListener('keyup', this.detectEscKeyboardListener);
    document.removeEventListener('focusout', this.detectFocusOutListener);

    this.afterHide();

    return theme.utils.waitForEvent(this, this.events.afterHide);
  }

  onToggleClicked(event) {
    event?.preventDefault();
    this.open ? this.hide() : this.show();
  }

  afterShow() {
    Motion.animate(this, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
    Motion.animate(this.container, { transform: ['translateY(-105%)', 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
  }

  afterHide() {
    Motion.animate(this, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
    Motion.animate(this.container, { transform: 'translateY(-105%)' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
  }

  detectClickOutside(event) {
    if (!this.parentElement.contains(event.target)) {
      this.hide();
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      this.hide();
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.hide();
    }
  }

  detectHover(event) {
    if (theme.config.isTouch) return;

    if (event.type === 'mouseenter') {
      this.show();
    }
    else {
      this.hide();
    }
  }
}

const COUNTRY_FLAG_OFFSET = 127397;
function getCountryEmoji(countryCode) {
  const normalizedCode = (countryCode || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalizedCode)) return '';

  return Array.from(normalizedCode, (character) => String.fromCodePoint(COUNTRY_FLAG_OFFSET + character.charCodeAt(0))).join('');
}

function populateCountryEmojis(root = document) {
  if (!root?.querySelectorAll) return;

  root.querySelectorAll('[data-country-emoji]').forEach((element) => {
    const emoji = getCountryEmoji(element.getAttribute('data-country-emoji'));
    element.textContent = emoji;
    element.hidden = emoji.length === 0;
  });
}

populateCountryEmojis();

class DropdownLocalization extends DropdownElement {
  constructor() {
    super();

    this.parentHoverListener = this.onParentHover.bind(this);
    this.internalPointerListener = this.onInternalPointer.bind(this);
    this.internalFocusOutListener = this.onInternalFocusOut.bind(this);
    this.documentTypeAheadListener = this.onDocumentTypeAhead.bind(this);
    this.focusSearchInputFrame = null;
  }

  get searchInput() {
    return this.querySelector('[data-localization-search-input]');
  }

  connectedCallback() {
    if (theme.config.isTouch) return;

    this.parentElement?.addEventListener('mouseenter', this.parentHoverListener);
    this.parentElement?.addEventListener('mouseleave', this.parentHoverListener);
    this.addEventListener('pointerdown', this.internalPointerListener);
    this.addEventListener('click', this.internalPointerListener);
    this.addEventListener('focusout', this.internalFocusOutListener);
    document.addEventListener('keydown', this.documentTypeAheadListener);
  }

  disconnectedCallback() {
    this.parentElement?.removeEventListener('mouseenter', this.parentHoverListener);
    this.parentElement?.removeEventListener('mouseleave', this.parentHoverListener);
    this.removeEventListener('pointerdown', this.internalPointerListener);
    this.removeEventListener('click', this.internalPointerListener);
    this.removeEventListener('focusout', this.internalFocusOutListener);
    document.removeEventListener('keydown', this.documentTypeAheadListener);
    this.clearSearchInputFocusFrame();
  }

  afterShow() {
    Motion.animate(this, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
    this.focusSearchInput({ select: true });
  }

  afterHide() {
    Motion.animate(this, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
    this.blurSearchInput();
    this.querySelector('form[is="localization-listbox"]')?.resetSearch?.();
  }

  detectHover(event) {
    return;
  }

  detectClickOutside(event) {
    if (this.contains(event.target) || this.parentElement?.contains(event.target)) return;
    this.hide();
  }

  detectFocusOut(event) {
    const nextTarget = event.relatedTarget;
    if (nextTarget && (this.contains(nextTarget) || this.parentElement?.contains(nextTarget))) return;

    requestAnimationFrame(() => {
      if (this.matches(':focus-within') || this.parentElement?.matches(':hover')) return;
      this.hide();
    });
  }

  onParentHover(event) {
    if (event.type === 'mouseenter') {
      this.show();
    }
    else {
      this.hide();
    }
  }

  onInternalPointer(event) {
    event.stopPropagation();
  }

  onInternalFocusOut() {
    requestAnimationFrame(() => {
      if (this.matches(':focus-within') || this.parentElement?.matches(':hover')) return;
      this.hide();
    });
  }

  focusSearchInput({ attempts = 4 } = {}) {
    const searchInput = this.searchInput;
    if (!searchInput) return;

    this.clearSearchInputFocusFrame();

    const focusAttempt = (remainingAttempts) => {
      if (!this.open) return;

      searchInput.focus({ preventScroll: true });

      if (document.activeElement === searchInput) {
        const caretPosition = searchInput.value.length;
        searchInput.setSelectionRange(caretPosition, caretPosition);
        this.focusSearchInputFrame = null;
        return;
      }

      if (remainingAttempts > 1) {
        this.focusSearchInputFrame = requestAnimationFrame(() => focusAttempt(remainingAttempts - 1));
      }
      else {
        this.focusSearchInputFrame = null;
      }
    };

    this.focusSearchInputFrame = requestAnimationFrame(() => focusAttempt(attempts));
  }

  clearSearchInputFocusFrame() {
    if (!this.focusSearchInputFrame) return;
    cancelAnimationFrame(this.focusSearchInputFrame);
    this.focusSearchInputFrame = null;
  }

  blurSearchInput() {
    const searchInput = this.searchInput;
    this.clearSearchInputFocusFrame();

    if (searchInput && document.activeElement === searchInput) {
      searchInput.blur();
    }
  }

  onDocumentTypeAhead(event) {
    if (!this.open || event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;

    const searchInput = this.searchInput;
    if (!searchInput) return;

    const target = event.target;
    if (target instanceof HTMLElement && (target === searchInput || target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]') || target.isContentEditable)) {
      return;
    }

    if (event.key.length !== 1) return;

    event.preventDefault();
    this.focusSearchInput();

    requestAnimationFrame(() => {
      if (document.activeElement !== searchInput) return;

      const start = searchInput.selectionStart ?? searchInput.value.length;
      const end = searchInput.selectionEnd ?? searchInput.value.length;
      searchInput.setRangeText(event.key, start, end, 'end');
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }
}
customElements.define('dropdown-localization', DropdownLocalization);

const lockDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown-menu'
    };

    this.events = {
      afterHide: 'menu:afterHide',
      afterShow: 'menu:afterShow'
    };

    this._open = this.hasAttribute('open');
    this.summaryElement.addEventListener('click', this.onSummaryClicked.bind(this));
    this.summaryElement.addEventListener('keydown', this.onSummaryKeydown.bind(this));

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);

    this.hoverTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener('mouseenter', this.detectHoverListener);
    this.addEventListener('mouseleave', this.detectHoverListener);
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      }
      else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
        this.updateAriaExpanded(value);
      }
    }
  }

  get summaryElement() {
    return this.firstElementChild;
  }

  get contentElement() {
    return this.lastElementChild;
  }

  get open() {
    return this._open;
  }

  get trigger() {
    return this.hasAttribute('trigger') ? this.getAttribute('trigger') : 'click';
  }

  get summaryLink() {
    return this.summaryElement.getAttribute('data-link') || '';
  }

  get level() {
    return this.hasAttribute('level') ? this.getAttribute('level') : 'top';
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get focusableContentElements() {
    return theme.a11y.getFocusableElements(this.contentElement).filter((element) => !element.closest('[hidden], [inert]') && !element.hasAttribute('disabled'));
  }

  onSummaryClicked(event) {
    event.preventDefault();

    const isPlainPrimaryClick = event.detail > 0 && event.button === 0 && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);

    if (!theme.config.isTouch && this.trigger === 'hover' && this.summaryLink.length > 0 && isPlainPrimaryClick) {
      window.location.href = this.summaryLink;
    }
    else {
      this.open = !this.open;
    }
  }

  onSummaryKeydown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.openAndFocus('first');
    }
    else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.openAndFocus('last');
    }
  }

  openAndFocus(position = 'first') {
    if (this.open) {
      this.focusContent(position);
      return;
    }

    this.open = true;
    theme.utils.waitForEvent(this, this.events.afterShow).then(() => this.focusContent(position));
  }

  focusContent(position = 'first') {
    const elements = this.focusableContentElements;
    if (!elements.length) return;

    const index = position === 'last' ? elements.length - 1 : 0;
    elements[index].focus();
  }

  async transition(value) {
    this.updateAriaExpanded(value);

    if (value) {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) + 1);
      document.body.classList.add(this.classes.bodyClass);

      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      if (theme.config.motionReduced) {
        this.contentElement.setAttribute('open', '');
      } else {
        setTimeout(() => this.contentElement.setAttribute('open', ''), 100);
      }
      document.addEventListener('click', this.detectClickOutsideListener);
      document.addEventListener('keyup', this.detectEscKeyboardListener);
      document.addEventListener('focusout', this.detectFocusOutListener);
      await this.transitionIn();
      this.shouldReverse();

      return theme.utils.waitForEvent(this, this.events.afterShow);
    }
    else {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) - 1);
      document.body.classList.toggle(this.classes.bodyClass, lockDropdownCount.get(DetailsDropdown) > 0);

      this.summaryElement.removeAttribute('open');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.detectClickOutsideListener);
      document.removeEventListener('keyup', this.detectEscKeyboardListener);
      document.removeEventListener('focusout', this.detectFocusOutListener);
      await this.transitionOut();
      if (!this.open) this.removeAttribute('open');

      // A localization list left filtered would reopen showing only the last
      // search's matches. DropdownLocalization already does this for the
      // announcement bar; the header's selectors are details-dropdowns, so
      // they need it here. No-op for every other dropdown.
      this.querySelector('form[is="localization-listbox"]')?.resetSearch?.();

      return theme.utils.waitForEvent(this, this.events.afterHide);
    }
  }

  async transitionIn() {
    Motion.animate(this.contentElement, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1], delay: theme.config.motionReduced ? 0 : 0.2 });
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return Motion.animate(this.contentElement.firstElementChild, { transform: [`translateY(${translateY})`, 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
  }

  async transitionOut() {
    Motion.animate(this.contentElement, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return Motion.animate(this.contentElement.firstElementChild, { transform: `translateY(${translateY})` }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
  }

  detectClickOutside(event) {
    if (!this.contains(event.target) && !(event.target.closest('details') instanceof DetailsDropdown)) {
      this.open = false;
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger !== 'hover' || theme.config.isTouch) return;

    if (event.type === 'mouseenter') {
      this.open = true;
    }
    else {
      this.open = false;
    }
  }

  shouldReverse() {
    const maxWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (maxWidth > window.innerWidth) {
      this.contentElement.classList.add('should-reverse');
    }
  }

  updateAriaExpanded(isOpen) {
    this.summaryElement.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }
}
customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
lockDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => this.open = true);
      this.addEventListener('shopify:block:deselect', () => this.open = false);
    }
  }

  async transitionIn() {
    this.contentElement.querySelector('tabs-element')?.unload();
    setTimeout(() => {
      this.contentElement.querySelector('tabs-element')?.load();
    }, theme.config.motionReduced ? 0 : 450);

    document.body.classList.add('with-mega');
    return Motion.animate(this.contentElement.firstElementChild, { visibility: 'visible', transform: ['translateY(-105%)', 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
  }

  async transitionOut() {
    document.body.classList.remove('with-mega');
    return Motion.animate(this.contentElement.firstElementChild, { visibility: 'hidden', transform: 'translateY(-105%)' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
  }
}
customElements.define('details-mega', DetailsMega, { extends: 'details' });

class LocalizationListbox extends HTMLFormElement {
  constructor() {
    super();

    populateCountryEmojis(this);

    this.onItemClickListener = this.onItemClick.bind(this);
    this.onSearchInputListener = this.onSearchInput.bind(this);
    this.onSearchKeydownListener = this.onSearchKeydown.bind(this);

    this.items.forEach((item) => {
      item.addEventListener('click', this.onItemClickListener);
      item.dataset.localizationSearchValue = this.normalizeString([
        item.textContent,
        item.getAttribute('title'),
        item.getAttribute('hreflang'),
        item.getAttribute('lang'),
        item.getAttribute('data-value')
      ].filter(Boolean).join(' '));
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', this.onSearchInputListener);
      this.searchInput.addEventListener('keydown', this.onSearchKeydownListener);
    }
  }

  get items() {
    return this._items = this._items || Array.from(this.querySelectorAll('a'));
  }

  get searchInput() {
    return this.querySelector('[data-localization-search-input]');
  }

  get input() {
    return this.querySelector('input[name="locale_code"], input[name="country_code"]');
  }

  get returnTo() {
    return this.querySelector('input[name="return_to"]');
  }

  normalizeString(value) {
    return (value || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  onSearchInput(event) {
    const query = this.normalizeString(event.currentTarget.value);

    this.items.forEach((item) => {
      const itemMatches = item.dataset.localizationSearchValue.includes(query);
      item.closest('li')?.toggleAttribute('hidden', !itemMatches);
    });

    const list = this.querySelector('.dropdown__nav, .listbox, [role="list"]');
    if (list) {
      list.scrollTop = 0;
    }
  }

  onSearchKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  resetSearch() {
    if (!this.searchInput) return;

    this.searchInput.value = '';
    this.onSearchInput({ currentTarget: this.searchInput });
  }

  onItemClick(event) {
    event.preventDefault();

    this.input.value = event.currentTarget.getAttribute('data-value');
    if (this.returnTo) {
      this.returnTo.value = window.location.pathname;
    }
    this.submit();
  }
}
customElements.define('localization-listbox', LocalizationListbox, { extends: 'form' });

const cachedSectionsRenderingAPI = new Map();
class APIButton extends HTMLElement {
  constructor() {
    super();

    // data-load="active": defer the fetch until this tab is actually activated
    // (clicked or keyboard-selected) instead of prefetching when scrolled into
    // view. Lighter when many tabbed sections are on the page — tabs nobody
    // opens are never fetched. Default behaviour (prefetch on view) is unchanged.
    if (this.getAttribute('data-load') === 'active') {
      this.initOnActivate();
    } else {
      Motion.inView(this, this.init.bind(this));
    }
  }

  initOnActivate() {
    const tabs = this.closest('tabs-element');
    const tab = this.closest('button[role="tab"]');
    if (!tabs || !tab) {
      // Unexpected structure — fall back to the default prefetch-on-view.
      Motion.inView(this, this.init.bind(this));
      return;
    }

    const tabIndex = Array.from(tabs.querySelectorAll('button[role="tab"]')).indexOf(tab);
    const isActive = () => parseInt(tabs.getAttribute('selected-index') || 0) === tabIndex;

    if (isActive()) {
      this.init();
      return;
    }

    const observer = new MutationObserver(() => {
      if (isActive()) {
        observer.disconnect();
        this.init();
      }
    });
    observer.observe(tabs, { attributes: true, attributeFilter: ['selected-index'] });
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get sectionRenderingId() {
    return this.getAttribute('data-id');
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (document.getElementById(this.sectionRenderingId) === null || document.getElementById(this.sectionRenderingId).hasAttribute('loaded')) return;

    const url = `${theme.routes.root_url}?section_id=${this.sectionId}`;
    cachedSectionsRenderingAPI.has(url)
      ? this.renderSectionFromCache(url)
      : this.renderSectionFromFetch(url);

    this.sectionsRenderingAPIListener = this.afterSectionsRenderingAPI.bind(this);
    document.addEventListener('sectionsRenderingAPI:cached', this.sectionsRenderingAPIListener);
  }

  renderSectionFromCache(url) {
    const responseText = cachedSectionsRenderingAPI.get(url);
    if (responseText.length) {
      this.updateSectionRendering(responseText);
    }
  }

  renderSectionFromFetch(url) {
    cachedSectionsRenderingAPI.set(url, '');
    fetch(url)
      .then(response => response.text())
      .then(responseText => {
        this.updateSectionRendering(responseText);
        cachedSectionsRenderingAPI.set(url, responseText);

        document.dispatchEvent(new CustomEvent('sectionsRenderingAPI:cached', {
          detail: {
            url,
            sectionId: this.sectionId
          }
        }));
      })
      .catch(e => {
        console.error(e);
      });
  }

  updateSectionRendering(responseText) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const renderedElement = parsedHTML.getElementById(this.sectionRenderingId);
    document.getElementById(this.sectionRenderingId).replaceWith(renderedElement);
    populateCountryEmojis(renderedElement);

    // remove listener
    document.removeEventListener('sectionsRenderingAPI:cached', this.sectionsRenderingAPIListener);
  }

  afterSectionsRenderingAPI(event) {
    if (event.detail.sectionId === this.sectionId) {
      this.renderSectionFromCache(event.detail.url);
    }
  }
}
customElements.define('api-button', APIButton);

// Tabbed products image warm-up: hidden tab panels arrive prefetched as HTML,
// but their images keep loading=lazy inside [hidden] panels, so the first tab
// switch showed pop-in — and a fast fling on a rail can outrun the browser's
// lazy-load distance. Flip images to eager on INTENT: hovering/touching a tab
// warms its panel's first window; first pointer contact with a rail warms the
// rest of that rail. Each root warms once; a re-fetched panel is a new element
// and can warm again.
(() => {
  const warmImages = (root, cap) => {
    if (!root || root.dataset.imgWarmed) return;
    root.dataset.imgWarmed = '1';
    Array.from(root.querySelectorAll('img[loading="lazy"]')).slice(0, cap).forEach((img) => {
      img.loading = 'eager';
    });
  };
  const onIntent = (event) => {
    if (!event.target.closest) return;
    const tab = event.target.closest('button[role="tab"][aria-controls^="TabPanel-"]');
    if (tab) {
      warmImages(document.getElementById(tab.getAttribute('aria-controls')), 14);
      return;
    }
    const rail = event.target.closest('.tabbed-products__slider-wrap');
    if (rail) warmImages(rail, 60);
  };
  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true });
})();

class StickyElement extends HTMLElement {
  constructor() {
    super();

    this.endScroll = window.innerHeight - this.offsetHeight - 500;
    this.currPos = window.scrollY;
    this.screenHeight = window.innerHeight;
    this.stickyElementHeight = this.offsetHeight;
    this.bottomGap = this.offsetHeight < window.innerHeight ? this.topGap : 0;
    this.ticking = false;

    window.addEventListener('scroll', this.onScrollHandler.bind(this), {
      capture: true,
      passive: true,
    });

    this.updateInset = this.updateInset.bind(this);
  }

  connectedCallback() {
    this.updateInset();
    window.addEventListener('resize', this.updateInset, { passive: true });
    document.addEventListener('header:scrolled', this.updateInset);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.updateInset);
    document.removeEventListener('header:scrolled', this.updateInset);
  }

  get headerHeight() {
    let header = document.querySelector('.header-section header[is="sticky-header"]');
    if (this.hasAttribute('aria-controls')) {
      header = document.getElementById(this.getAttribute('aria-controls'));
    }

    const rootStyles = window.getComputedStyle(document.documentElement);
    const headerHeight = parseFloat(rootStyles.getPropertyValue('--header-height')) || 0;
    const stickyHeaderHeight = parseFloat(rootStyles.getPropertyValue('--sticky-header-height')) || 0;
    const liveHeaderHeight = header ? header.getBoundingClientRect().height : 0;

    let resolvedHeaderHeight = liveHeaderHeight;
    if (this.getAttribute('data-header-offset') === 'max') {
      resolvedHeaderHeight = Math.max(liveHeaderHeight, headerHeight, stickyHeaderHeight);
    } else if (this.getAttribute('data-header-offset') === 'sticky') {
      resolvedHeaderHeight = Math.max(liveHeaderHeight, stickyHeaderHeight);
    }

    return header ? (resolvedHeaderHeight + this.topGap) : (this.offsetHeight < window.innerHeight ? this.topGap : 0);
  }

  get topGap() {
    return this.hasAttribute('data-gap') ? parseInt(this.getAttribute('data-gap')) : 20;
  }

  get inset() {
    return parseFloat(this.style.getPropertyValue('--inset')) || 0;
  }

  updateInset() {
    requestAnimationFrame(() => {
      this.style.setProperty('--inset', `${this.headerHeight.toFixed(1)}px`);
    });
  }

  onScrollHandler() {
    if (!this.ticking) {
      requestAnimationFrame(() => {
        const style = window.getComputedStyle(this);
        if (style.position == 'sticky') {
          this.screenHeight = window.innerHeight;
          this.stickyElementHeight = this.offsetHeight;

          this.positionStickySidebar();
        }
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  positionStickySidebar() {
    this.endScroll = window.innerHeight - this.offsetHeight - this.bottomGap;
    const stickyElementTop = this.inset;
    const scrollDelta = this.currPos - window.scrollY;

    if (this.stickyElementHeight + this.headerHeight + this.bottomGap > this.screenHeight) {
      let newInset;

      if (window.scrollY < this.currPos) {
        // Scroll up
        if (stickyElementTop < this.headerHeight) {
          newInset = Math.min(stickyElementTop + scrollDelta, this.headerHeight);
        } else {
          newInset = this.headerHeight;
        }
      } else {
        // Scroll down
        if (stickyElementTop > this.endScroll) {
          newInset = Math.max(stickyElementTop + scrollDelta, this.endScroll);
        } else {
          newInset = this.endScroll;
        }
      }

      if (Math.abs(newInset - stickyElementTop) > 0.5) {
        this.style.setProperty('--inset', `${newInset.toFixed(1)}px`);
      }
    } else {
      this.style.setProperty('--inset', `${this.headerHeight.toFixed(1)}px`);
    }

    this.currPos = window.scrollY;
  }
}
customElements.define('sticky-element', StickyElement);

class ParallaxElement extends HTMLDivElement {
  constructor() {
    super();

    this.load();

    document.addEventListener('matchSmall', this.unload.bind(this));
    document.addEventListener('unmatchSmall', this.load.bind(this));
  }

  get mobileDisabled() {
    return false;
  }

  load() {
    if (theme.config.motionReduced) return;
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return;

    this.motion();
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, { transform: [`translateY(${this.getAttribute('data-start')})`, `translateY(${this.getAttribute('data-stop')})`] }),
      { target: this, offset: Motion.ScrollOffset.Any }
    );
  }

  unload() {
    if (this.animation) {
      this.animation();
      this.style.transform = null;
    }
  }
}
customElements.define('parallax-element', ParallaxElement, { extends: 'div' });

class ParallaxOverlay extends HTMLElement {
  constructor() {
    super();

    this.load();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.refresh.bind(this));
    }
  }

  refresh() {
    let options = {};
    options[this.getAttribute('data-target')] = this.getAttribute('data-stop');

    Motion.animate(this, options, { duration: 0 });
  }

  load() {
    let options = {};
    options[this.getAttribute('data-target')] = [this.getAttribute('data-start'), this.getAttribute('data-stop')];

    this.animation = Motion.scroll(
      Motion.animate(this, options),
      { target: this.parentElement, offset: Motion.ScrollOffset.Enter }
    );
  }
}
customElements.define('parallax-overlay', ParallaxOverlay);

class FooterParallax extends ParallaxElement {
  constructor() {
    super();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.refresh.bind(this));
    }
  }

  get mobileDisabled() {
    return true;
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, { transform: ['translateY(-50%)', 'translateY(0)'] }),
      { target: this, offset: Motion.ScrollOffset.Enter }
    );
  }

  refresh() {
    if (theme.config.motionReduced) return;
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return;

    setTimeout(() => {
      Motion.animate(this, { transform: 'translateY(0)' }, { duration: 0 });
    });
  }
}
customElements.define('footer-parallax', FooterParallax, { extends: 'div' });

class FooterGroup extends HTMLElement {
  constructor() {
    super();

    this.init();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', this.init.bind(this));
    }
  }

  get rounded() {
    return Array.from(this.querySelectorAll('.section--rounded'));
  }

  get sections() {
    return Array.from(this.querySelectorAll('.shopify-section'));
  }

  init() {
    this.sections.forEach((shopifySection, index) => {
      const section = shopifySection.querySelector('.section');
      if (section) {
        section.classList.remove('section--next-rounded');
        section.style.zIndex = this.sections.length - index;
      }
    });

    this.rounded.forEach((section) => {
      const shopifySection = section.closest('.shopify-section');
      let previousShopifySection = shopifySection.previousElementSibling;

      if (previousShopifySection === null) {
        previousShopifySection = document.querySelector('.main-content .shopify-section:last-child');
      }

      if (previousShopifySection) {
        const previousSection = previousShopifySection.querySelector('.section');
        if (previousSection) {
          previousSection.classList.add('section--next-rounded');
        }
      }
    });
  }
}
customElements.define('footer-group', FooterGroup, { extends: 'footer' });

class CarouselElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get watchCSS() {
    return this.hasAttribute('watch-css');
  }

  get initialIndex() {
    return parseInt(this.getAttribute('initial-index') || 0);
  }

  init() {
    if (this.items.length > 1) {
      this.carousel = new Flickity(this, {
        watchCSS: this.watchCSS,
        prevNextButtons: false,
        // Flickity's own page dots are the theme's standard ones. Opt-out only,
        // so every existing carousel keeps them.
        pageDots: !this.hasAttribute('no-dots'),
        adaptiveHeight: true,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        initialIndex: this.initialIndex,
      });

      // Flickity's accessibility option (on by default) tabindexes the element
      // and focuses it whenever a cell is selected — including from a click or
      // the end of a drag. That programmatic focus paints the global
      // keyboard-focus ring, so a mouse user sees a blue box round the
      // carousel. Five other Flickity instances in this theme dodge it with
      // accessibility:false, but that also throws away arrow-key navigation.
      //
      // Instead, silence only the pointer case, with the theme's own idiom:
      // lastInputWasKeyboard (capture-phase keydown/pointerdown) plus the
      // one-shot .focus-quiet class, exactly as removeTrapFocus does. A real
      // Tab still gets its ring, because the capture keydown sets the flag
      // before focus moves.
      this.addEventListener('focus', () => {
        if (theme.a11y.lastInputWasKeyboard) return;

        this.classList.add('focus-quiet');
        this.addEventListener('blur', () => this.classList.remove('focus-quiet'), { once: true });
      });

      this.addEventListener('control:select', (event) => this.select(event.detail.index));
      this.carousel.on('change', this.onChange.bind(this));

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.carousel.select(this.items.indexOf(event.target)));
      }
    }
  }

  disconnectedCallback() {
    if (this.carousel) this.carousel.destroy();
  }

  select(index = 0, immediate = false) {
    if (!immediate) {
      const { selectedIndex, slides } = this.carousel;

      immediate = Math.abs(index - selectedIndex) > 3;

      if (index === 0 && selectedIndex === slides.length - 1) {
        immediate = false;
      }

      if (index === slides.length - 1 && selectedIndex === 0) {
        immediate = false;
      }
    }
    this.carousel.select(index, false, immediate);
  }

  onChange(index) {
    this.dispatchEvent(new CustomEvent('carousel:change', { bubbles: true, detail: { index } }));
  }
}
customElements.define('carousel-element', CarouselElement);

class TestimonialsElement extends CarouselElement {
  constructor() {
    super();

    Motion.inView(this, () => {
      setTimeout(() => this.update(), 600);
    });
  }

  update() {
    if (this.carousel) this.carousel.select(0);
  }
}
customElements.define('testimonials-element', TestimonialsElement);

class SecondaryMedia extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get template() {
    return this.previousElementSibling;
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  disconnectedCallback() {
    if (this.carousel) this.carousel.destroy();
  }

  init() {
    if (this.built) return;
    this.appendChild(this.template.content.cloneNode(true));
    this.buildCarousel();
  }

  // Build (or rebuild) the Flickity carousel from whatever .media slides are
  // currently children. A single slide stays a static image — no Flickity, no
  // scroll, no page dots.
  buildCarousel() {
    this.built = true;
    this.mediaCount = this.querySelectorAll('.media').length;
    this.items = Array.from(this.children);

    if (this.mediaCount > 1) {
      this.style.visibility = 'hidden';

      this.carousel = new Flickity(this, {
        accessibility: false,
        draggable: false,
        pageDots: true,
        prevNextButtons: false,
        wrapAround: true,
        // Each cell is 1px wider than the viewport (theme.css, the overlap that hides seams
        // mid-swipe). Centred, that left 0.5px of the next image as a line on the right edge;
        // start-aligned, the extra pixel sits outside the viewport (v2.10.42).
        cellAlign: theme.config.rtl ? 'right' : 'left',
        rightToLeft: theme.config.rtl,
        on: {
          ready: () => {
            this.style.visibility = 'visible';
          }
        }
      });

      if (!this.hoverBound) {
        this.addEventListener('mousemove', this.onMoveHandler);
        this.addEventListener('mouseleave', this.onLeaveHandler);
        this.hoverBound = true;
      }
      this.carousel.on('change', this.onChange.bind(this));
    } else {
      this.style.visibility = 'visible';
    }
  }

  // Replace the carousel's slides with a variant gallery (a list of image
  // URLs), so a card-mode swatch click shows exactly that variant's images —
  // one image renders as a single static image, two as a two-up gallery, etc.
  setGallery(urls) {
    if (!urls || !urls.length) return;

    var blueprintImg = this.template && this.template.content && this.template.content.querySelector('.media img');
    var blueprint = blueprintImg && blueprintImg.closest('.media');
    if (!blueprint) return;

    function buildSrcset(url) {
      return [180, 360, 540, 720, 900, 1080].map(function (w) {
        return url.replace(/width=\d+/, 'width=' + w) + ' ' + w + 'w';
      }).join(', ');
    }

    if (this.carousel) {
      this.carousel.destroy();
      this.carousel = null;
    }
    this.innerHTML = '';

    urls.slice(0, 4).forEach(function (url) {
      var cell = blueprint.cloneNode(true);
      var img = cell.querySelector('img');
      if (img) {
        img.classList.remove('loaded');
        img.removeAttribute('style');
        img.src = url;
        img.srcset = buildSrcset(url);
      }
      this.appendChild(cell);
    }, this);

    // Reset the selection so the swapped gallery starts on its first image and
    // the selected-index attribute can't point past the new (shorter) slides.
    this.removeAttribute('selected-index');
    this.buildCarousel();
  }

  onMoveHandler(event) {
    if (!this.carousel) return;
    // One equal-width zone per image: the i-th horizontal slice selects image
    // i, so the hovered position always matches the active page dot — for any
    // image count (2 -> halves, 3 -> thirds, 4 -> quarters).
    const { width } = this.carousel.size;
    if (!width) return;
    const mouseX = event.clientX - this.getBoundingClientRect().left;
    const zone = Math.floor(mouseX / (width / this.mediaCount));
    this.carousel.select(Math.min(Math.max(zone, 0), this.mediaCount - 1));
  }

  onLeaveHandler() {
    if (!this.carousel) return;
    this.carousel.select(0);
  }

  onChange() {
    this.selectedIndex = this.carousel.selectedIndex;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      // items can be shorter than a stale index after setGallery() rebuilds
      // the slides for a different variant — guard against undefined cells.
      const fromElement = this.items && this.items[parseInt(oldValue)];
      const toElement = this.items && this.items[parseInt(newValue)];

      if (fromElement && fromElement.tagName === 'VIDEO-MEDIA') {
        fromElement.pause();
      }

      if (toElement && toElement.tagName === 'VIDEO-MEDIA') {
        toElement.play();
      }
    }
  }
}
customElements.define('secondary-media', SecondaryMedia);

class MotionList extends HTMLElement {
  constructor() {
    super();

    this.isMobile = theme.config.mqlSmall || theme.config.isTouch;
    this.motionReduced = theme.config.motionReduced || this.hasAttribute('motion-reduced');

    if (this.motionReduced || this.initialized) return;

    this.unload();
    Motion.inView(this, this.load.bind(this));
  }

  get items() {
    return Array.from(this.querySelectorAll('.card'));
  }

  get itemsToShow() {
    return Array.from(this.querySelectorAll('.card:not([style])'));
  }

  get initialized() {
    return this.hasAttribute('initialized');
  }

  getAnimationParams() {
    return {
      distance: this.isMobile ? 30 : 50,
      duration: this.motionReduced ? 0.001 : (this.isMobile ? 0.3 : 0.5),
      staggerDelay: this.motionReduced ? 0 : (this.isMobile ? 0.05 : 0.1)
    };
  }

  getVisibleItems(items) {
    const viewportHeight = window.innerHeight + window.scrollY;
    const visible = [];
    const invisible = [];

    items.forEach(item => {
      const rect = item.getBoundingClientRect();
      const isVisible = rect.top < viewportHeight && rect.bottom > 0;

      if (isVisible) {
        visible.push(item);
      } else {
        invisible.push(item);
      }
    });

    return { visible, invisible };
  }

  unload() {
    const { distance } = this.getAnimationParams();
    const visibleItems = this.items;

    Motion.animate(visibleItems, { y: distance, opacity: 0, visibility: 'hidden' }, { duration: 0 });
  }

  async load() {
    const { distance, duration, staggerDelay } = this.getAnimationParams();
    const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(this.items);

    await Motion.animate(visibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration, delay: Motion.stagger(staggerDelay) }).finished;
    Motion.animate(inVisibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration });

    this.items.forEach(item => item.style.removeProperty('transform'));
    this.setAttribute('initialized', '');
  }

  async reload() {
    const { distance, duration, staggerDelay } = this.getAnimationParams();
    const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(this.itemsToShow);

    await Motion.animate(visibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration, delay: Motion.stagger(staggerDelay) }).finished;
    Motion.animate(inVisibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration });

    this.items.forEach(item => item.style.removeProperty('transform'));
  }
}
customElements.define('motion-list', MotionList);

class LazyBackground extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  get image() {
    const style = window.getComputedStyle(this);
    return style.backgroundImage ? style.backgroundImage.slice(5, -2).replace('width=1', 'width=720') : false;
  }

  async init() {
    if (!this.image) return;

    const img = document.createElement('img');
    img.src = this.image;
    img.style.visibility = 'hidden';
    img.style.position = 'absolute';

    await theme.utils.imageLoaded(img);
    this.style.backgroundImage = `url(${this.image})`;
    img.remove();
  }
}
customElements.define('lazy-background', LazyBackground);

class MenuToggle extends MagnetButton {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get expanded() {
    return this.getAttribute('aria-expanded') === 'true';
  }

  onClick() {
    this.setAttribute('aria-expanded', this.expanded ? 'false' : 'true');
    if (this.controlledElement) this.controlledElement.classList.toggle('active');
  }
}
customElements.define('menu-toggle', MenuToggle, { extends: 'button' });

class ScrollShadow extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get template() {
    return this.querySelector('template');
  }

  init() {
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.appendChild(this.template.content.cloneNode(true));
    this.shadowRoot.addEventListener('slotchange', () => this.load());
    this.updater = new theme.scrollShadow.Updater(this.shadowRoot.children[1]);

    this.load();
  }

  load() {
    this.updater.on(this.children[0]);
  }

  disconnectedCallback() {
    this.updater?.on();
  }
}
customElements.define('scroll-shadow', ScrollShadow);

class CustomSelect extends HTMLSelectElement {
  constructor() {
    super();

    this.onChange();
    this.addEventListener('change', this.onChange);
  }

  onChange() {
    this.value !== '' || this.selectedIndex === -1 ? this.setAttribute('selected', '') : this.removeAttribute('selected');
  }
}
customElements.define('custom-select', CustomSelect, { extends: 'select' });

class GMap extends HTMLElement {
  constructor() {
    super();

    if (!this.hasAttribute('data-api-key') || !this.hasAttribute('data-map-address')) {
      return;
    }

    Motion.inView(this, this.prepMapApi.bind(this), { margin: '600px 0px 600px 0px' });

    // Global function called by Google on auth errors.
    // Show an auto error message on all map instances.
    window.gm_authFailure = () => {

      // Show errors only to merchant in the editor.
      if (Shopify.designMode) {
        window.mapError(theme.strings.authError);
      }
    };

    window.mapError = (error, element) => {
      const container = element ? element.closest('.shopify-section') : document;
      container.querySelectorAll('.gmap--error').forEach((error) => {
        error.remove();
      });

      container.querySelectorAll('g-map').forEach((map) => {
        const message = document.createElement('div');
        message.classList.add('rte', 'alert', 'alert--error', 'gmap--error');
        message.innerHTML = error;
        map.closest('.with-map').prepend(message);
      });
    };

    window.gmNoop = () => { };
  }

  prepMapApi() {
    this.loadScript()
      .then(this.initMap.bind(this))
      .then(() => {
        setTimeout(() => {
          const container = this.closest('.banner__map');
          if (container && container.previousElementSibling) {
            container.previousElementSibling.classList.remove('opacity-0');
          }
        }, 1e3);
      });
  }

  loadScript() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.getAttribute('data-api-key')}&callback=gmNoop`;
    });
  }

  initMap() {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: this.getAttribute('data-map-address') }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK) {

        // Show errors only to merchant in the editor.
        if (Shopify.designMode) {
          let errorMessage;

          switch (status) {
            case 'ZERO_RESULTS':
              errorMessage = theme.strings.addressNoResults;
              break;
            case 'OVER_QUERY_LIMIT':
              errorMessage = theme.strings.addressQueryLimit;
              break;
            case 'REQUEST_DENIED':
              errorMessage = theme.strings.authError;
              break;
            default:
              errorMessage = theme.strings.addressError;
              break;
          }
          window.mapError(errorMessage, this);
        }
      }
      else {
        const mapOptions = {
          zoom: parseInt(this.getAttribute('data-zoom')),
          center: results[0].geometry.location,
          draggable: true,
          clickableIcons: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          disableDefaultUI: true
        };

        const map = new google.maps.Map(this, mapOptions), center = map.getCenter();

        map.setCenter(center);

        const icon = {
          path: "M22.6746 0C10.2174 0 0 8.79169 0 21.5118C0 31.2116 4.33864 38.333 9.26606 42.998C11.7232 45.3243 14.3387 47.0534 16.6674 48.2077C18.9384 49.3333 21.1148 50 22.6746 50C24.2345 50 26.4108 49.3333 28.6818 48.2077C31.0105 47.0534 33.626 45.3243 36.0832 42.998C41.0106 38.333 45.3492 31.2116 45.3492 21.5118C45.3492 8.79169 35.1318 0 22.6746 0ZM29.6514 22.6746C29.6514 26.5278 26.5278 29.6514 22.6746 29.6514C18.8214 29.6514 15.6978 26.5278 15.6978 22.6746C15.6978 18.8214 18.8214 15.6978 22.6746 15.6978C26.5278 15.6978 29.6514 18.8214 29.6514 22.6746Z",
          fillColor: this.getAttribute('data-marker-color'),
          fillOpacity: 1,
          anchor: new google.maps.Point(15, 55),
          strokeWeight: 0,
          scale: 0.7
        };

        new google.maps.Marker({
          map: map,
          position: map.getCenter(),
          icon: icon
        });

        const styledMapType = new google.maps.StyledMapType(JSON.parse(this.parentNode.querySelector('[data-gmap-style]').innerHTML));

        map.mapTypes.set('styled_map', styledMapType);
        map.setMapTypeId('styled_map');
      }
    });
  }
}
customElements.define('g-map', GMap);

class GMapLocations extends HTMLUListElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
    this.buttons.forEach((button, index) => button.addEventListener('click', () => this.selectedIndex = index));

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => this.selectedIndex = this.buttons.indexOf(event.target));
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.buttons.length - 1).toString());
  }

  get buttons() {
    return this._buttons = this._buttons || Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons.forEach((button, index) => {
      button.classList.toggle('active', index === parseInt(newValue));
      button.querySelector('[data-map-button]')?.classList.toggle('button--secondary', index !== parseInt(newValue));
    });

    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      if (this.controlledElement) {
        const button = this.buttons[parseInt(newValue)];
        this.controlledElement.setAttribute('data-map-address', button.getAttribute('data-map-address'));
        this.controlledElement.initMap();
      }
    }
  }
}
customElements.define('g-map-locations', GMapLocations, { extends: 'ul' });

class PreviousButton extends HoverButton {
  constructor() {
    super();

    this.load();
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  load() {
    this.onClickListener = this.onClick.bind(this);
    this.addEventListener('click', this.onClickListener);

    if (this.controlledElement) {
      this.updateStatusListener = this.updateStatus.bind(this);
      this.controlledElement.addEventListener('slider:previousStatus', this.updateStatusListener);
    }
  }

  unload() {
    this.removeEventListener('click', this.onClickListener);
    if (this.controlledElement) {
      this.controlledElement.removeEventListener('slider:previousStatus', this.updateStatusListener);
    }
  }

  onClick() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:previous', { bubbles: true, cancelable: true }));
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case 'hidden':
        this.hidden = true;
        break;

      case 'disabled':
        this.disabled = true;
        break;

      default:
        this.hidden = false;
        this.disabled = false;
    }
  }
}
customElements.define('previous-button', PreviousButton, { extends: 'button' });

class NextButton extends HoverButton {
  constructor() {
    super();

    this.load();
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  load() {
    this.onClickListener = this.onClick.bind(this);
    this.addEventListener('click', this.onClickListener);

    if (this.controlledElement) {
      this.updateStatusListener = this.updateStatus.bind(this);
      this.controlledElement.addEventListener('slider:nextStatus', this.updateStatusListener);
    }
  }

  unload() {
    this.removeEventListener('click', this.onClickListener);
    if (this.controlledElement) {
      this.controlledElement.removeEventListener('slider:nextStatus', this.updateStatusListener);
    }
  }

  onClick() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:next', { bubbles: true, cancelable: true }));
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case 'hidden':
        this.hidden = true;
        break;

      case 'disabled':
        this.disabled = true;
        break;

      default:
        this.hidden = false;
        this.disabled = false;
    }
  }
}
customElements.define('next-button', NextButton, { extends: 'button' });

class SliderElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get looping() {
    return this.hasAttribute('looping') || false;
  }

  get items() {
    return this._items = this._items || Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children).filter(el => !el.hasAttribute('data-cloned'));
  }

  get itemsToShow() {
    if (!this._cachedItemsToShow) {
      this._cachedItemsToShow = Array.from(this.items).filter(element => element.clientWidth > 0);
    }
    return this._cachedItemsToShow;
  }

  get itemOffset() {
    if (this._cachedItemOffset !== undefined && this._cachedItemOffset !== null) {
      return this._cachedItemOffset;
    }
    const items = this.itemsToShow;
    if (items.length <= 1) {
      this._cachedItemOffset = 0;
      return 0;
    }
    const firstLeft = items[0].offsetLeft;
    let offset = items[0].clientWidth;
    for (let i = 1; i < items.length; i++) {
      const left = items[i].offsetLeft;
      const diff = theme.config.rtl ? firstLeft - left : left - firstLeft;
      if (Math.abs(diff) > 1) {
        offset = diff;
        break;
      }
    }
    this._cachedItemOffset = offset;
    return offset;
  }

  get perPage() {
    if (this._cachedPerPage !== undefined && this._cachedPerPage !== null) {
      return this._cachedPerPage;
    }
    let elementWidth = this.clientWidth;
    const mql = window.matchMedia('screen and (min-width: 1280px)');
    if (mql.matches) {
      const styles = window.getComputedStyle(this);
      elementWidth = this.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
    }
    const pageVal = Math.floor(elementWidth / this.itemOffset);
    this._cachedPerPage = pageVal;
    return pageVal;
  }

  get isScrollable() {
    const style = window.getComputedStyle(this);
    const hasScrollableOverflow = style.overflow === 'scroll' || style.overflow === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'auto' || style.overflowX === 'scroll' || style.overflowX === 'auto';
    const hasScrollableContent = this.scrollHeight > this.clientHeight || this.scrollWidth > this.clientWidth;

    return hasScrollableOverflow && hasScrollableContent;
  }

  reset() {
    this._items = Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children).filter(el => !el.hasAttribute('data-cloned'));
    this._cachedItemsToShow = null;
    this._cachedItemOffset = null;
    this._cachedPerPage = null;
  }

  disconnectedCallback() {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
    if (this._globalKeydownHandler) {
      document.removeEventListener('keydown', this._globalKeydownHandler);
    }
    // Tab panels and filtered grids replace their sliders wholesale; a live
    // observer on a detached rail would leak.
    if (this._warmObserver) {
      this._warmObserver.disconnect();
      this._warmObserver = null;
    }
  }

  // Load the images of slides that are close to the scroll position but not
  // on screen yet, so swiping never lands on an empty card. Only for an actual
  // RAIL: when the same markup is laid out as a grid every slide is already
  // visible, and warming there would just pull the whole grid eagerly.
  warmUpcomingSlides() {
    if (!('IntersectionObserver' in window)) return;
    if (this.scrollWidth <= this.clientWidth + 1) return;

    const slides = this.items;
    if (!slides.length) return;

    // The root is the rail itself, so only horizontal position counts. A
    // margin of one-and-a-half rail widths keeps roughly a screen and a half
    // of runway warmed ahead of (and behind) the shopper.
    this._warmObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        this._warmObserver.unobserve(entry.target);
        theme.utils.warmImages(entry.target);
      });
    }, { root: this, rootMargin: '0px 150% 0px 150%' });

    slides.forEach((slide) => this._warmObserver.observe(slide));
  }

  init() {
    this.hasPendingOnScroll = false;
    this.currentPage = 1;
    this._cachedItemsToShow = null;
    this._cachedItemOffset = null;
    this._cachedPerPage = null;

    this._resizeHandler = () => {
      this._cachedItemsToShow = null;
      this._cachedItemOffset = null;
      this._cachedPerPage = null;
    };
    window.addEventListener('resize', this._resizeHandler, { passive: true });

    this.updateButtons();
    this.updateTabindex();
    this.warmUpcomingSlides();

    // Mark the item on screen as selected from the start (v2.10.42), not only after the
    // first page change: the product gallery paints its selected photo on top so no sliver
    // of a neighbour shows at the edges. Sections that mark their first item in Liquid
    // (timeline) get the same state they already had.
    const startOffset = this.itemOffset;
    const startPage = startOffset > 0 ? Math.round(Math.abs(this.scrollLeft) / startOffset) + 1 : 1;
    this.itemsToShow.forEach((sliderItem, index) => {
      sliderItem.classList.toggle('selected', index + 1 === startPage);
    });

    this.addEventListener('scroll', theme.utils.debounce(this.update.bind(this), 50));
    this.addEventListener('scrollend', this.scrollend.bind(this));
    this.addEventListener('slider:previous', this.previous);
    this.addEventListener('slider:next', this.next);

    this._globalKeydownHandler = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target.closest('input, textarea, select')) return;

      const isFocused = this.contains(document.activeElement);
      const isHovered = this.matches(':hover');

      if (isFocused || isHovered) {
        event.preventDefault();
        if (event.key === 'ArrowLeft') {
          this.previous();
        } else {
          this.next();
        }
      }
    };
    document.addEventListener('keydown', this._globalKeydownHandler);
    document.addEventListener('matchSmall', this.updateTabindex.bind(this));
    document.addEventListener('unmatchSmall', this.updateTabindex.bind(this));

    if (Shopify.designMode && !this.closest('product-info')) {
      this.addEventListener('shopify:block:select', (event) => event.target.scrollIntoView({ behavior: 'smooth' }));
    }

  }

  previous() {
    const step = this.itemOffset * (theme.config.rtl ? -1 : 1);
    if (this.looping) {
      this.scrollPosition = this.scrollLeft - step;
      this.scrollToPosition(this.scrollPosition);
      return;
    }
    if (this.currentPage === 1) return;
    if (this.variableWidths) {
      this.select(this.currentPage - 1);
      return;
    }
    this.scrollPosition = this.scrollLeft - step;
    this.scrollToPosition(this.scrollPosition);
  }

  next() {
    const step = this.itemOffset * (theme.config.rtl ? -1 : 1);
    if (this.looping) {
      this.scrollPosition = this.scrollLeft + step;
      this.scrollToPosition(this.scrollPosition);
      return;
    }
    if (this.currentPage === this.itemsToShow.length) return;
    if (this.variableWidths) {
      this.select(this.currentPage + 1);
      return;
    }
    this.scrollPosition = this.scrollLeft + step;
    this.scrollToPosition(this.scrollPosition);
  }

  // Slides of different widths (Runway 1 + 2, v2.10.65: the first photo full width,
  // the rest half): pages are counted by each slide's own position, not one fixed
  // step. Sliders whose slides are all one width never take this path.
  get variableWidths() {
    const items = this.itemsToShow;
    return items.length > 2 && Math.abs(items[0].clientWidth - items[1].clientWidth) > 2;
  }

  slideOffset(item) {
    return Math.abs(item.offsetLeft - this.itemsToShow[0].offsetLeft);
  }

  select(selectedIndex, immediate = false) {
    const item = this.itemsToShow[selectedIndex - 1];
    if (!item) return;
    this.requestedPage = selectedIndex;
    // Scroll to the slide's own position (v2.10.65). For slides of one width this is
    // where the old "current page minus n steps" landed at rest; it also holds at the
    // end of a two-up rail, where the current page is ahead of the scroll position.
    this.scrollPosition = this.slideOffset(item) * (theme.config.rtl ? -1 : 1);

    // At the end of the rail the slide asked for can already be on screen: nothing
    // scrolls, so no scroll event would mark it current. Mark it here.
    const max = this.scrollWidth - this.clientWidth;
    const target = Math.min(max, Math.max(0, Math.abs(this.scrollPosition)));
    if (Math.abs(target - Math.abs(this.scrollLeft)) < 2) {
      this.update();
      return;
    }
    this.scrollToPosition(this.scrollPosition, immediate);
  }

  // At the end of the rail the last slides share the screen and the rail cannot scroll
  // further, so the slide that was asked for (thumbnail, arrow key, dot) is the current
  // one (v2.10.65; a two-up rail could never mark its last photo). Scrolling by hand
  // forgets the request.
  endOfRailPage() {
    if (!this.requestedPage) return 0;
    const max = this.scrollWidth - this.clientWidth;
    if (Math.abs(this.scrollLeft) < max - 2) {
      // Still travelling to the end after a select, or scrolled away from it by hand.
      if (!this.hasPendingOnScroll) this.requestedPage = null;
      return 0;
    }
    const item = this.itemsToShow[this.requestedPage - 1];
    return item && this.slideOffset(item) >= max - 2 ? this.requestedPage : 0;
  }

  // The page a variable-width slider is on: the slide nearest the left edge.
  variablePage() {
    const items = this.itemsToShow;
    const x = Math.abs(this.scrollLeft);
    let best = 0;
    items.forEach((item, index) => {
      if (Math.abs(this.slideOffset(item) - x) < Math.abs(this.slideOffset(items[best]) - x)) best = index;
    });
    return best + 1;
  }

  selected(selectedIndex) {
    return this.itemsToShow[selectedIndex];
  }

  scrollend() {
    this.hasPendingOnScroll = false;
    this.dispatchEventHandler();
  }

  update() {
    if (window.onscrollend === void 0) {
      clearTimeout(this.scrollendTimeout);
    }

    const previousPage = this.currentPage;
    const activeOffset = this.itemOffset;
    this.currentPage = this.endOfRailPage() || (this.variableWidths
      ? this.variablePage()
      : activeOffset > 0 ? Math.round(Math.abs(this.scrollLeft) / activeOffset) + 1 : 1);

    if (this.currentPage !== previousPage) {
      if (!this.hasPendingOnScroll) {
        this.dispatchEventHandler();
      }

      this.itemsToShow.forEach((sliderItem, index) => {
        sliderItem.classList.toggle('selected', index + 1 === this.currentPage);
      });
    }

    if (window.onscrollend === void 0) {
      this.scrollendTimeout = setTimeout(() => {
        this.dispatchEvent(new CustomEvent('scrollend', { bubbles: true, composed: true }));
      }, 75);
    }

    this.updateButtons();
  }

  updateButtons() {
    let previousDisabled = this.currentPage === 1;
    let nextDisabled = this.currentPage === this.itemsToShow.length;

    if (this.perPage > 1) {
      previousDisabled = previousDisabled || this.itemsToShow.length > 0 && this.isVisible(this.itemsToShow[0]) && this.scrollLeft === 0;
      nextDisabled = nextDisabled || this.itemsToShow.length > 0 && this.isVisible(this.itemsToShow[this.itemsToShow.length - 1]);
    }

    if (this.looping) {
      previousDisabled = false;
      nextDisabled = false;
    }

    this.dispatchEvent(
      new CustomEvent('slider:previousStatus', {
        bubbles: true,
        detail: {
          status: previousDisabled ? (nextDisabled ? 'hidden' : 'disabled') : 'visible'
        },
      })
    );
    this.dispatchEvent(
      new CustomEvent('slider:nextStatus', {
        bubbles: true,
        detail: {
          status: nextDisabled ? (previousDisabled ? 'hidden' : 'disabled') : 'visible'
        },
      })
    );
  }

  updateTabindex() {
    this.removeAttribute('tabindex');

    if (this.isScrollable) {
      this.setAttribute('tabindex', '0');
    }
  }

  isVisible(element, offset = 0) {
    const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.scrollLeft;

    /*
    const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
    const offsetLeft = element.getBoundingClientRect().left;
    return offsetLeft + element.clientWidth <= lastVisibleSlide && offsetLeft >= this.scrollLeft;
    */
  }

  scrollToPosition(position, immediate = false) {
    this.hasPendingOnScroll = !immediate;

    this.scrollTo({
      left: position,
      behavior: immediate ? 'instant' : theme.config.motionReduced ? 'auto' : 'smooth'
    });
  }

  dispatchEventHandler() {
    this.dispatchEvent(
      new CustomEvent('slider:change', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.itemsToShow[this.currentPage - 1],
        },
      })
    );
  }
}
customElements.define('slider-element', SliderElement);

// Gallery image counter "4 | 7" (v2.10.45): follows the slider it controls,
// counting only the photos shown (variant-hidden images are left out).
class MediaCounter extends HTMLElement {
  connectedCallback() {
    this.onChange = this.onChange || ((event) => this.update(event));
    // Live while swiping (v2.10.51): the number follows the photo nearest the
    // slider's start edge on every scroll frame, not only when the slide settles.
    this.onScroll = this.onScroll || (() => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateFromScroll();
      });
    });
    this.slider?.addEventListener('slider:change', this.onChange);
    this.slider?.addEventListener('scroll', this.onScroll, { passive: true });
    requestAnimationFrame(() => this.update());
  }

  disconnectedCallback() {
    this.slider?.removeEventListener('slider:change', this.onChange);
    this.slider?.removeEventListener('scroll', this.onScroll);
  }

  updateFromScroll() {
    const index = this.nearestIndex();
    if (index >= 0) this.setCurrent(index + 1);
  }

  // The photo nearest the slider's start edge, or -1 before layout. The scroll
  // position is the truth: slider:change can still name the previous photo for
  // a frame after a swipe settles, which made the rolling number play twice.
  nearestIndex() {
    const slider = this.slider;
    if (!slider || !slider.itemsToShow || !slider.clientWidth) return -1;
    const shown = slider.itemsToShow;
    const edge = slider.getBoundingClientRect();
    const rtl = getComputedStyle(slider).direction === 'rtl';
    let best = 0;
    let bestDistance = Infinity;
    shown.forEach((item, index) => {
      const box = item.getBoundingClientRect();
      const distance = Math.abs(rtl ? box.right - edge.right : box.left - edge.left);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  // The number rolls in the direction of travel with the cart count's pop
  // (v2.10.72), and only when it actually changes — scroll frames repeat it.
  setCurrent(value) {
    const current = this.querySelector('[data-current]');
    const previous = parseInt(current.textContent, 10) || 0;
    if (previous === value) return;
    current.textContent = value;
    if (!this.ready) return;
    current.classList.remove('is-rolling-next', 'is-rolling-prev');
    void current.offsetWidth;
    current.classList.add(value > previous ? 'is-rolling-next' : 'is-rolling-prev');
  }

  get slider() {
    return document.getElementById(this.getAttribute('aria-controls'));
  }

  update(event) {
    const slider = this.slider;
    if (!slider || !slider.itemsToShow) return;
    const shown = slider.itemsToShow;
    let current = 1;
    const element = event?.detail?.currentElement;
    if (element && shown.indexOf(element) >= 0) {
      current = shown.indexOf(element) + 1;
    } else if (event?.detail?.currentPage) {
      current = parseInt(event.detail.currentPage, 10) || 1;
    } else {
      const selected = shown.findIndex((item) => item.classList.contains('selected'));
      if (selected >= 0) current = selected + 1;
    }
    const nearest = this.nearestIndex();
    if (nearest >= 0) current = nearest + 1;
    this.setCurrent(current);
    this.querySelector('[data-total]').textContent = shown.length;
    this.hidden = shown.length < 2;
    // The first count on load is set, not animated.
    this.ready = true;
  }
}
customElements.define('media-counter', MediaCounter);

class SliderDots extends HTMLElement {
  constructor() {
    super();

    new theme.initWhenVisible(this.init.bind(this));
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get itemsToShow() {
    return Array.from(this.items).filter(element => element.clientWidth > 0);
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (this.controlledElement) {
      this.controlledElement.addEventListener('slider:change', this.onChange.bind(this));

      this.items.forEach((item) => {
        item.addEventListener('click', this.onButtonClick.bind(this));
      });
    }
  }

  reset() {
    this._items = Array.from(this.children);
  }

  onChange(event) {
    const shown = this.itemsToShow;
    const visibleIndex = shown.findIndex((item) => parseInt(item.getAttribute('data-index')) === parseInt(event.detail.currentPage));
    this.transitionTo((visibleIndex >= 0 ? visibleIndex : parseInt(event.detail.currentPage)) - 1);

    shown.forEach((item) => {
      item.setAttribute('aria-current', parseInt(item.getAttribute('data-index')) === parseInt(event.detail.currentPage) ? 'true' : 'false');
    });
  }

  transitionTo(selectedIndex, immediate = false) {
    if (this.itemsToShow[selectedIndex]) {
      const scrollElement = this.hasAttribute('align-selected') ? this.closest(this.getAttribute('align-selected')) : this;
      scrollElement?.scrollTo({
        left: this.itemsToShow[selectedIndex].offsetLeft - scrollElement.clientWidth / 2 + this.itemsToShow[selectedIndex].clientWidth / 2,
        top: this.itemsToShow[selectedIndex].offsetTop - scrollElement.clientHeight / 2 - this.itemsToShow[selectedIndex].clientHeight / 2,
        behavior: immediate ? 'instant' : theme.config.motionReduced ? 'auto' : 'smooth'
      });
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.currentTarget;

    this.itemsToShow.forEach((item) => {
      item.setAttribute('aria-current', item.getAttribute('data-index') === target.getAttribute('data-index') ? 'true' : 'false');
    });

    this.controlledElement.select(parseInt(target.getAttribute('data-index')));
  }
}
customElements.define('slider-dots', SliderDots);

// <product-slider> — pairs two carousel-element rails so each lifestyle image
// stays beside its own product card.
//
// Built on carousel-element (Flickity), which is where the theme's smooth drag
// lives — the same thing shop-the-look, the marquee and the 3D carousel use.
// An earlier version paired two scroll-snap slider-elements and hand-rolled a
// pointer drag on top; that meant no momentum, and it needed a pile of
// arithmetic to stay in sync, because slider-element's select() is RELATIVE to
// a currentPage that is itself re-derived from scrollLeft on every scroll tick.
// Flickity indexes absolutely, so the pairing is just the two listeners below —
// the same shape as ShopTheLook further down this file.
//
// Indices here are 0-based, matching Flickity.
class ProductSlider extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.media = document.getElementById(this.getAttribute('media-rail'));
    this.products = document.getElementById(this.getAttribute('product-rail'));
    if (!this.media || !this.products) return;

    this.index = 0;

    this.media.addEventListener('carousel:change', (event) => this.adopt(this.products, event.detail.index));
    this.products.addEventListener('carousel:change', (event) => this.adopt(this.media, event.detail.index));

    // The prev/next buttons carry no aria-controls, so their events bubble to
    // here instead of driving one rail directly. Flickity wraps around, so
    // there is no disabled end state for them to track either.
    this.addEventListener('slider:previous', () => this.step(-1));
    this.addEventListener('slider:next', () => this.step(1));

    this.initAutoplay();
  }

  disconnectedCallback() {
    this.stop();
    if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
  }

  get count() {
    return this.media ? this.media.items.length : 0;
  }

  // carousel-element only builds its Flickity once it scrolls into view, and
  // never at all for a single slide — so select() is not safe to call blind.
  selectOn(rail, index) {
    if (rail && rail.carousel) rail.select(index);
  }

  // One rail moved on its own (drag, dots, buttons, block select in the editor).
  // Take its index and bring the other along; an echo of our own move is the
  // index we already hold, and stops here.
  adopt(other, index) {
    if (typeof index !== 'number' || index === this.index) return;

    this.index = index;
    this.selectOn(other, index);
  }

  goTo(index) {
    const total = this.count;
    if (total < 2) return;

    this.index = ((index % total) + total) % total;
    this.selectOn(this.media, this.index);
    this.selectOn(this.products, this.index);
  }

  step(delta) {
    this.goTo(this.index + delta);
  }

  // --- autoplay -----------------------------------------------------------
  get delay() {
    return parseFloat(this.getAttribute('autoplay') || 0) * 1000;
  }

  initAutoplay() {
    if (!this.delay || theme.config.motionReduced) return;

    this._onVisibility = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this._onVisibility);

    if (this.hasAttribute('pause-on-hover')) {
      this.addEventListener('pointerenter', this.stop.bind(this));
      this.addEventListener('pointerleave', this.start.bind(this));
    }
    // Keyboard users get the same courtesy as mouse users: a rail that moves
    // under a focused card is unusable.
    this.addEventListener('focusin', this.stop.bind(this));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.start();
    });

    // Only run while the section is on screen. Motion's inView calls the
    // returned function on leave and re-runs the callback on re-entry.
    Motion.inView(this, () => {
      this.start();
      return () => this.stop();
    });
  }

  start() {
    if (this.timer || !this.delay || document.hidden) return;
    this.timer = setInterval(() => this.step(1), this.delay);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }
}
customElements.define('product-slider', ProductSlider);

class ProgressBar extends HTMLElement {
  constructor() {
    super();

    if (this.hasAttribute('style')) return;

    Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.style.setProperty('--progress', `${parseInt(this.getAttribute('data-value')) * 0.75 * 100 / parseInt(this.getAttribute('data-max'))}%`);
  }
}
customElements.define('progress-bar', ProgressBar);

const onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});
class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    if (this.posterElement) {
      this.posterElement.addEventListener('click', this.onPosterClick.bind(this));
    }

    if (this.autoplay) {
      // Two-stage load so a below-the-fold video is READY on arrival without
      // playing off-screen in the background:
      //  1. ~1 viewport ahead (bottom rootMargin 100%): pre-buffer the clip —
      //     inject the <video> with preload=auto and autoplay stripped, so it
      //     downloads while the user is still scrolling toward it, but does NOT
      //     play.
      //  2. On actual viewport entry (no margin): start playback of the
      //     already-buffered clip, so it begins instantly and only while visible.
      // Above-the-fold videos are intersecting at load, so both fire immediately.
      // Hosted YouTube/Vimeo embeds can't buffer without playing, so they skip
      // stage 1 and simply start on entry.
      Motion.inView(this, () => this.prepare(), { margin: '0px 0px 100% 0px' });
      Motion.inView(this, this.init.bind(this));
    }
  }

  // Stage 1: pre-buffer a self-hosted <video> without playing it. Swapping the
  // inert <template> for a real <video> (autoplay removed, preload=auto) makes
  // the browser download the clip; playback is left to init() on viewport entry.
  prepare() {
    if (this._prepared) return;

    // Skip videos already on screen (e.g. the hero): they play immediately via
    // init() anyway, so forcing a full preload=auto download here would only pile
    // onto the initial page load. Let them use their normal (metadata) preload.
    const rect = this.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) return;

    this._prepared = true;

    // Defer the buffer download until the page has finished loading so it never
    // competes with the initial render / LCP. Once loaded, buffer straight away.
    if (document.readyState === 'complete') {
      this.bufferVideo();
    } else {
      window.addEventListener('load', () => this.bufferVideo(), { once: true });
    }
  }

  bufferVideo() {
    const templateElement = this.querySelector('template');
    if (!templateElement) return;
    const node = templateElement.content.firstElementChild;
    if (!node || node.tagName !== 'VIDEO') return; // only native video buffers cleanly

    const video = node.cloneNode(true);
    video.removeAttribute('autoplay'); // download, but don't play yet
    video.setAttribute('preload', 'auto');
    if (typeof this.setLoadedState === 'function') {
      video.addEventListener('loadedmetadata', () => this.setLoadedState(), { once: true });
    }
    templateElement.replaceWith(video);
  }

  get posterElement() {
    return this.querySelector('[id^="DeferredPoster-"]');
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get playing() {
    return this.hasAttribute('playing');
  }

  get player() {
    return this.playerProxy = this.playerProxy || new Proxy(this.playerTarget(), {
      get: (target, prop) => {
        return async () => {
          target = await target;
          this.playerHandler(target, prop);
        };
      }
    });
  }

  static get observedAttributes() {
    return ['playing'];
  }

  onPosterClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.playing) {
      this.paused = true;
      this.pause();
    }
    else {
      this.paused = false;
      this.play();
    }
  }

  init() {
    if (!this.paused) {
      this.play();
    }
    return () => {
      this.pause();
    };
  }

  play() {
    if (!this.playing) {
      this.player.play();
    }
  }

  pause() {
    if (this.playing) {
      this.player.pause();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'playing') {
      if (oldValue === null && newValue === '') {
        return this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }));
      }

      if (newValue === null) {
        return this.dispatchEvent(new CustomEvent('video:pause', { bubbles: true }));
      }
    }
  }
}

class VideoMedia extends DeferredMedia {
  constructor() {
    super();
  }

  connectedCallback() {
    if (this.getAttribute('host') === 'vimeo') {
      const videoId = this.getAttribute('video-id');
      const placeholderSvg = this.querySelector('svg.placeholder');
      
      if (videoId && placeholderSvg) {
        fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`)
          .then(res => res.json())
          .then(data => {
            if (data && data.thumbnail_url) {
              const img = document.createElement('img');
              img.src = data.thumbnail_url;
              img.alt = this.getAttribute('aria-label') || 'Vimeo video poster';
              img.style.position = 'absolute';
              img.style.top = '0';
              img.style.left = '0';
              img.style.width = '100%';
              img.style.height = '100%';
              img.style.objectFit = 'cover';
              img.style.opacity = '0';
              img.style.transition = 'opacity 0.3s ease';
              img.onload = () => {
                img.style.opacity = '1';
                placeholderSvg.style.display = 'none';
              };
              this.insertBefore(img, placeholderSvg);
            }
          })
          .catch(err => console.error('Failed to load Vimeo thumbnail', err));
      }
    }
  }

  playerTarget() {
    if (this.hasAttribute('host')) {
      this.setAttribute('loaded', '');
      this.closest('.media')?.classList.remove('loading');

      return new Promise(async (resolve) => {
        const templateElement = this.querySelector('template');
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
        }
        const muteVideo = this.hasAttribute('autoplay') || window.matchMedia('screen and (max-width: 1023px)').matches;
        const script = document.createElement('script');
        script.type = 'text/javascript';
        if (this.getAttribute('host') === 'youtube') {
          if (!window.YT || !window.YT.Player) {
            script.src = 'https://www.youtube.com/iframe_api';
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          await onYouTubePromise;
          const player = new YT.Player(this.querySelector('iframe'), {
            events: {
              onReady: () => {
                if (muteVideo) {
                  player.mute();
                }
                resolve(player);
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute('playing', '');
                }
                else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute('playing');
                }
              }
            }
          });
        }
        if (this.getAttribute('host') === 'vimeo') {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = 'https://player.vimeo.com/api/player.js';
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          const player = new Vimeo.Player(this.querySelector('iframe'));
          if (muteVideo) {
            player.setMuted(true);
          }
          player.on('play', () => {
            this.setAttribute('playing', '');
          });
          player.on('pause', () => this.removeAttribute('playing'));
          player.on('ended', () => this.removeAttribute('playing'));
          resolve(player);
        }
      });
    }
    else {
      let video;
      const templateElement = this.querySelector('template');

      if (templateElement) {
        video = templateElement.content.firstElementChild.cloneNode(true);
        video.addEventListener('loadedmetadata', () => this.setLoadedState(), { once: true });
        templateElement.replaceWith(video);
      } else {
        video = this.querySelector('video');
      }

      if (video && !templateElement) {
        if (video.readyState >= 1) {
          this.setLoadedState();
        } else {
          video.addEventListener('loadedmetadata', () => this.setLoadedState(), { once: true });
        }
      }

      const player = video;
      if (!player) return;

      player.addEventListener('play', () => {
        this.setAttribute('playing', '');
        this.removeAttribute('suspended');
      });

      player.addEventListener('pause', () => {
        if (!player.seeking && player.paused) {
          this.removeAttribute('playing');
        }
      });

      player.addEventListener('canplay', () => {
        if (this.hasAttribute('autoplay')) {
          requestAnimationFrame(() => {
            player.play().catch((error) => {
              if (error.name === 'NotAllowedError') {
                this.setAttribute('suspended', '');
                const replacementImageSrc = player.previousElementSibling?.currentSrc;
                if (replacementImageSrc) {
                  player.poster = replacementImageSrc;
                }
              }
            });
          });
        }
      }, { once: true });

      return player;
    }
  }

  playerHandler(target, prop) {
    if (this.getAttribute('host') === 'youtube') {
      if (target && target.playVideo) {
        prop === 'play' ? target.playVideo() : target.pauseVideo();
      }
    }
    else {
      if (prop === 'play' && !this.hasAttribute('host')) {
        target.play().catch((error) => {
          if (error.name === 'NotAllowedError') {
            this.setAttribute('suspended', '');
            const replacementImageSrc = target.previousElementSibling?.currentSrc;
            if (replacementImageSrc) {
              target.poster = replacementImageSrc;
            }
          }
        });
      }
      else {
        target[prop]();
      }
    }
  }

  setLoadedState() {
    this.setAttribute('loaded', '');
    this.closest('.media')?.classList.remove('loading');
  }
}
customElements.define('video-media', VideoMedia);

class ModelMedia extends DeferredMedia {
  constructor() {
    super();

    this.player;

    if (this.closeElement) {
      this.closeElement.addEventListener('click', this.onCloseClick.bind(this));
    }
  }

  get closeElement() {
    return this.querySelector('[id^="DeferredPosterClose-"]');
  }

  onCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.modelViewerUI) {
      this.modelViewerUI.pause();
    }
  }

  playerTarget() {
    return new Promise(() => {
      this.setAttribute('loaded', '');

      Shopify.loadFeatures([
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: this.setupShopifyXR.bind(this),
        },
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this.setupModelViewerUI.bind(this),
        },
      ]);
    });
  }

  playerHandler(target, prop) {
    target[prop]();
  }

  async setupShopifyXR() {
    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', this.setupShopifyXR.bind(this));
      return;
    }

    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  }

  setupModelViewerUI(errors) {
    if (errors) return;

    const modelViewer = this.querySelector('model-viewer');
    if (modelViewer && !modelViewer.hasAttribute('loaded')) {
      modelViewer.setAttribute('loaded', '');
      modelViewer.addEventListener('shopify_model_viewer_ui_toggle_play', this.modelViewerPlayed.bind(this));
      modelViewer.addEventListener('shopify_model_viewer_ui_toggle_pause', this.modelViewerPaused.bind(this));

      this.modelViewerUI = new Shopify.ModelViewerUI(modelViewer);
    }
  }

  modelViewerPlayed() {
    this.setAttribute('playing', '');
    this.closeElement.removeAttribute('hidden');
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:play', { bubbles: true }));
  }

  modelViewerPaused() {
    this.removeAttribute('playing');
    this.closeElement.setAttribute('hidden', '');
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:pause', { bubbles: true }));
  }
}
customElements.define('model-media', ModelMedia);

class VariantPicker extends HTMLElement {
  constructor() {
    super();
    this.currentVariant = null;
    this.cachedVariantData = null;
    this.currentOptions = [];
    this.handleVariantChange = this.handleVariantChange.bind(this);
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    ).map((el) => el.getAttribute('data-option-value-id'));
  }

  connectedCallback() {
    this.addEventListener('change', this.handleVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleVariantChange);
  }

  handleVariantChange(event) {
    const target = event.target;

    if (this.hasAttribute('data-dynamic-variants')) {
      this.syncCurrentOptions(target);
      this.findAndSetCurrentVariant(target);
      this.updateAvailableOptions(target);
    }

    this.markSelectedOption(target);
    this.publishSelectionChange(event, this.getActualInput(target));
  }

  markSelectedOption(target) {
    if (target.tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options).find((opt) => opt.hasAttribute('selected'))?.removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', '');
    }
  }

  getActualInput(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  getVariantData() {
    if (this.cachedVariantData) return this.cachedVariantData;

    const data = this.querySelector('[data-variants]')?.textContent;
    return this.cachedVariantData = data ? JSON.parse(data) : [];
  }

  findAndSetCurrentVariant(target) {
    this.currentVariant = this.findVariantByOptions(true) ||
      this.findClosestAvailableVariant(target) ||
      this.findVariantByOptions(false) ||
      null;
  }

  findVariantByOptions(requireAvailability) {
    return this.getVariantData().find((variant) => {
      const matches = this.currentOptions.every((val, i) => variant[`option${i + 1}`] === val);
      return requireAvailability ? matches && variant.available : matches;
    });
  }

  findClosestAvailableVariant(lastSelectedOption) {
    if (!lastSelectedOption) return null;

    const lastIndex = parseInt(lastSelectedOption.getAttribute('data-option-index'));
    const matches = this.getVariantData().filter((variant) =>
      variant.available &&
      this.currentOptions.slice(0, lastIndex).every((val, i) => variant[`option${i + 1}`] === val)
    );

    return matches.reduce((best, variant) => {
      if (!best) return variant;
      const bestScore = this.currentOptions.reduce((sum, val, i) =>
        best[`option${i + 1}`] === val ? sum + (3 - i) : sum, 0);
      const currentScore = this.currentOptions.reduce((sum, val, i) =>
        variant[`option${i + 1}`] === val ? sum + (3 - i) : sum, 0);
      return currentScore > bestScore ? variant : best;
    }, null);
  }

  syncCurrentOptions(target) {
    this.currentOptions = Array.from(this.querySelectorAll('select, fieldset')).map((el) => {
      if (el.tagName === 'SELECT') return el.value;
      return Array.from(el.querySelectorAll('input')).find((radio) =>
        radio === target || radio.checked
      )?.value;
    });
  }

  updateAvailableOptions(lastSelectedOption) {
    const firstValue = this.querySelector(':checked')?.value;
    if (!firstValue) return;

    const matchingVariants = this.getVariantData().filter((v) => v.option1 === firstValue);
    const groups = [...this.querySelectorAll('fieldset, .variant-wrapper')];

    groups.forEach((group, i) => {
      if (i === 0 || lastSelectedOption.parentElement === group) return;

      const inputs = [...group.querySelectorAll('input[type="radio"], option')];
      const prevValue = groups[i - 1].querySelector(':checked')?.value;

      const available = matchingVariants
        .filter((v) => v.available && v[`option${i}`] === prevValue)
        .map((v) => v[`option${i + 1}`]);

      this.setInputStates(inputs, available);
    });
  }

  setInputStates(inputs, availableValues) {
    inputs.forEach((input) => {
      const index = input.getAttribute('data-option-index');
      const value = input.getAttribute('value');
      const isAvailable = availableValues.includes(value);
      const isCurrent = this.currentVariant?.[`option${index}`] === value;

      if (input.tagName === 'INPUT') {
        input.classList.toggle('disabled', !isAvailable);
        if (isCurrent) input.checked = true;
      } else {
        isCurrent ? input.setAttribute('selected', '') : input.removeAttribute('selected');
      }
    });
  }

  publishSelectionChange(event, target) {
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: { event, target, selectedOptionValues: this.selectedOptionValues }
    });
  }
}
customElements.define('variant-picker', VariantPicker);

class ProductInfo extends HTMLElement {
  onVariantChangeUnsubscriber = undefined;
  cartUpdateUnsubscriber = undefined;
  abortController = undefined;
  pendingRequestUrl = null;
  preProcessHtmlCallbacks = [];
  postProcessHtmlCallbacks = [];

  constructor() {
    super();
  }

  get sectionId() {
    return this.hasAttribute('data-original-section-id') ? this.getAttribute('data-original-section-id') : this.getAttribute('data-section-id');
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get productStickyForm() {
    return document.getElementById(`ProductStickyForm-${this.sectionId}-${this.productId}`);
  }

  get pickupAvailability() {
    return this.querySelector('pickup-availability');
  }

  get variantSelectors() {
    return this.querySelector('variant-picker');
  }

  get quantityInput() {
    return this.querySelector('quantity-input input');
  }

  connectedCallback() {
    this.initProductAnimation();

    this.onVariantChangeUnsubscriber = theme.pubsub.subscribe(
      theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this)
    );

    this.initQuantityHandlers();
    this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
    this.scheduleOptionWarmup();
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber();
    this.cartUpdateUnsubscriber?.();
  }

  initProductAnimation() {
    if (theme.config.motionReduced) return;

    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    Motion.inView(this, async () => {
      if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media);
      this.animation.load();
    });
  }

  handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return;

    const productUrl = target.getAttribute('data-product-url') || this.pendingRequestUrl || this.getAttribute('data-product-url');
    const shouldSwapProduct = this.getAttribute('data-product-url') !== productUrl;
    const requestUrl = shouldSwapProduct ? null : this.buildRequestUrlWithParams(productUrl, selectedOptionValues);

    // A switch already loaded (warm-up, hover, an earlier visit) applies on the next microtask:
    // greying Add to cart for that instant only flashed it (v2.10.92). A real network wait still
    // disables it, so nothing can be added for the old variant.
    if (!requestUrl || !this.sectionCache?.[requestUrl]) this.resetProductFormState();

    this.pendingRequestUrl = productUrl;

    if (shouldSwapProduct) {
      window.location.href = productUrl;
      return;
    }

    this.renderProductInfo({
      requestUrl,
      targetId: target.tagName === 'OPTION' ? target.parentElement.id : target.id,
      callback: this.handleUpdateProductInfo()
    });
  }

  resetProductFormState() {
    this.productForm?.handleErrorMessage();
    this.productForm?.toggleSubmitButton(true, '');
    this.productStickyForm?.handleErrorMessage();
    this.productStickyForm?.toggleSubmitButton(true, '');
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.sectionCache = this.sectionCache || {};
    // Monotonic token so a superseded request (rapid clicking) never applies
    // its result after a newer one — the cached path resolves on a microtask.
    const token = (this._reqToken = (this._reqToken || 0) + 1);

    const proceed = (responseText) => {
      if (token !== this._reqToken) return; // a newer switch already won
      this.pendingRequestUrl = null;
      if (callback !== undefined) {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        callback(parsedHTML);
      }
      // set focus to last clicked option value
      const activeElement = document.getElementById(targetId);
      if (activeElement === null) return;
      if (activeElement.hasAttribute('align-selected')) {
        const scrollElement = activeElement.closest(activeElement.getAttribute('align-selected'));
        scrollElement?.scrollTo({ left: activeElement.offsetLeft, behavior: 'instant' });
      }
      setTimeout(() => {
        activeElement.focus({ preventScroll: true });
      }, 100);
    };

    // Re-clicking a colour you already viewed (or one warmed on hover) reuses
    // the cached section render — no second network round-trip, so the switch
    // is instant. Variant data is stable for the page's lifetime.
    const cached = this.sectionCache[requestUrl];
    if (cached) {
      Promise.resolve().then(() => proceed(cached));
      return;
    }

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.sectionCache[requestUrl] = responseText;
        proceed(responseText);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
        } else {
          console.error(error);
          // The switch failed (network): give the buttons back instead of leaving them
          // disabled in the loading state (v2.10.25).
          if (token === this._reqToken) {
            this.pendingRequestUrl = null;
            this.productForm?.toggleSubmitButton(false);
            this.productStickyForm?.toggleSubmitButton(false);
          }
        }
      });
  }

  // Warm the section-render cache for a variant URL ahead of the click (called
  // on swatch hover / touch), so even the first switch to a colour is instant.
  // Resolves to the section HTML (or null) so the caller can also warm the
  // variant's gallery images.
  prefetchSection(requestUrl) {
    if (!requestUrl) return Promise.resolve(null);
    this.sectionCache = this.sectionCache || {};
    this._prefetching = this._prefetching || {};
    if (this.sectionCache[requestUrl]) return Promise.resolve(this.sectionCache[requestUrl]);
    if (this._prefetching[requestUrl]) return this._prefetching[requestUrl];
    const p = fetch(requestUrl)
      .then((response) => response.text())
      .then((responseText) => { this.sectionCache[requestUrl] = responseText; return responseText; })
      .catch(() => null)
      .then((t) => { delete this._prefetching[requestUrl]; return t; });
    this._prefetching[requestUrl] = p;
    return p;
  }

  buildRequestUrlWithParams(url, optionValues) {
    const params = [];
    params.push(`section_id=${this.sectionId}`);

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }

    return `${url}?${params.join('&')}`;
  }

  handleUpdateProductInfo() {
    return (parsedHTML) => {
      const variant = this.getSelectedVariant(parsedHTML);

      this.updateCatalogState(variant);
      this.pickupAvailability?.update(variant);
      this.updateOptionValues(parsedHTML);
      // The sticky bar's options panel (v2.10.90) shows the new selection and availability,
      // an unavailable combination included.
      this.updateSourceFromDestination(parsedHTML, 'StickyOptions');
      // The sticky bar's photo from the server too (v2.10.94): a variant without its own photo has no
      // featured_media in the variant data, but the server shows its colour's photo.
      this.updateSourceFromDestination(parsedHTML, 'StickyMedia');
      this.updateURL(variant?.id);
      this.updateVariantInputs(variant?.id);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateGalleryFromDestination(parsedHTML, variant);
      this.updateSourceFromDestination(parsedHTML, 'Price');
      this.updateSourceFromDestination(parsedHTML, 'BuyButtonPrice');
      this.updateSourceFromDestination(parsedHTML, 'StickyPrice');
      this.updateSourceFromDestination(parsedHTML, 'Sku');
      this.updateSourceFromDestination(parsedHTML, 'VendorSku');
      this.updateSourceFromDestination(parsedHTML, 'Badges');
      this.updateSourceFromDestination(parsedHTML, 'Inventory');
      this.updateSourceFromDestination(parsedHTML, 'Volume');
      this.updateSourceFromDestination(parsedHTML, 'PricePerItem');
      this.updateSourceFromDestination(parsedHTML, 'BackInStock');
      this.updateSourceFromDestination(parsedHTML, 'ProductBundle');

      this.updateQuantityRules(parsedHTML);
      this.updateSourceFromDestination(parsedHTML, 'QuantityRules');
      this.updateSourceFromDestination(parsedHTML, 'QuantityRulesCart');
      this.updateSourceFromDestination(parsedHTML, 'VolumeNote');

      this.productForm?.toggleSubmitButton(!variant.available, theme.variantStrings.soldOut);
      this.productStickyForm?.toggleSubmitButton(!variant.available, theme.variantStrings.soldOut);

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.sectionId,
          productId: this.productId,
          parsedHTML: parsedHTML,
          variant: variant
        }
      });

      (this.productForm ?? document).dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          variant: variant
        }
      }));

      this.scheduleOptionWarmup();
    };
  }

  // Gallery on a variant change (v2.10.92, the approach of Concept 6 and Dawn): photos are matched
  // by media id and MOVED into the new order instead of rebuilt, so nothing reloads, flashes or
  // loses its state; only photos the new variant adds are new nodes. The current gallery stays on
  // screen until the new first photo has decoded (capped at 1.2s, so the photos never lag the price
  // for long), and a slow network never shows a blank tile. A layout whose lists do not line up falls back to the full swap of before.
  updateGalleryFromDestination(parsedHTML, variant) {
    const source = parsedHTML.getElementById(`ProductGallery-${this.sectionId}-${this.productId}`);
    const destination = document.getElementById(`ProductGallery-${this.sectionId}-${this.productId}`);
    if (!source || !destination) return;

    const token = this._reqToken;
    const swap = () => {
      if (token !== this._reqToken || !destination.isConnected) return; // a newer switch won
      destination.removeAttribute('aria-busy');
      if (source.className && destination.className !== source.className) destination.className = source.className;

      if (this.morphGalleryMedia(source, destination)) {
        const slider = destination.querySelector('slider-element');
        slider?.reset?.();
        slider?.scrollTo?.({ left: 0, top: 0, behavior: 'instant' });
      }
      else {
        destination.innerHTML = source.innerHTML;
      }
      destination.removeAttribute('hidden');
      destination.countMediaGallery?.();
      if (variant?.featured_media?.id) destination.setActiveMedia?.(variant.featured_media.id);
    };

    const firstItem = source.querySelector('[data-media-id]');
    const firstImg = firstItem?.querySelector('img');
    if (!firstImg) return swap();
    const existing = destination.querySelector(`[data-media-id="${firstItem.getAttribute('data-media-id')}"] img`);
    // Already painted on the page (a sharper size may still be loading): no wait.
    if (existing && existing.naturalWidth > 0) return swap();

    destination.setAttribute('aria-busy', 'true');
    const loader = new Image();
    loader.fetchPriority = 'high';
    loader.sizes = firstImg.getAttribute('sizes') || '';
    loader.srcset = firstImg.getAttribute('srcset') || '';
    loader.src = firstImg.getAttribute('src') || '';
    Promise.race([
      loader.decode().catch(() => {}),
      new Promise((resolve) => setTimeout(resolve, 1200))
    ]).then(swap);
  }

  // Keyed reconciliation: each list of media items (the preview, the main list) keeps its nodes —
  // an item with the same media id is moved and its attributes brought up to date. The thumbnails
  // (media-dots) index themselves once when created, so they are replaced whole. Returns false
  // when the two galleries' lists do not line up, and the caller swaps everything.
  morphGalleryMedia(source, destination) {
    const listsOf = (root) => {
      const parents = [];
      root.querySelectorAll('[data-media-id]').forEach((item) => {
        if (!parents.includes(item.parentElement)) parents.push(item.parentElement);
      });
      return parents;
    };
    const sourceLists = listsOf(source);
    const destinationLists = listsOf(destination);
    if (!sourceLists.length || sourceLists.length !== destinationLists.length) return false;

    const pairs = [];
    for (let i = 0; i < sourceLists.length; i++) {
      const sourceList = sourceLists[i];
      const destinationList = destinationLists[i];
      if (sourceList.tagName !== destinationList.tagName || sourceList.className !== destinationList.className) return false;
      const sourceDots = sourceList.closest('media-dots');
      const destinationDots = destinationList.closest('media-dots');
      if (!!sourceDots !== !!destinationDots) return false;
      pairs.push(sourceDots ? { oldDots: destinationDots, newDots: sourceDots } : { sourceList, destinationList });
    }

    pairs.forEach((pair) => {
      if (pair.newDots) {
        if (pair.oldDots.isConnected) pair.oldDots.replaceWith(pair.newDots);
        return;
      }
      const { sourceList, destinationList } = pair;
      const before = Array.from(destinationList.children);
      const keyed = new Map();
      before.forEach((child) => {
        const id = child.getAttribute('data-media-id');
        if (id) keyed.set(id, child);
      });
      const after = Array.from(sourceList.children).map((incoming) => {
        const id = incoming.getAttribute('data-media-id');
        const kept = id ? keyed.get(id) : null;
        if (!kept) return incoming;
        Array.from(kept.attributes).forEach((attr) => {
          if (!incoming.hasAttribute(attr.name)) kept.removeAttribute(attr.name);
        });
        Array.from(incoming.attributes).forEach((attr) => {
          if (kept.getAttribute(attr.name) !== attr.value) kept.setAttribute(attr.name, attr.value);
        });
        return kept;
      });
      if (after.length === before.length && after.every((node, index) => node === before[index])) return;
      destinationList.replaceChildren(...after);
    });
    return true;
  }

  // Warm-up (v2.10.92, after Concept 6): once the page is idle, load the section for every option
  // value one tap away (up to 12 per round, 40 a page) so the FIRST switch is as quick as a re-click
  // on phones too, where hover never warms anything. The first option's values also warm their
  // first photo. Again after each switch, for the options the new selection opens. Never on
  // Save-Data or 2G, never in quick view.
  scheduleOptionWarmup() {
    if (this.hasAttribute('data-original-section-id')) return;
    const connection = navigator.connection;
    if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) return;
    if (this._warmScheduled) return;
    this._warmScheduled = true;

    const run = () => {
      this._warmScheduled = false;
      this.warmOptionValues();
    };
    // On load the warm-up waits for a quiet moment (up to 4s) so it never competes with the page;
    // after a switch the next tap can come within a second, so that round starts within 0.8s.
    const wait = this._warmedOnce ? 800 : 4000;
    this._warmedOnce = true;
    const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(run, { timeout: wait }) : setTimeout(run, Math.min(wait, 1500)));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  warmOptionValues() {
    const picker = this.variantSelectors;
    const productUrl = this.getAttribute('data-product-url');
    if (!picker || !productUrl) return;
    this._warmCount = this._warmCount || 0;

    // The groups in DOM order, as VariantPicker.selectedOptionValues reads them.
    const groups = Array.from(picker.querySelectorAll('fieldset, select'));
    const selectedOf = (group) => (group.tagName === 'SELECT' ? group.querySelector('option[selected]') : group.querySelector('input:checked'));
    if (!groups.length || groups.some((group) => !selectedOf(group))) return;

    let budget = Math.min(12, 40 - this._warmCount);
    groups.forEach((group, groupIndex) => {
      group.querySelectorAll('input[data-option-value-id], option[data-option-value-id]').forEach((candidate) => {
        if (budget <= 0 || candidate === selectedOf(group) || candidate.hasAttribute('data-product-url')) return;
        const ids = groups.map((other) => (other === group ? candidate : selectedOf(other)).getAttribute('data-option-value-id'));
        const requestUrl = this.buildRequestUrlWithParams(productUrl, ids);
        if (this.sectionCache?.[requestUrl] || this._prefetching?.[requestUrl]) return;
        budget--;
        this._warmCount++;
        const request = this.prefetchSection(requestUrl);
        if (groupIndex === 0) request.then((html) => this.warmFirstPhoto(html));
      });
    });
  }

  warmFirstPhoto(responseText) {
    if (!responseText) return;
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    const img = parsedHTML.getElementById(`ProductGallery-${this.sectionId}-${this.productId}`)?.querySelector('[data-media-id] img');
    if (!img || !img.getAttribute('src')) return;
    const warm = new Image();
    warm.fetchPriority = 'low';
    warm.sizes = img.getAttribute('sizes') || '';
    warm.srcset = img.getAttribute('srcset') || '';
    warm.src = img.getAttribute('src');
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector('[data-selected-variant]')?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  updateOptionValues(parsedHTML) {
    const variantPicker = parsedHTML.getElementById(`VariantPicker-${this.sectionId}-${this.productId}`);
    if (variantPicker) {
      theme.HTMLUpdateUtility.viewTransition(this.variantSelectors, variantPicker, this.preProcessHtmlCallbacks);
    }
  }

  updateURL(variantId) {
    if (!variantId || this.getAttribute('data-update-url') === 'false') return;

    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('variant', variantId);
    window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
  }

  updateVariantInputs(variantId) {
    if (!variantId) return;

    const productForms = document.querySelectorAll(`#ProductForm-${this.sectionId}-${this.productId}, #ProductFormInstallment-${this.sectionId}-${this.productId}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = variantId ?? '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateSourceFromDestination(parsedHTML, id) {
    const source = parsedHTML.getElementById(`${id}-${this.sectionId}-${this.productId}`);
    const destination = document.querySelector(`#${id}-${this.sectionId}-${this.productId}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
      destination.removeAttribute('hidden');
      // The wrapper's own visibility class follows the new variant too (Inventory is
      // sr-only for an untracked variant and visible for a tracked one) (v2.10.25).
      destination.classList.toggle('sr-only', source.classList.contains('sr-only'));
    }
  }

  // Variant-level catalog: a variant flagged with the custom.catalog metafield
  // keeps its real price in Shopify but is hidden + not purchasable on the
  // storefront. The Price fragment re-renders to "Contact for price" via Liquid;
  // here we swap the Add-to-cart form for the enquiry block without re-rendering
  // (so the form isn't torn down on every variant change).
  updateCatalogState(variant) {
    const idsEl = this.querySelector('[data-catalog-variant-ids]');
    if (!idsEl) return;
    let ids = [];
    try {
      ids = JSON.parse(idsEl.textContent) || [];
    } catch (e) {
      ids = [];
    }
    const isCatalog = !!variant && ids.indexOf(variant.id) !== -1;
    this.querySelectorAll('[data-buy-normal]').forEach((el) => el.toggleAttribute('hidden', isCatalog));
    this.querySelectorAll('[data-buy-enquiry]').forEach((el) => el.toggleAttribute('hidden', !isCatalog));
  }

  setUnavailable() {
    this.productForm?.toggleSubmitButton(true, theme.variantStrings.unavailable, true);
    this.productStickyForm?.toggleSubmitButton(true, theme.variantStrings.unavailable, true);

    const selectors = ['Price', 'BuyButtonPrice', 'StickyPrice', 'Inventory', 'Sku', 'VendorSku', 'Badges', 'PricePerItem', 'BackInStock', 'ProductBundle', 'VolumeNote', 'Volume', 'QuantityRules', 'QuantityRulesCart']
      .map((id) => `#${id}-${this.sectionId}-${this.productId}`)
      .join(', ');
    document.querySelectorAll(selectors).forEach((selector) => selector.setAttribute('hidden', ''));
  }

  initQuantityHandlers() {
    if (!this.quantityInput) return;

    this.setQuantityBoundries();
    if (!this.hasAttribute('data-original-section-id')) {
      this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
    }
  }

  setQuantityBoundries() {
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
      data: {
        sectionId: this.sectionId,
        productId: this.productId
      }
    });
  }

  fetchQuantityRules() {
    const currentVariantId = this.productForm?.variantIdInput?.value;
    if (!currentVariantId) return;

    this.querySelector('label[is="quantity-label"]')?.setAttribute('aria-busy', 'true');
    fetch(`${this.getAttribute('data-product-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateQuantityRules(parsedHTML);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.querySelector('label[is="quantity-label"]')?.removeAttribute('aria-busy');
      });
  }

  updateQuantityRules(parsedHTML) {
    if (!this.quantityInput) return;

    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityRules, {
      data: {
        sectionId: this.sectionId,
        productId: this.productId,
        parsedHTML
      }
    });

    this.setQuantityBoundries();
  }
}
customElements.define('product-info', ProductInfo);

class ProductForm extends HTMLFormElement {
  constructor() {
    super();

    this.variantIdInput.disabled = false;
    this.addEventListener('submit', this.onSubmitHandler);
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get submitButton() {
    return this._submitButton = this._submitButton || this.querySelector('[type="submit"]');
  }

  get submitButtons() {
    return this._submitButtons = this._submitButtons || document.querySelectorAll(`[type="submit"][form="${this.getAttribute('id')}"]`);
  }

  get hideErrors() {
    return this.getAttribute('data-hide-errors') === 'true';
  }

  get variantIdInput() {
    return this.querySelector('[name="id"]');
  }

  get bundles() {
    return Array.from(document.querySelectorAll(`[form="${this.getAttribute('id')}"] input[name="bundles"]:checked`));
  }

  prepareFormData(formData) {
    const bundlesLength = this.bundles.length;
    const itemsArray = new Array(bundlesLength + 1);

    for (let i = 0; i < bundlesLength; i++) {
      const reverseIndex = bundlesLength - 1 - i;
      itemsArray[i] = {
        id: this.bundles[reverseIndex].value,
        quantity: 1
      };
    }

    const allFormData = { items: itemsArray.slice(0, bundlesLength) };

    const json = {};
    const formEntries = Array.from(formData.entries());

    for (const [name, value] of formEntries) {
      if (name === 'id' || name === 'quantity' || name.includes('properties')) {
        if (name === 'id' || name === 'quantity') {
          json[name] = value;
        } else {
          const regex = /(?:^(properties\[))(.*?)(?:\])/;
          const property = regex.exec(name)[2];
          json.properties = json.properties || {};
          json.properties[property] = value;
        }
      } else {
        allFormData[name] = value;
      }
    }

    allFormData.items.push(json);
    return allFormData;
  }

  onSubmitHandler(event) {
    const hasBundles = this.bundles.length > 0;

    if (!Shopify.designMode) {
      if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
        if (hasBundles) {
          theme.utils.postLink2(theme.routes.cart_add_url, {
            parameters: {
              ...this.prepareFormData(new FormData(this))
            }
          });
          event.preventDefault();
        }
        return;
      }
    }

    event.preventDefault();
    if (this.submitButton.hasAttribute('aria-disabled')) return;
    this.activeElement = event.submitter || event.currentTarget;

    this.handleErrorMessage();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    const config = theme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this);
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);

    config.body = formData;

    if (hasBundles) {
      const allFormData = this.prepareFormData(formData);
      config.body = JSON.stringify(allFormData);
      config.headers['Content-Type'] = 'application/json';
    }

    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.setAttribute('aria-busy', 'true');

    fetch(theme.routes.cart_add_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: parsedState.errors || parsedState.description,
            message: parsedState.message
          });
          this.handleErrorMessage(parsedState.description);
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: parsedState.description
            }
          }));

          const submitButtonText = this.submitButton.querySelector('.btn-text>span');
          if (submitButtonText && submitButtonText.hasAttribute('data-sold-out')) {
            submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out');
            this.submitButton.setAttribute('aria-disabled', 'true');
            this.error = true;
          }

          const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cart: cartJson });

          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
          return;
        }

        if (Shopify.designMode) {
          if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
            window.location.href = theme.routes.cart_url;
            return;
          }
        }

        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: parsedState
          }
        }));

        this.cartDrawer?.show(this.activeElement);
      })
      .catch((error) => {
      })
      .finally(() => {
        this.submitButton.removeAttribute('aria-busy');
        this.submitButtons.forEach(submitButton => submitButton.removeAttribute('aria-busy'));

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtons.forEach(submitButton => submitButton.removeAttribute('aria-disabled'));
        }
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  toggleSubmitButton(disable = true, text, unavailable = false) {
    if (!this.submitButton) return;

    this.submitButton.removeAttribute('loading');
    this.submitButton.removeAttribute('unavailable');

    const submitButtonText = this.submitButton.querySelector('.btn-text');
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text>span');

    if (disable) {
      this.submitButton.setAttribute('disabled', '');
      if (unavailable) this.submitButton.setAttribute('unavailable', '');
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      }
      else {
        this.submitButton.setAttribute('loading', '');
      }
    }
    else {
      this.submitButton.removeAttribute('disabled');
      (submitButtonTextChild || submitButtonText).textContent = this.submitButton.hasAttribute('data-pre-order') ? theme.variantStrings.preOrder : theme.variantStrings.addToCart;
    }
  }
}
customElements.define('product-form', ProductForm, { extends: 'form' });

class ProductStickyForm extends HTMLElement {
  constructor() {
    super();

    this.scopeFrom = document.querySelector('.quick-order-list') || document.getElementById(this.getAttribute('form'));
    this.scopeTo = document.querySelector('.footer-group');

    if (!this.scopeFrom || !this.scopeTo) {
      return;
    }

    const intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this));
    intersectionObserver.observe(this.scopeFrom);
    intersectionObserver.observe(this.scopeTo);

    this.onVariantChangedListener = this.onVariantChanged.bind(this);
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get submitButton() {
    return this._submitButton = this._submitButton || this.querySelector('[type="submit"]');
  }

  get productOptions() {
    return this._productOptions = this._productOptions || this.querySelector('[data-sticky-product-options]');
  }

  get productMedia() {
    return this._productMedia = this._productMedia || this.querySelector('[data-sticky-product-media]');
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target === this.scopeFrom) {
        this.scopeFromPassed = entry.boundingClientRect.bottom < 0;
        this.scopeFromVisible = entry.isIntersecting;
      }
      if (entry.target === this.scopeTo) {
        this.scopeToReached = entry.isIntersecting;
      }
    });

    // Show "Any time" (data-show="load", v2.10.26): whenever the main form is off screen —
    // above it on the first screen too, not only after it has scrolled past.
    const shown = this.getAttribute('data-show') === 'load'
      ? !this.scopeFromVisible && !this.scopeToReached
      : this.scopeFromPassed && !this.scopeToReached;
    if (shown) {
      Motion.animate(this.firstElementChild, { opacity: 1, visibility: 'visible', transform: ['translateY(15px)', 'translateY(0)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
    }
    else {
      Motion.animate(this.firstElementChild, { opacity: 0, visibility: 'hidden', transform: ['translateY(0)', 'translateY(15px)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
      if (this.panelOpen) this.togglePanel(false);
    }
    this.announceVisibility(shown);
  }

  // Tell fixed corner buttons (the WhatsApp float) to lift above the bar while it shows:
  // a class on <html> and the distance from the screen's bottom edge to the bar's top
  // (v2.10.25 — the float sat on top of the bar's Add to cart). Measured on the bar's
  // wrapper, which is not animated, so it includes the mobile dock the bar sits on.
  announceVisibility(shown) {
    const root = document.documentElement;
    if (shown) {
      const offset = Math.max(0, Math.round(window.innerHeight - this.getBoundingClientRect().top));
      root.style.setProperty('--sticky-buy-offset', `${offset}px`);
    }
    root.classList.toggle('sticky-buy-visible', shown);
  }

  connectedCallback() {
    (this.productForm ?? document).addEventListener('variant:change', this.onVariantChangedListener);

    // Options panel (v2.10.90): the cart button's first tap opens the panel above the bar; with it
    // open, the button adds. A chip clicks the same value in the page's variant picker, which does
    // the rest (price, gallery, stock, URL) and re-renders the panel's options.
    this.panel = this.querySelector('[data-sticky-panel]');
    if (!this.panel) return;

    this.onSubmitClick = (event) => {
      if (this.panelOpen) {
        setTimeout(() => this.togglePanel(false), 400);
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      this.togglePanel(true);
    };
    this.submitButton?.addEventListener('click', this.onSubmitClick, true);

    this.onPanelClick = (event) => {
      if (event.target.closest('[data-sticky-panel-close]')) {
        this.togglePanel(false);
        return;
      }
      const chip = event.target.closest('[data-option-value-id]');
      if (chip) this.selectOptionValue(chip.getAttribute('data-option-value-id'));
    };
    this.panel.addEventListener('click', this.onPanelClick);

    this.onOutside = (event) => {
      if (this.panelOpen && !this.contains(event.target)) this.togglePanel(false);
    };
    this.onKeydown = (event) => {
      if (this.panelOpen && event.key === 'Escape') {
        this.togglePanel(false);
        this.submitButton?.focus();
      }
    };
    document.addEventListener('click', this.onOutside);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    (this.productForm ?? document).removeEventListener('variant:change', this.onVariantChangedListener);
    document.documentElement.classList.remove('sticky-buy-visible');
    if (this.panel) {
      this.submitButton?.removeEventListener('click', this.onSubmitClick, true);
      document.removeEventListener('click', this.onOutside);
      document.removeEventListener('keydown', this.onKeydown);
    }
  }

  get panelOpen() {
    return this.classList.contains('is-panel-open');
  }

  togglePanel(open) {
    if (!this.panel) return;
    this.classList.toggle('is-panel-open', open);
    this.panel.toggleAttribute('inert', !open);
    this.submitButton?.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) this.panel.querySelector('.product-sticky-form__chip.is-selected, .product-sticky-form__chip')?.focus({ preventScroll: true });
  }

  // The page's picker for this product: the sticky form shares the section and product ids.
  selectOptionValue(valueId) {
    const ids = this.id.replace('ProductStickyForm-', '');
    const picker = document.getElementById(`VariantPicker-${ids}`);
    const target = picker?.querySelector(`[data-option-value-id="${CSS.escape(valueId)}"]`);
    if (!target) return;

    if (target.tagName === 'OPTION') {
      const select = target.closest('select');
      if (select.value === target.value) return;
      select.value = target.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    else if (!target.checked) {
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  onVariantChanged(event) {
    this.updateProductMedia(event.detail.variant);
    this.updateProductOptions(event.detail.variant);
  }

  updateProductMedia(currentVariant) {
    if (!currentVariant || !currentVariant.featured_media) return;

    const image = this.productMedia.querySelector('img');
    const newImage = new Image(currentVariant.featured_media.preview_image.width, currentVariant.featured_media.preview_image.height);

    newImage.alt = currentVariant.featured_media.alt;
    newImage.src = currentVariant.featured_media.preview_image.src;
    newImage.srcset = this.generateSrcset(currentVariant.featured_media.preview_image);
    newImage.sizes = image.sizes;

    this.productMedia.replaceChildren(newImage);
  }

  updateProductOptions(currentVariant) {
    if (!currentVariant) return;

    this.productOptions.innerText = currentVariant.title;
  }

  handleErrorMessage(errorMessage = false) {
    if (this.productForm?.hideErrors) return;

    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  toggleSubmitButton(disable = true, text) {
    if (!this.submitButton) return;

    const submitButtonText = this.submitButton.querySelector('.btn-text');
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text>span');

    if (disable) {
      this.submitButton.setAttribute('disabled', '');
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      }
    }
    else {
      this.submitButton.removeAttribute('disabled');
      (submitButtonTextChild || submitButtonText).textContent = this.submitButton.hasAttribute('data-pre-order') ? theme.variantStrings.preOrder : theme.variantStrings.addToCart;
    }
  }

  generateSrcset(image) {
    const widths = this.productMedia.getAttribute('data-widths').split(',').map((width) => parseInt(width));
    return widths.filter((width) => width <= image.width).map((width) => {
      return `${image.src}&width=${width} ${width}w`;
    }).join(', ');
  }
}
customElements.define('product-sticky-form', ProductStickyForm);

// Sticky product columns taller than the screen (v2.10.26). The info column and the
// gallery column stick at the top from 1024px; one taller than the screen kept its lower
// part (often Add to cart) out of reach until the other column ended. Such a column now
// scrolls with the page until its bottom meets the screen's bottom, then sticks there.
(() => {
  const GAP = 24;
  const selector = '.product .lg\\:sticky:is(.product__info, .product__gallery-container)';
  const fit = () => {
    const wide = window.matchMedia('(min-width: 1024px)').matches;
    document.querySelectorAll(selector).forEach((el) => {
      el.style.removeProperty('inset-block-start');
      if (!wide) return;
      const top = parseFloat(getComputedStyle(el).insetBlockStart) || 0;
      if (el.offsetHeight + top + GAP > window.innerHeight) {
        el.style.setProperty('inset-block-start', `${window.innerHeight - el.offsetHeight - GAP}px`, 'important');
      }
    });
  };
  let frame;
  const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(fit); };
  const watch = () => {
    if (!('ResizeObserver' in window)) return;
    const ro = new ResizeObserver(schedule);
    document.querySelectorAll(selector).forEach((el) => ro.observe(el));
    schedule();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('shopify:section:load', watch);
})();

class ProductBundleDetails extends AccordionDetails {
  constructor() {
    super();
  }

  cartUpdateUnsubscriber = undefined;

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get bundles() {
    return Array.from(this.querySelectorAll('input[name="bundles"]:checked'));
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    this.onBundleChangeListener = this.onBundleChange.bind(this);
    this.addEventListener('change', this.onBundleChangeListener);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    this.removeEventListener('change', this.onBundleChangeListener);
  }

  onCartUpdate(event) {
    if (this.bundles.length === 0) return;

    if (event.source === 'product-form') {
      this.bundles.forEach((input) => {
        if (!input.disabled) input.checked = false;
      });
      this.onBundleChange();
    }
  }

  onBundleChange() {
    (this.productForm ?? document).dispatchEvent(new CustomEvent('bundle:change', {
      detail: {
        items: this.bundles.map(input => ({
          id: input.value,
          quantity: 1,
          price: input.getAttribute('data-price')
        }))
      }
    }));
  }
}
customElements.define('product-bundle-details', ProductBundleDetails, { extends: 'details' });

class ProductBundlePicker extends HTMLLabelElement {
  constructor() {
    super();
    this.handleVariantChange = this.handleVariantChange.bind(this);
  }

  connectedCallback() {
    this.addEventListener('change', this.handleVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleVariantChange);
  }

  handleVariantChange(event) {
    const target = event.target;

    if (target.tagName !== 'SELECT') return;

    const option = target.selectedOptions?.[0];
    if (!option) return;

    const value = option.value;
    const price = option.getAttribute('data-price');
    const imgSrc = option.getAttribute('data-img-src');
    const imgSrcset = option.getAttribute('data-img-srcset');

    this.updateCheckboxInput(value, price);
    this.updatePrice(price);
    this.updateImage(imgSrc, imgSrcset);
  }

  updateCheckboxInput(value, price) {
    const input = this.querySelector('input[type="checkbox"]');
    if (!input) return;

    input.value = value;
    input.setAttribute('data-price', price);
  }

  updatePrice(price) {
    const priceElement = this.querySelector('.price__regular');
    if (!priceElement || !price) return;

    const format = theme.settings.currencyCodeEnabled
      ? theme.settings.moneyWithCurrencyFormat
      : theme.settings.moneyFormat;

    priceElement.innerHTML = theme.Currency.formatMoney(price, format);
  }

  updateImage(src, srcset) {
    const img = this.querySelector('img');
    if (!img) return;

    if (src) img.src = src;
    if (srcset) img.srcset = srcset;
  }
}
customElements.define('product-bundle-picker', ProductBundlePicker, { extends: 'label' });

class ProductBuyPrice extends HTMLElement {
  constructor() {
    super();
    this.onBundleChangedListener = this.onBundleChanged.bind(this);
  }

  get productForm() {
    const formId = this.getAttribute('form');
    return formId ? document.forms[formId] : null;
  }

  get basePrice() {
    return parseInt(this.getAttribute('data-price')) || 0;
  }

  connectedCallback() {
    (this.productForm ?? document).addEventListener('bundle:change', this.onBundleChangedListener);
  }

  disconnectedCallback() {
    (this.productForm ?? document).removeEventListener('bundle:change', this.onBundleChangedListener);
  }

  onBundleChanged(event) {
    const { items } = event.detail;

    const subtotal = items.reduce((total, item) => {
      const price = parseInt(item.price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      return total + (price * quantity);
    }, this.basePrice);

    this.updatePrice(subtotal);
  }

  updatePrice(amount) {
    const format = theme.settings.currencyCodeEnabled
      ? theme.settings.moneyWithCurrencyFormat
      : theme.settings.moneyFormat;

    this.innerHTML = theme.Currency.formatMoney(amount, format);
  }

}
customElements.define('product-buy-price', ProductBuyPrice);

class MediaGallery extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, () => {
      requestAnimationFrame(() => this.pauseAllMedia());
      //setTimeout(() => this.pauseAllMedia(), 500);
    });

    (this.productForm ?? document).addEventListener('variant:change', this.onVariantChanged.bind(this));

    this.addEventListener('lightbox:open', (event) => this.openZoom(event.detail.index));
    // Listen on the gallery itself, in the capture phase: the slider inside is replaced on
    // every variant change and its slider:change does not bubble, so a listener bound to
    // the first slider died after one switch (v2.10.25).
    this.addEventListener('slider:change', (event) => {
      if (event.target.closest('media-gallery') === this && event.detail?.currentElement) this.onSlideChange(event);
    }, true);

    this.countMediaGallery();
  }

  get viewInSpaceButton() {
    return this.querySelector('[data-shopify-xr]');
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get sliderGallery() {
    return this.querySelector('slider-element');
  }

  get mediaPreview() {
    return this.querySelector('.product__preview .product__media');
  }

  get photoswipe() {
    if (this._photoswipe) return this._photoswipe;

    const lightbox = new PhotoSwipeLightbox({
      arrowPrevSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      arrowNextSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      closeSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="m7.5 22.5 15-15m-15 0 15 15"/></svg>',
      bgOpacity: 1,
      pswpModule: () => import(theme.settings.pswpModule),

      arrowPrevTitle: theme.strings.previous,
      arrowNextTitle: theme.strings.next,
      closeTitle: theme.strings.close,

      // Recommended options for this plugin
      allowPanToNext: false,
      allowMouseDrag: true,
      wheelToZoom: false,
      returnFocus: true,
      zoom: false,
    });

    lightbox.addFilter('thumbEl', (_thumbEl, data) => data['thumbnailElement']);

    lightbox.on('contentLoad', (event) => {
      const { content } = event;

      if (content.type === 'video' || content.type === 'external_video' || content.type === 'model') {
        event.preventDefault();

        // Create a container for video and assign it to the `content.element` property
        content.element = document.createElement('div');
        content.element.className = 'pswp__video-container';
        content.element.appendChild(content.data.domElement.cloneNode(true));
      }
    });

    lightbox.on('change', () => {
      this.sliderGallery.select(lightbox.pswp.currIndex + 1, true);
    });

    lightbox.on('close', () => {
      const slideSelected = this.sliderGallery.selected(lightbox.pswp.currIndex);
      lightbox.pswp._lastActiveElement = slideSelected ? slideSelected.querySelector('button') : document.activeElement;
    });

    lightbox.init();
    return this._photoswipe = lightbox;
  }

  onVariantChanged(event) {
    const currentVariant = event.detail.variant;
    if (!currentVariant.featured_media) return;

    this.countMediaGallery();
    this.setActiveMedia(currentVariant.featured_media.id);
  }

  onSlideChange(event) {
    const activeMedia = event.detail.currentElement;
    this.playActiveMedia(activeMedia);

    if (this.viewInSpaceButton) {
      if (activeMedia.getAttribute('data-media-type') === 'model') {
        this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', activeMedia.getAttribute('data-media-id'));
      } else {
        this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', this.viewInSpaceButton.getAttribute('data-shopify-model3d-default-id'));
      }
    }
  }

  setActiveMedia(mediaId) {
    const mediaItems = [...this.sliderGallery.querySelectorAll('[data-media-id]')];
    if (!mediaItems.length) return;

    const isVisible = (element) => {
      if (!element) return false;
      if (typeof element.checkVisibility === 'function') return element.checkVisibility();
      if (element.hidden) return false;
      return element.getClientRects().length > 0;
    };

    let activeMedia = mediaItems.find((element) => element.getAttribute('data-media-id') === `${mediaId}`);
    if (!isVisible(activeMedia)) {
      const startIndex = activeMedia ? mediaItems.indexOf(activeMedia) + 1 : 0;
      activeMedia = mediaItems.slice(startIndex).find(isVisible) || mediaItems.find(isVisible);
    }
    if (!activeMedia) return;

    // When the gallery is a horizontal carousel (mobile/tablet), move the carousel
    // to the selected slide. Advancing the slider scrolls horizontally within the
    // gallery only — it must never scroll the whole page vertically.
    if (this.sliderGallery.isScrollable) {
      const slideIndex = this.sliderGallery.itemsToShow.indexOf(activeMedia);
      if (slideIndex > -1) {
        this.sliderGallery.select(slideIndex + 1);
        return;
      }
    }

    // Desktop grid/stack layouts: bring the media into view only if it isn't
    // already visible, and by the minimum amount. `block: 'nearest'` is a no-op
    // when the media is on screen, so clicking variants no longer jumps the page
    // to the top on every selection.
    activeMedia.scrollIntoView({
      block: 'nearest',
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    });
  }

  playActiveMedia(activeMedia) {
    if (typeof activeMedia === 'undefined') return;

    const deferredMedia = activeMedia.querySelector('.deferred-media');

    this.sliderGallery.querySelectorAll('.deferred-media').forEach((media) => {
      deferredMedia === media ? media.play() : media.pause();
    });
  }

  pauseAllMedia() {
    this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media, index) => {
      if (index > 0) {
        const deferredMedia = media.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.pause === 'function') deferredMedia.pause();
      }
    });
  }

  openZoom(index = 0) {
    let dataSource = this.sliderGallery.itemsToShow.map((media) => {
      const image = media.querySelector('img');

      if (media.getAttribute('data-media-type') === 'image') {
        return {
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute('width')),
          height: parseInt(image.getAttribute('height')),
          alt: image.alt,
          thumbCropped: true
        };
      }

      if (media.getAttribute('data-media-type') === 'video' || media.getAttribute('data-media-type') === 'external_video' || media.getAttribute('data-media-type') === 'model') {
        const video = media.querySelector('.deferred-media');
        return {
          thumbnailElement: image,
          domElement: video,
          type: media.getAttribute('data-media-type'),
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: 800,
          height: 800 / video.getAttribute('data-aspect-ratio'),
          alt: image.alt,
          thumbCropped: true
        };
      }
    });

    if (this.mediaPreview && this.mediaPreview.offsetParent) {
      if (this.mediaPreview.getAttribute('data-media-type') === 'image') {
        const image = this.mediaPreview.querySelector('img');
        dataSource.push({
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute('width')),
          height: parseInt(image.getAttribute('height')),
          alt: image.alt,
          thumbCropped: true
        });

        if (index === -1) {
          index = dataSource.length - 1;
        }
      }
    }

    this.photoswipe.loadAndOpen(index, dataSource);
  }

  countMediaGallery() {
    let mediaCount = 0;
    this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media) => {
      if (!media.classList.contains('xl:hidden') && !media.hasAttribute('hidden')) {
        mediaCount++;
      }
    });

    if (this.mediaPreview) {
      mediaCount++;
    }

    mediaCount > 1 ? this.classList.remove('with-only1') : this.classList.add('with-only1');
  }
}
customElements.define('media-gallery', MediaGallery);

class MediaLightboxButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener('click', this.onButtonClick);
  }

  onButtonClick() {
    const media = this.closest('[data-media-id]');
    const sliderGallery = this.closest('slider-element');
    const openIndex = sliderGallery ? sliderGallery.itemsToShow.indexOf(media) : -1;

    this.dispatchEvent(new CustomEvent('lightbox:open', {
      bubbles: true,
      detail: {
        index: openIndex
      }
    }));
  }
}
customElements.define('media-lightbox-button', MediaLightboxButton, { extends: 'button' });

class MediaHoverButton extends MediaLightboxButton {
  constructor() {
    super();
  }

  get zoomRatio() {
    return 2;
  }

  onButtonClick(event) {
    if (theme.config.isTouch) {
      super.onButtonClick();
    }
    else {
      const media = this.closest('[data-media-type="image"]');
      if (media) {
        const image = media.querySelector('img');
        this.gallery = this.closest('slider-element');

        this.magnify(image);
        this.moveWithHover(image, event);
      }
    }
  }

  createOverlay(image) {
    const overlayImage = document.createElement('img');
    overlayImage.setAttribute('src', `${image.src}`);
    const overlay = document.createElement('media-hover-overlay');
    this.prepareOverlay(overlay, overlayImage);

    this.toggleLoadingSpinner(image);

    overlayImage.onload = () => {
      this.toggleLoadingSpinner(image);
      image.parentElement.insertBefore(overlay, image);
    };

    if (this.gallery) this.gallery.classList.add('magnify');

    return overlay;
  }

  prepareOverlay(container, image) {
    container.setAttribute('class', 'media z-10 absolute top-0 left-0 w-full h-full');
    container.setAttribute('aria-hidden', 'true');
    container.style.backgroundImage = `url('${image.src}')`;
    container.style.cursor = 'zoom-out';
  }

  toggleLoadingSpinner(image) {
    const loadingSpinner = image.parentElement;
    loadingSpinner.classList.toggle('loading');
    loadingSpinner.classList.toggle('pointer-events-none');
  }

  moveWithHover(image, event) {
    // calculate mouse position
    const ratio = image.height / image.width;
    const container = event.target.getBoundingClientRect();
    const xPosition = event.clientX - container.left;
    const yPosition = event.clientY - container.top;
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

    // determine what to show in the frame
    this.overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
    this.overlay.style.backgroundSize = `${image.width * this.zoomRatio}px`;
  }

  magnify(image) {
    this.overlay = this.createOverlay(image);
    this.overlay.onclick = () => this.reset();
    this.overlay.onmousemove = (event) => this.moveWithHover(image, event);
    this.overlay.onmouseleave = () => this.reset();
  }

  reset() {
    this.overlay.remove();

    if (this.gallery) this.gallery.classList.remove('magnify');
  }
}
customElements.define('media-hover-button', MediaHoverButton, { extends: 'button' });

class MediaDots extends SliderDots {
  constructor() {
    super();
  }

  init() {
    super.init();
    this.resetIndexes();
    this.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  // Arrow keys move between thumbnails and show that photo, like a click
  // (v2.10.65). Left/Right follow the reading direction; Home/End jump to the ends.
  onKeyDown(event) {
    const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;

    const items = this.itemsToShow;
    const current = items.indexOf(event.target.closest('media-dots > *'));
    if (current < 0) return;

    const rtl = theme.config.rtl;
    let next = current;
    if (event.key === 'ArrowDown' || event.key === (rtl ? 'ArrowLeft' : 'ArrowRight')) next = current + 1;
    else if (event.key === 'ArrowUp' || event.key === (rtl ? 'ArrowRight' : 'ArrowLeft')) next = current - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;

    next = Math.max(0, Math.min(items.length - 1, next));
    event.preventDefault();
    if (next === current) return;
    items[next].focus();
    items[next].click();
  }

  resetIndexes() {
    let newIndex = 1;
    this.itemsToShow.forEach((item, index) => {
      item.setAttribute('data-index', newIndex);
      item.setAttribute('aria-current', index === 0 ? 'true' : 'false');

      newIndex++;
    });
  }
}
customElements.define('media-dots', MediaDots);

class XModal extends ModalElement {
  constructor() {
    super();
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }
}
customElements.define('x-modal', XModal);

class LogoList extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get childElement() {
    return this.firstElementChild;
  }

  get speed() {
    return this.hasAttribute('data-speed') ? parseInt(this.getAttribute('data-speed')) : 16;
  }

  get maximum() {
    return this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : Math.ceil(this.parentWidth / this.childElementWidth) + 2;
  }

  get direction() {
    return this.hasAttribute('data-direction') ? this.getAttribute('data-direction') : 'left';
  }

  get parentWidth() {
    return this.getWidth(this);
  }

  get childElementWidth() {
    return this.getWidth(this.childElement);
  }

  disconnectedCallback() {
    if (this.marquee) {
      this.pause();
      this.marquee.destroy();
    }
  }

  init() {
    if (this.childElementCount === 1) {
      this.childElement.classList.add('animated');

      const maximum = this.maximum;
      for (let index = 0; index < maximum; index++) {
        this.clone = this.childElement.cloneNode(true);
        this.clone.setAttribute('aria-hidden', true);
        this.appendChild(this.clone);
        this.clone.querySelectorAll('.media').forEach((media) => media.classList.remove('loading'));
      }

      if (theme.config.isTouch) {
        this.style.setProperty('--duration', `${33 - this.speed}s`);
      }
      else {
        this.marquee = new Flickity(this, {
          prevNextButtons: false,
          pageDots: false,
          wrapAround: true,
          freeScroll: true,
          rightToLeft: this.direction === 'right',
          // The marquee is decorative: accessibility:true would tabindex the
          // row and focus it on every click, which paints the global
          // keyboard-focus outline as a stray line between rows (Safari/FF).
          accessibility: false,
        });

        // Set initial position to be 0
        this.marquee.x = 0;

        // Start the marquee animation
        this.play();

        this.addEventListener('mouseenter', this.pause);
        this.addEventListener('mouseleave', this.play);
      }
    }
  }

  play() {
    // Set the decrement of position x
    this.marquee.x -= this.speed * 0.1;

    // Settle position into the slider
    this.marquee.settle(this.marquee.x);

    // Set the requestId to the local variable
    this.requestId = requestAnimationFrame(this.play.bind(this));
  }

  pause() {
    if (this.requestId) {
      // Cancel the animation
      cancelAnimationFrame(this.requestId);

      // Reset the requestId for the next animation.
      this.requestId = undefined;
    }
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect();
    return rect.right - rect.left;
  }
}
customElements.define('logo-list', LogoList);


class TabsElement extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
    this.buttons.forEach((button, index) => {
      button.addEventListener('click', () => this.selectedIndex = index);
      button.addEventListener('keydown', this.onButtonKeydown.bind(this));
    });
    this.initialized = this.buttons.length > 0;

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => this.selectedIndex = this.buttons.indexOf(event.target));
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    if (!this.buttons.length) return;
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.buttons.length - 1).toString());
  }

  get buttons() {
    return this._buttons = this._buttons || Array.from(this.querySelectorAll('button[role="tab"]'));
  }

  get panels() {
    return this._panels = this._panels || this.buttons.map((button) => document.getElementById(button.getAttribute('aria-controls')));
  }

  get indicators() {
    return this._indicators = this._indicators || Array.from(this.querySelectorAll('.indicators'));
  }

  connectedCallback() {
    this._buttons = Array.from(this.querySelectorAll('button[role="tab"]'));
    this._panels = null;

    if (!this.initialized) {
      this.buttons.forEach((button, index) => {
        button.addEventListener('click', () => this.selectedIndex = index);
        button.addEventListener('keydown', this.onButtonKeydown.bind(this));
      });
      this.initialized = true;
    }

    // Opt-in hover-to-switch (mega-menu tabs only, via data-hover-switch): a
    // pointer entering a tab selects it instantly, like large retail mega menus.
    // Pointer-only so touch is unaffected; click + keyboard still work.
    if (!this._hoverInit && this.hasAttribute('data-hover-switch') &&
        window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this._hoverInit = true;
      this.buttons.forEach((button, index) => {
        button.addEventListener('pointerenter', () => { this.selectedIndex = index; });
        // Hover already previews the panel, so on fine-pointer devices a CLICK
        // acts as navigation to the tab's collection. Touch keeps tap-to-preview
        // (this whole block is gated on hover+fine pointer).
        const url = button.getAttribute('data-url');
        if (url) {
          button.addEventListener('click', () => { window.location.href = url; });
        }
      });
      // Group headers that don't link to a collection aren't tabs themselves —
      // hovering one previews its first child's products.
      this.querySelectorAll('[data-group-first]').forEach((el) => {
        el.addEventListener('pointerenter', () => {
          const idx = parseInt(el.getAttribute('data-group-first'), 10);
          if (!isNaN(idx)) this.selectedIndex = idx;
        });
      });
    }

    this.syncTabs();
    requestAnimationFrame(() => this.scrollSelectedTabIntoView(true));
    this.warmPanels();
    this.initLazyPanels();
  }

  // Product Tab Mega Menu: child-collection panels ship as skeleton shells
  // (a tree-style menu can hold dozens of collections — inlining every
  // product card would bloat every page). Each shell's grid carries its
  // collection URL; the real cards come from the tiny mega-collection-panel
  // section via the Section Rendering API the moment the shopper shows
  // intent (hover/focus/selection), and are cached per URL so a panel is
  // fetched at most once per page.
  initLazyPanels() {
    if (this._lazyInit) return;
    if (!this.panels.some((panel) => panel && panel.querySelector('[data-mega-lazy]'))) return;
    this._lazyInit = true;

    this.buttons.forEach((button, index) => {
      const load = () => this.loadLazyPanel(index);
      button.addEventListener('pointerenter', load, { passive: true });
      button.addEventListener('focus', load);
    });
  }

  loadLazyPanel(index) {
    const panel = this.panels[index];
    const grid = panel && panel.querySelector('[data-mega-lazy]');
    if (!grid || grid.hasAttribute('data-loaded') || grid.hasAttribute('data-loading')) return;
    const collectionUrl = grid.getAttribute('data-panel-url');
    if (!collectionUrl) return;

    const url = `${collectionUrl}${collectionUrl.includes('?') ? '&' : '?'}section_id=mega-collection-panel`;
    const insert = (responseText) => {
      const cards = new DOMParser().parseFromString(responseText, 'text/html').querySelector('[data-mega-panel-cards]');
      if (!cards) return;
      const limit = parseInt(grid.getAttribute('data-limit')) || 5;
      grid.replaceChildren(...Array.from(cards.children).slice(0, limit));
      grid.setAttribute('data-loaded', '');
      theme.utils.warmImages(panel);
      // Re-run the single-row fit so the fresh cards respect the row budget.
      scheduleMegaMenuFit();
      if (!panel.hidden && grid.initialized) grid.load();
    };

    const cached = cachedSectionsRenderingAPI.get(url);
    if (cached) {
      insert(cached);
      return;
    }

    grid.setAttribute('data-loading', '');
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        cachedSectionsRenderingAPI.set(url, responseText);
        insert(responseText);
      })
      .catch((e) => console.error(e))
      .finally(() => grid.removeAttribute('data-loading'));
  }

  // A tab panel is fetched ahead of time (api-button) but its images are not:
  // they are lazy inside a hidden panel, so the browser skips them until the
  // panel is shown — which is exactly when the shopper is looking at it. Warm
  // them earlier instead:
  //
  //   intent — a pointer entering, or focus landing on, a tab warms that whole
  //            panel. On a pointer device this buys the ~100-300ms between
  //            hovering a tab and clicking it, so the switch looks instant.
  //   idle   — the NEXT panel's first few cards are warmed once the page goes
  //            quiet. That covers touch, where there is no hover to react to,
  //            without pulling every image of every tab down a phone
  //            connection: only the panel most likely to be opened next, and
  //            only the cards that would be on screen when it opens.
  warmPanels() {
    if (this._warmPanelsInit) return;
    this._warmPanelsInit = true;

    // NOT once-only: a panel's markup is fetched asynchronously, so an early
    // hover can land before there is anything to warm. warmImages marks what
    // it touches, so repeat calls cost a querySelectorAll and nothing more.
    this.buttons.forEach((button, index) => {
      const warmThisPanel = () => theme.utils.warmImages(this.panels[index]);
      button.addEventListener('pointerenter', warmThisPanel, { passive: true });
      button.addEventListener('touchstart', warmThisPanel, { passive: true });
      button.addEventListener('focus', warmThisPanel);
    });

    const warmNext = () => {
      const next = this.panels[parseInt(this.selectedIndex) + 1];
      if (next) theme.utils.warmImages(next, 4);
    };
    theme.utils.onIdle(warmNext);

    // ...and again once a panel's markup actually arrives, since the idle pass
    // above may well have run against an empty panel.
    this._warmOnSectionRender = () => theme.utils.onIdle(warmNext);
    document.addEventListener('sectionsRenderingAPI:cached', this._warmOnSectionRender);
  }

  disconnectedCallback() {
    if (this._warmOnSectionRender) {
      document.removeEventListener('sectionsRenderingAPI:cached', this._warmOnSectionRender);
      this._warmOnSectionRender = null;
    }
  }

  onButtonKeydown(event) {
    const currentIndex = this.buttons.indexOf(event.currentTarget);
    if (currentIndex < 0) return;

    let nextIndex = null;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + (theme.config.rtl ? -1 : 1) + this.buttons.length) % this.buttons.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex + (theme.config.rtl ? 1 : -1) + this.buttons.length) % this.buttons.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.buttons.length - 1;
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.focusPanel(currentIndex);
        return;
      default:
        return;
    }

    event.preventDefault();
    this.selectedIndex = nextIndex;
    this.buttons[nextIndex]?.focus({ preventScroll: true });
  }

  syncTabs() {
    this.buttons.forEach((button, index) => {
      const panel = this.panels[index];

      if (!button.id) {
        button.id = `${this.id || 'Tabs'}-Tab-${index}`;
      }

      button.disabled = false;
      button.setAttribute('aria-selected', index === parseInt(this.selectedIndex) ? 'true' : 'false');
      button.setAttribute('tabindex', index === parseInt(this.selectedIndex) ? '0' : '-1');

      if (panel) {
        panel.setAttribute('aria-labelledby', button.id);
      }
    });

    // Product Tab Mega Menu: keep the group that holds the active tab open,
    // independent of hover — so moving the mouse to the product area doesn't
    // collapse it; it only closes when a tab in another group (or a top-level
    // item) becomes active.
    if (this.classList.contains('mega-menu__nav--groups')) {
      const selected = this.buttons[parseInt(this.selectedIndex)];
      const activeGroup = selected ? selected.closest('.mega-menu__group') : null;
      this.querySelectorAll('.mega-menu__group').forEach((group) => {
        const wasOpen = group.classList.contains('is-open');
        group.classList.toggle('is-open', group === activeGroup);
        // A group just opened: warm its child panels in the background so
        // hovering down the sub-list never shows a skeleton for long.
        if (group === activeGroup && !wasOpen && this._lazyInit) {
          this.buttons.forEach((button, index) => {
            if (group.contains(button)) theme.utils.onIdle(() => this.loadLazyPanel(index));
          });
        }
      });
    }
  }

  focusPanel(index = parseInt(this.selectedIndex)) {
    const panel = this.panels[index];
    if (!panel) return;

    const focusTarget = theme.a11y.getFocusableElements(panel).find((element) => !element.closest('[hidden], [inert]') && !element.hasAttribute('disabled'));
    focusTarget?.focus();
  }

  // Keep the active tab visible in the horizontally-scrollable tab bar. When a
  // far-off tab is selected (e.g. jumping from tab 2 to tab 5) this smoothly
  // scrolls the track so the active tab is centred, instead of leaving it
  // off-screen or letting native focus jump to it abruptly.
  scrollSelectedTabIntoView(immediate = false) {
    const button = this.buttons[parseInt(this.selectedIndex)];
    if (!button) return;

    const scroller = button.closest('.scroll-area');
    if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    const delta = (buttonRect.left - scrollerRect.left) - (scroller.clientWidth - buttonRect.width) / 2;
    if (Math.abs(delta) < 1) return;

    scroller.scrollTo({
      left: scroller.scrollLeft + delta,
      behavior: immediate || theme.config.motionReduced ? 'auto' : 'smooth',
    });
  }

  load() {
    const toButton = this.buttons[parseInt(this.selectedIndex)];
    if (toButton === undefined) return;

    const toPanel = document.getElementById(toButton.getAttribute('aria-controls'));
    Motion.animate(toPanel, { transform: ['translateY(2rem)', 'translateY(0)'], opacity: [0, 1] }, { duration: theme.config.motionReduced ? 0 : 0.15 });

    const motionList = toPanel.querySelector('motion-list');
    if (motionList && motionList.initialized) {
      motionList.load();
    }
  }

  unload() {
    const fromButton = this.buttons[parseInt(this.selectedIndex)];
    if (fromButton === undefined) return;

    const fromPanel = document.getElementById(fromButton.getAttribute('aria-controls'));
    Motion.animate(fromPanel, { transform: ['translateY(0)', 'translateY(2rem)'], opacity: [1, 0] }, { duration: theme.config.motionReduced ? 0 : 0.15 });

    const motionList = fromPanel.querySelector('motion-list');
    if (motionList && motionList.initialized) {
      motionList.unload();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons.forEach((button, index) => {
      button.classList.toggle('button--primary', index === parseInt(newValue));
      button.classList.toggle('button--secondary', index !== parseInt(newValue));
    });
    this.syncTabs();
    this.scrollSelectedTabIntoView();

    this.indicators.forEach((indicators, index) => {
      indicators.hidden = index !== parseInt(newValue);
      if (index === parseInt(newValue)) {
        indicators.querySelectorAll('button').forEach((button) => {
          button.unload();
          button.load();
        });
      }
    });

    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      // Selection can arrive without a prior hover on that button (keyboard
      // arrows, group-label hover jumping to the first child, touch tap) —
      // make sure a lazy panel's cards are fetched the moment it's shown.
      if (this._lazyInit) this.loadLazyPanel(parseInt(newValue));
      const fromButton = this.buttons[parseInt(oldValue)];
      const toButton = this.buttons[parseInt(newValue)];
      this.transition(document.getElementById(fromButton.getAttribute('aria-controls')), document.getElementById(toButton.getAttribute('aria-controls')));
    }
  }

  async transition(fromPanel, toPanel) {
    // Instant swap (no fade-out-then-fade-in gap) — used by the Product Tab
    // Mega Menu so hovering between tabs never flashes a blank panel.
    if (this.hasAttribute('data-instant')) {
      // The panels share one container (.mega-menu__item--panels); its height
      // follows whichever panel is visible. Swapping instantly makes that height
      // jump when tabs have different content heights (e.g. 1-line vs 2-line
      // product titles). Measure before, swap, then tween the container height
      // from the old value to the new so the dropdown grows/shrinks smoothly.
      const container = toPanel.parentElement;
      const animateHeight = container && container.classList.contains('mega-menu__item--panels') && !theme.config.motionReduced;
      const startHeight = animateHeight ? container.offsetHeight : 0;

      if (fromPanel) fromPanel.hidden = true;
      toPanel.hidden = false;
      const motionList = toPanel.querySelector('motion-list');
      if (motionList && motionList.initialized) motionList.load();

      if (animateHeight) {
        // Cancel any in-flight height tween so rapid tab hovering re-targets
        // from the current (interpolated) height instead of fighting it.
        if (container._heightCleanup) container._heightCleanup();
        const endHeight = container.offsetHeight;
        if (startHeight !== endHeight) {
          container.style.overflow = 'hidden';
          container.style.height = `${startHeight}px`;
          container.offsetHeight; // force reflow so the start height is committed
          container.style.transition = 'height 0.3s cubic-bezier(0.3, 1, 0.3, 1)';
          container.style.height = `${endHeight}px`;
          const cleanup = () => {
            container.style.height = '';
            container.style.transition = '';
            container.style.overflow = '';
            container.removeEventListener('transitionend', onEnd);
            container._heightCleanup = null;
          };
          const onEnd = (event) => {
            if (event.target === container && event.propertyName === 'height') cleanup();
          };
          container._heightCleanup = cleanup;
          container.addEventListener('transitionend', onEnd);
        }
      }
      return;
    }

    if (fromPanel) {
      await Motion.animate(fromPanel, { transform: ['translateY(0)', 'translateY(2rem)'], opacity: [1, 0] }, { duration: theme.config.motionReduced ? 0 : 0.15 }).finished;
    }

    fromPanel.hidden = true;
    toPanel.hidden = false;
    Motion.animate(toPanel, { transform: ['translateY(2rem)', 'translateY(0)'], opacity: [0, 1] }, { duration: theme.config.motionReduced ? 0 : 0.15 });

    const motionList = toPanel.querySelector('motion-list');
    if (motionList && motionList.initialized) {
      motionList.load();
    }
  }
}
customElements.define('tabs-element', TabsElement);

// Product Tab Mega Menu: show only as many product cards as fit ONE row at the
// current viewport width. The CSS grid auto-fills column tracks by width; here
// we read how many tracks resolved and hide every card past that count with
// display:none — so the grid is always a clean single row (no clipped
// second-row cards, no shadow bleed, no horizontal scroll). Wider screens
// resolve more tracks and reveal more cards.
//
// Must run while the menu is OPEN: a closed <details> hides its content (zero
// width) so the grid would resolve to a single column. We therefore recompute
// on open (and on resize), and never hide cards when the grid isn't laid out.
function fitProductTabMegaGrids(scope) {
  (scope || document).querySelectorAll('.mega-menu__list--tabs').forEach((list) => {
    const grids = list.querySelectorAll('.mega-menu__product-grid');
    if (!grids.length) return;

    // Column count is width-driven and identical for every panel, so measure
    // the first grid that's actually laid out (closed menu / hidden panels have
    // zero width and must be skipped, else we'd wrongly collapse to 1 column).
    let cols = 0;
    grids.forEach((grid) => {
      if (cols || !grid.clientWidth) return;
      const template = getComputedStyle(grid).gridTemplateColumns;
      if (template && template !== 'none') {
        const count = template.split(' ').filter(Boolean).length;
        if (count > 0) cols = count;
      }
    });
    if (!cols) return; // not laid out yet — leave every card visible

    grids.forEach((grid) => {
      Array.from(grid.children).forEach((card, index) => {
        card.classList.toggle('mm-card--hidden', index >= cols);
      });
    });
  });
}

// Bind each Product Tab Mega Menu to recompute when it opens (hover or click).
function initProductTabMegaFit() {
  document.querySelectorAll('details[is="details-mega"]').forEach((details) => {
    if (details._mmFitBound || !details.querySelector('.mega-menu__list--tabs')) return;
    details._mmFitBound = true;
    const run = () => requestAnimationFrame(() => fitProductTabMegaGrids(details));
    details.addEventListener('toggle', () => { if (details.open) run(); });
    details.addEventListener('pointerenter', run);
    details.addEventListener('focusin', run);
  });
}

let _megaMenuFitFrame;
function scheduleMegaMenuFit() {
  cancelAnimationFrame(_megaMenuFitFrame);
  _megaMenuFitFrame = requestAnimationFrame(() => fitProductTabMegaGrids());
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductTabMegaFit);
} else {
  initProductTabMegaFit();
}
window.addEventListener('resize', scheduleMegaMenuFit);
// Editor: a section re-render replaces the header DOM, dropping the toggle
// bindings made at page load — re-bind and re-fit so overflow cards are
// hidden again in the fresh markup.
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    initProductTabMegaFit();
    scheduleMegaMenuFit();
  });
}

// Menu overflow (Priority+): the list is CSS-clamped to one line (nowrap +
// overflow hidden) so the first paint can never show a second line; this
// decides which trailing items hide behind the overflow button (which
// opens the menu drawer, where the full menu lives). Overflow is detected
// horizontally via scrollWidth. The clamp cannot clip the absolutely
// positioned dropdown/mega panels — their containing block (.header) is
// outside the list, so they escape its overflow.
function fitMenuOverflow() {
  document.querySelectorAll('ul[data-menu-overflow]').forEach((list) => {
    const button = list.querySelector('[data-menu-overflow-item]');
    if (!button) return;
    const items = Array.from(list.children).filter((el) => el !== button);

    items.forEach((li) => li.classList.remove('menu__item--overflowed'));
    button.classList.add('hidden');
    if (!list.clientWidth) return; // not laid out (mobile / closed)

    const fits = () => list.scrollWidth <= list.clientWidth + 1;

    if (fits()) return;
    button.classList.remove('hidden');
    for (let i = items.length - 1; i >= 0 && !fits(); i--) {
      items[i].classList.add('menu__item--overflowed');
    }
  });
}

let _menuOverflowFrame;
function scheduleMenuOverflowFit() {
  cancelAnimationFrame(_menuOverflowFrame);
  _menuOverflowFrame = requestAnimationFrame(fitMenuOverflow);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', fitMenuOverflow);
} else {
  fitMenuOverflow();
}
// fonts change item widths — refit once everything has loaded
window.addEventListener('load', scheduleMenuOverflowFit);
window.addEventListener('resize', scheduleMenuOverflowFit);
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', scheduleMenuOverflowFit);
}

// Optional hover-open for the overflow button (header setting
// menu_overflow_hover -> data-hover-open). Delegated so it survives editor
// section re-renders; fine pointers only, and only while the drawer is
// closed so repeated pointerover events can't re-trigger it.
if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
  document.addEventListener('pointerover', (event) => {
    const button = event.target.closest && event.target.closest('[data-menu-overflow-item] button[data-hover-open]');
    if (!button) return;
    const drawer = document.getElementById(button.getAttribute('aria-controls'));
    if (!drawer || drawer.hasAttribute('open')) return;
    button.click();
    // The modal's a11y code focuses inside the drawer after the open
    // transition — right for click/keyboard opens (the browser suppresses
    // the ring after a real pointer-down), but a hover-open has none, so
    // the focus ring appears out of nowhere. Blur that one automatic
    // focus; Escape-to-close and tabbing afterwards still work.
    const blurAutoFocus = (e) => {
      if (drawer.contains(e.target)) e.target.blur();
    };
    document.addEventListener('focusin', blurAutoFocus, { once: true, capture: true });
    setTimeout(() => document.removeEventListener('focusin', blurAutoFocus, { capture: true }), 1500);
  });
}

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get date() {
    return this._date = this._date || new Date(this.getAttribute('data-expires'));
  }

  get isCompact() {
    return this.getAttribute('data-compact') === 'true' || (this.getAttribute('data-compact') === 'mobile' && theme.config.mqlSmall);
  }

  init() {
    this.calculate();
    this.timerInterval = setInterval(this.calculate.bind(this), 1000);
  }

  calculate() {
    const now = new Date(),
      countTo = new Date(this.date),
      timeDifference = (countTo - now);

    if (timeDifference < 0) {
      this.complete();
      return;
    }

    const secondsInADay = 60 * 60 * 1000 * 24,
      secondsInAHour = 60 * 60 * 1000;

    const days = Math.floor(timeDifference / (secondsInADay) * 1);
    const hours = Math.floor((timeDifference % (secondsInADay)) / (secondsInAHour) * 1);
    const mins = Math.floor(((timeDifference % (secondsInADay)) % (secondsInAHour)) / (60 * 1000) * 1);
    const secs = Math.floor((((timeDifference % (secondsInADay)) % (secondsInAHour)) % (60 * 1000)) / 1000 * 1);

    if (this.isCompact) {
      const dayHTML = days > 0 ? `<div class="countdown__item"><p>${days}${theme.dateStrings.d}</p></div>` : '';
      const hourHTML = `<div class="countdown__item"><p>${hours}${theme.dateStrings.h}</p></div>`;
      const minHTML = `<div class="countdown__item"><p>${mins}${theme.dateStrings.m}</p></div>`;
      const secHTML = `<div class="countdown__item"><p>${secs}${theme.dateStrings.s}</p></div>`;

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    }
    else {
      const dayHTML = days > 0 ? `<div class="countdown__item"><p>${days}</p><span>${days === 1 ? theme.dateStrings.day : theme.dateStrings.days}</span></div>` : '';
      const hourHTML = `<div class="countdown__item"><p>${hours}</p><span>${hours === 1 ? theme.dateStrings.hour : theme.dateStrings.hours}</span></div>`;
      const minHTML = `<div class="countdown__item"><p>${mins}</p><span>${mins === 1 ? theme.dateStrings.minute : theme.dateStrings.minutes}</span></div>`;
      const secHTML = `<div class="countdown__item"><p>${secs}</p><span>${secs === 1 ? theme.dateStrings.second : theme.dateStrings.seconds}</span></div>`;

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    }
  }

  complete() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.hidden = true;
  }
}
customElements.define('countdown-timer', CountdownTimer);

class ImageComparison extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, async () => {
      await theme.utils.imageLoaded(this.media);
      this.init();
    });
  }

  get button() {
    return this.querySelector('button');
  }

  get bounding() {
    return this.getBoundingClientRect();
  }

  get horizontal() {
    return this.getAttribute('data-layout') === 'horizontal';
  }

  get media() {
    return Array.from(this.querySelectorAll('img, svg'));
  }

  init() {
    this.active = false;

    this.button.addEventListener('touchstart', this.startHandler.bind(this), theme.supportsPassive ? { passive: false } : false);
    document.body.addEventListener('touchend', this.endHandler.bind(this), theme.supportsPassive ? { passive: true } : false);
    document.body.addEventListener('touchmove', this.onHandler.bind(this), theme.supportsPassive ? { passive: true } : false);

    this.button.addEventListener('mousedown', this.startHandler.bind(this));
    document.body.addEventListener('mouseup', this.endHandler.bind(this));
    document.body.addEventListener('mousemove', this.onHandler.bind(this));

    setTimeout(() => this.animate(), 1e3);
  }

  animate() {
    this.setAttribute('animate', '');

    this.classList.add('animated');
    setTimeout(() => {
      this.classList.remove('animated');
    }, 1e3);
  }

  startHandler(event) {
    event.preventDefault();
    this.active = true;
    this.classList.add('scrolling');
  }

  endHandler() {
    this.active = false;
    this.classList.remove('scrolling');
  }

  onHandler(e) {
    if (!this.active) return;

    const event = (e.touches && e.touches[0]) || e;
    let x = this.horizontal
      ? event.pageX - (this.bounding.left + window.scrollX)
      : event.pageY - (this.bounding.top + window.scrollY);

    this.scrollIt(x);
  }

  scrollIt(x) {
    const distance = this.horizontal ? this.clientWidth : this.clientHeight;

    const max = distance - 20;
    const min = 20;
    const mouseX = Math.max(min, (Math.min(x, max)));
    const mousePercent = (mouseX * 100) / distance;
    this.style.setProperty('--percent', mousePercent + '%');
  }
}
customElements.define('image-comparison', ImageComparison);

class LookbookElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, async () => {
      await theme.utils.imageLoaded(this.media);
      this.init();
    });
  }

  get media() {
    return Array.from(this.querySelectorAll('img, svg'));
  }

  get items() {
    return this._items = this._items || Array.from(this.querySelectorAll('.hotspot'));
  }

  init() {
    this.items.forEach((item) => {
      item.addEventListener('mouseenter', (event) => {
        this.select(this.items.indexOf(event.target));
      });
    });

    this.items.forEach((item) => {
      item.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.select(this.items.indexOf(event.target));
        }
      });
    });

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:select', this.animate.bind(this));
      section.addEventListener('shopify:section:deselect', this.closeAll.bind(this));
      this.addEventListener('shopify:block:select', (event) => this.open(this.items.indexOf(event.target)));
    }

    setTimeout(() => this.animate(), 1e3);
  }

  animate() {
    this.openAll();

    this.timer = setTimeout(() => this.closeAll(), 3e3);
  }

  open(selectedIndex) {
    this.items.forEach((item, index) => item.classList.toggle('active', selectedIndex === index));
  }

  openAll() {
    this.items.forEach((item) => item.classList.add('active'));
  }

  closeAll() {
    this.items.forEach((item) => item.classList.remove('active'));
    clearTimeout(this.timer);
  }

  select(selectedIndex) {
    const selectedItem = this.items[selectedIndex];
    if (!selectedItem || selectedItem.getAttribute('aria-current') === 'true') {
      return;
    }

    this.items.forEach((item, index) => item.setAttribute('aria-current', selectedIndex === index ? 'true' : 'false'));
    this.dispatchEvent(new CustomEvent('lookbook:change', { bubbles: true, detail: { index: selectedIndex } }));
  }
}
customElements.define('lookbook-element', LookbookElement);

class ShopTheLook extends HTMLElement {
  constructor() {
    super();

    this.lookbook.addEventListener('lookbook:change', (event) => this.carousel.select(event.detail.index));
    this.carousel.addEventListener('carousel:change', (event) => this.lookbook.select(event.detail.index));
  }

  get lookbook() {
    return this.querySelector('lookbook-element');
  }

  get carousel() {
    return this.querySelector('carousel-element');
  }
}
customElements.define('shop-the-look', ShopTheLook);

class SpinningText extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get string() {
    let string = this.getAttribute('data-string');
    string = string.replace(/  +/g, ' ');
    return '•' + string.replace(/ +/g, '•')
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    const canTrig = CSS.supports('(top: calc(sin(1) * 1px))');
    const OPTIONS = {
      TEXT: this.string,
      SIZE: 2,
      SPACING: 2,
    };

    const HEADING = document.createElement('div');
    const text = OPTIONS.TEXT;

    // Take the text and split it into spans...
    const chars = text.split('');
    this.style.setProperty('--char-count', chars.length);

    for (let c = 0; c < chars.length; c++) {
      HEADING.innerHTML += `<span aria-hidden="true" class="split-char" style="--char-index: ${c};">${chars[c]}</span>`;
    }
    HEADING.innerHTML += `<span class="sr-only">${OPTIONS.TEXT}</span>`;
    HEADING.classList = 'split-chars';

    // Set the styles
    this.style.setProperty('--font-size', OPTIONS.SIZE);
    this.style.setProperty('--character-width', OPTIONS.SPACING);
    this.style.setProperty(
      '--radius',
      canTrig
        ? 'calc((var(--character-width) / sin(var(--inner-angle))) * -1ch)'
        : `calc(
        (${OPTIONS.SPACING} / ${Math.sin(
          360 / this.children.length / (180 / Math.PI)
        )})
        * -1ch
      )`
    );

    // Append
    this.appendChild(HEADING);
  }
}
customElements.define('spinning-text', SpinningText);

class SlideshowElement extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;

    if (this.hasAttribute('data-instant-init')) {
      requestAnimationFrame(this.init.bind(this));
    }
    else if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
    else {
      new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  // Every slide, in authored order, read once before Flickity moves the cells.
  get allItems() {
    return this._allItems = this._allItems || Array.from(this.children);
  }

  // The slides that show on this breakpoint. A slide the merchant hid for
  // phones or desktop (slider.liquid Visibility, v2.10.17) carries
  // data-hide-mobile / data-hide-desktop; it is left out of the carousel, so
  // the dots, autoplay and wrap-around count only what is visible.
  get items() {
    return this._items = this._items || this.allItems.filter((item) => !this.isHiddenHere(item));
  }

  get hasHiddenSlides() {
    return this.allItems.some((item) => item.hasAttribute('data-hide-mobile') || item.hasAttribute('data-hide-desktop'));
  }

  isHiddenHere(item) {
    return item.hasAttribute(theme.config.mqlSmall ? 'data-hide-mobile' : 'data-hide-desktop');
  }

  // The slide's authored index (data-index) — what the words and dots are keyed on.
  // Falls back to the cell index for slideshows without data-index.
  indexOf(item) {
    const authored = item?.getAttribute('data-index');
    return authored !== null && authored !== undefined ? parseInt(authored) : this.items.indexOf(item);
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get speed() {
    return this.hasAttribute('autoplay') ? parseInt(this.getAttribute('autoplay-speed')) * 1000 : 5000;
  }

  get pauseAutoPlayOnHover() {
    if (this.getAttribute('pause-autoplay-on-hover') === 'false') return false;
    if (this.getAttribute('pause-autoplay-on-hover') === 'true') return true;

    return true;
  }

  get wrapAround() {
    if (this.getAttribute('wrap-around') === 'false') return false;
    if (this.getAttribute('wrap-around') === 'true') return true;

    return true;
  }

  get adaptiveHeightProperty() {
    return false;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    const that = this;
    if (this.hasHiddenSlides && !this.breakpointBound) {
      // Re-build for the other breakpoint's set of slides.
      this.breakpointBound = true;
      const rebuild = () => this.rebuild();
      document.addEventListener('matchSmall', rebuild);
      document.addEventListener('unmatchSmall', rebuild);
    }
    if (this.items.length > 1) {
      this.slider = new Flickity(this, {
        accessibility: false,
        pageDots: false,
        prevNextButtons: false,
        // Flickity's cellSelector also searches inside each child, so it is keyed on
        // the slides' own data-index: the slide elements, not what they contain.
        cellSelector: this.hasHiddenSlides ? (theme.config.mqlSmall ? '[data-index]:not([data-hide-mobile])' : '[data-index]:not([data-hide-desktop])') : undefined,
        wrapAround: this.wrapAround,
        rightToLeft: theme.config.rtl,
        autoPlay: this.autoplay ? this.speed : false,
        pauseAutoPlayOnHover: this.pauseAutoPlayOnHover,
        adaptiveHeightProperty: this.adaptiveHeightProperty,
        on: {
          ready: function () {
            const { selectedElement } = this;
            that.onReady(selectedElement);
          }
        }
      });

      this.slider.on('change', this.onChange.bind(this));
      this.slider.on('settle', this.onSettle.bind(this));

      // Bound once: a breakpoint rebuild re-enters init() and must not stack a
      // second set of listeners (two 'next' handlers skip a slide).
      if (!this.eventsBound) {
        this.eventsBound = true;
        this.addEventListener('slider:previous', () => this.slider?.previous());
        this.addEventListener('slider:next', () => this.slider?.next());
        this.addEventListener('slider:play', () => this.slider?.playPlayer());
        this.addEventListener('slider:pause', () => this.slider?.pausePlayer());

        if (Shopify.designMode) {
          this.addEventListener('shopify:block:select', (event) => {
            const index = this.items.indexOf(event.target);
            if (index >= 0) this.slider?.select(index);
          });
        }
      }
    }
    else if (this.items.length === 1) {
      const selectedElement = this.items[0];
      that.onReady(selectedElement);
    }

    if (this.hasHiddenSlides && this.items.length) {
      // The words and dots start on the first authored slide; point them at the
      // first slide that actually shows here.
      this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { currentPage: this.indexOf(this.items[0]) } }));
    }
  }

  // Tear down and start again for the current breakpoint: Flickity's destroy
  // re-appends its cells after the children it skipped, so the authored order
  // is restored first or the next build would run the slides out of sequence.
  rebuild() {
    if (!this.initialized) return;
    if (this.slider) {
      this.slider.destroy();
      this.slider = null;
    }
    this.allItems.forEach((item) => this.appendChild(item));
    this._items = null;
    this.initialized = false;
    this.setAttribute('selected-index', '0');
    this.init();
  }

  disconnectedCallback() {
    if (this.slider) this.slider.destroy();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const fromElement = this.items[parseInt(oldValue)];
      const toElement = this.items[parseInt(newValue)];

      const animateElement = toElement.querySelector('animate-element');
      animateElement?.refresh();

      // Start the incoming video right away. The OUTGOING video is paused in
      // onSettle (after the slide finishes), NOT here — pausing on change fires
      // at the drag midpoint, which dropped [playing] and let the poster cover
      // the still-visible video mid-slide ("turns into thumbnail" bug).
      if (toElement.getAttribute('data-type') === 'video') {
        const toVideoElement = theme.utils.visibleMedia(toElement.querySelectorAll('video-media'));
        toVideoElement?.play();
      }

      this.preloadAdjacentSlides(parseInt(newValue));
    }
  }

  onChange() {
    this.selectedIndex = this.slider.selectedIndex;
    this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { currentPage: this.indexOf(this.items[this.slider.selectedIndex]) } }));
  }

  // Pause every video that isn't the now-settled slide. Deferring to 'settle'
  // (rather than 'change') keeps the outgoing video playing through the slide
  // animation, so it stays live until it's off-screen instead of snapping to
  // its poster halfway through the drag.
  onSettle() {
    const selected = this.items[this.slider.selectedIndex];
    this.items.forEach((item) => {
      if (item !== selected && item.getAttribute('data-type') === 'video') {
        const videoElement = theme.utils.visibleMedia(item.querySelectorAll('video-media'));
        videoElement?.pause();
      }
    });
  }

  // `selectedIndex` is the authored index the dots send; map it to the cell.
  select(selectedIndex) {
    if (!this.slider) return;
    const cell = this.items.findIndex((item) => this.indexOf(item) === selectedIndex);
    this.slider.select(cell >= 0 ? cell : selectedIndex);
  }

  onReady(selectedElement) {
    if (selectedElement.getAttribute('data-type') === 'video') {
      const videoElement = theme.utils.visibleMedia(selectedElement.querySelectorAll('video-media'));
      videoElement?.play();
    }

    if (!theme.config.isTouch) {
      const animateElement = selectedElement.querySelector('animate-element');
      animateElement?.refresh();
    }

    this.preloadAdjacentSlides(this.selectedIndex);
  }

  preloadAdjacentSlides(currentIndex) {
    if (!this.items || this.items.length <= 1) return;

    // Preload next slide
    const nextIndex = (currentIndex + 1) % this.items.length;
    const nextElement = this.items[nextIndex];
    if (nextElement && nextElement.getAttribute('data-type') === 'video') {
      const nextVideoMediaElements = nextElement.querySelectorAll('video-media');
      nextVideoMediaElements.forEach((videoMedia) => {
        if (videoMedia && !videoMedia.hasAttribute('loaded')) {
          const p = videoMedia.player;
        }
      });
    }

    // Preload previous slide (since we support wrapAround, or for backward swiping)
    const prevIndex = (currentIndex - 1 + this.items.length) % this.items.length;
    if (prevIndex !== nextIndex) {
      const prevElement = this.items[prevIndex];
      if (prevElement && prevElement.getAttribute('data-type') === 'video') {
        const prevVideoMediaElements = prevElement.querySelectorAll('video-media');
        prevVideoMediaElements.forEach((videoMedia) => {
          if (videoMedia && !videoMedia.hasAttribute('loaded')) {
            const p = videoMedia.player;
          }
        });
      }
    }
  }
}
customElements.define('slideshow-element', SlideshowElement);

class SlideshowHero extends SlideshowElement {
  constructor() {
    super();

    window.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)), false);
  }

  get adaptiveHeightProperty() {
    return true;
  }

  get heroSection() {
    return this.closest('.hero-section');
  }

  get nextSection() {
    let nextSibling = this.heroSection.nextElementSibling;
    for (let i = 0; i < 20; i++) {
      if (nextSibling.classList.contains('shopify-section') && !nextSibling.classList.contains('header-section')) break;
      nextSibling = nextSibling.nextElementSibling;
    }

    if (nextSibling && nextSibling.nodeType != 1) {
      nextSibling = document.getElementById('PageContainer');
    }

    return nextSibling;
  }

  onScrollHandler() {
    const bounding = this.nextSection.getBoundingClientRect();
    this.heroSection.classList.toggle('hero-scrolled', bounding.y < -30);
  }
}
customElements.define('slideshow-hero', SlideshowHero);

class SlideshowWords extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
    this.transitionId = 0;

    if (this.controlledElement) {
      this.controlledElement.addEventListener('slider:change', (event) => this.selectedIndex = event.detail.currentPage);
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      this.transition(this.items[parseInt(oldValue)], this.items[parseInt(newValue)]);
    }
  }

  async transition(fromElement, toElement) {
    const transitionId = ++this.transitionId;
    const fromWords = Array.from(fromElement.querySelectorAll('animate-element'));
    const toWords = Array.from(toElement.querySelectorAll('animate-element'));

    await Promise.all(fromWords.map((element) => element.reset()));

    if (transitionId !== this.transitionId) return;

    this.items.forEach((item) => {
      item.setAttribute('aria-current', parseInt(item.getAttribute('data-index')) === parseInt(this.selectedIndex) ? 'true' : 'false');
    });

    await Promise.all(toWords.map((element) => element.refresh()));
  }
}
customElements.define('slideshow-words', SlideshowWords, { extends: 'nav' });

class TimedContentHide extends HTMLElement {
  constructor() {
    super();

    this.sync = this.sync.bind(this);
    this.onVideoPlay = this.onVideoPlay.bind(this);
    this.onVideoPause = this.onVideoPause.bind(this);
  }

  connectedCallback() {
    this.hideDelay = parseInt(this.getAttribute('data-hide-delay') || '0', 10);
    this.activeAttribute = this.getAttribute('data-active-attribute');
    this.requirePlaying = this.hasAttribute('data-require-playing');

    if (this.hideDelay <= 0 || this.hiddenPermanently) return;

    if (this.activeAttribute) {
      this.mutationObserver = new MutationObserver(() => this.sync());
      this.mutationObserver.observe(this, { attributes: true, attributeFilter: [this.activeAttribute] });
    }

    if (this.mediaElement) {
      this.mediaElement.addEventListener('video:play', this.onVideoPlay);
      this.mediaElement.addEventListener('video:pause', this.onVideoPause);
    }

    this.sync();
  }

  disconnectedCallback() {
    this.clearTimer();
    this.mutationObserver?.disconnect();

    if (this.mediaElement) {
      this.mediaElement.removeEventListener('video:play', this.onVideoPlay);
      this.mediaElement.removeEventListener('video:pause', this.onVideoPause);
    }
  }

  get hiddenPermanently() {
    return this.classList.contains('timed-content-hide--hidden');
  }

  get controlledElement() {
    const navElement = this.closest('nav[is="slideshow-words"]');
    const controlledId = navElement?.getAttribute('aria-controls');
    return controlledId ? document.getElementById(controlledId) : null;
  }

  get mediaElement() {
    if (this._mediaElement !== undefined) return this._mediaElement;

    let mediaElement = null;
    const controlledElement = this.controlledElement;
    if (controlledElement) {
      const mediaIndex = this.getAttribute('data-media-index') || this.getAttribute('data-index');
      const banner = controlledElement.querySelector(`.banner[data-index="${mediaIndex}"]`);
      mediaElement = theme.utils.visibleMedia(banner?.querySelectorAll('video-media') || []);
    } else {
      mediaElement = theme.utils.visibleMedia(this.closest('.shopify-section, .section')?.querySelectorAll('video-media') || []);
    }

    this._mediaElement = mediaElement;
    return this._mediaElement;
  }

  get active() {
    return !this.activeAttribute || this.getAttribute(this.activeAttribute) === 'true';
  }

  get playing() {
    return !this.requirePlaying || !this.mediaElement || this.mediaElement.hasAttribute('playing');
  }

  sync() {
    if (this.hiddenPermanently) {
      this.clearTimer();
      return;
    }

    if (!this.active || !this.playing) {
      this.clearTimer();
      return;
    }

    if (!this.timer) {
      this.timer = window.setTimeout(() => this.hideContent(), this.hideDelay);
    }
  }

  clearTimer() {
    if (!this.timer) return;

    window.clearTimeout(this.timer);
    this.timer = null;
  }

  hideContent() {
    this.clearTimer();

    if (this.hiddenPermanently) return;

    this.classList.add('timed-content-hide--hidden');
  }

  onVideoPlay() {
    this.sync();
  }

  onVideoPause() {
    if (!this.hiddenPermanently) {
      this.clearTimer();
    }
  }
}
if (!customElements.get('timed-content-hide')) {
  customElements.define('timed-content-hide', TimedContentHide);
}

class SlideshowParallax extends HTMLDivElement {
  constructor() {
    super();

    this.load();
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get mobileDisabled() {
    return false;
  }

  load() {
    if (theme.config.motionReduced) return;
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return;

    this.motion();
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, { transform: [`translateY(${this.getAttribute('data-start')})`, `translateY(${this.getAttribute('data-stop')})`] }),
      { target: this.controlledElement ?? document.body, offset: Motion.ScrollOffset.Exit }
    );
  }
}
customElements.define('slideshow-parallax', SlideshowParallax, { extends: 'div' });

class ControlButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);

    this.pauseLabel = this.getAttribute('data-pause-label');
    this.playLabel = this.getAttribute('data-play-label');
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  static get observedAttributes() {
    return ['paused'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'paused') {
      this.updateAriaLabel();

      if (oldValue === null && newValue === '') {
        return (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:pause', { bubbles: true }));
      }

      if (newValue === null) {
        return (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:play', { bubbles: true }));
      }
    }
  }

  onClick() {
    this.paused ? this.removeAttribute('paused') : this.setAttribute('paused', '');
  }

  updateAriaLabel() {
    this.setAttribute('aria-label', this.paused ? this.playLabel : this.pauseLabel);
  }
}
customElements.define('control-button', ControlButton, { extends: 'button' });

class QuickView extends XModal {
  constructor() {
    super();
  }

  get selector() {
    return '.quick-view__content';
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.hide.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  beforeShow() {
    super.beforeShow();
    this.quickview();
  }

  afterShow() {
    super.afterShow();

    document.dispatchEvent(new CustomEvent('quickview:open', {
      detail: {
        productUrl: this.getAttribute('data-product-url')
      }
    }));
  }

  afterHide() {
    super.afterHide();

    const drawerContent = this.querySelector(this.selector);
    drawerContent.innerHTML = '';
  }

  quickview() {
    const drawerContent = this.querySelector(this.selector);
    const productUrl = this.getAttribute('data-product-url').split('?')[0];
    const sectionUrl = `${productUrl}?view=modal`;

    fetch(sectionUrl)
      .then(response => response.text())
      .then(responseText => {
        setTimeout(() => {
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          // The modal page also contains empty .quick-view__content stubs from
          // any product card rendered before the modal section (header mega
          // menus especially), so scope to the modal's own wrapper — the only
          // quick-view in the fetched document carrying open/active.
          const productElement = parsedHTML.querySelector(`.quick-view[open] ${this.selector}`) || parsedHTML.querySelector(this.selector);
          if (!productElement || productElement.innerHTML.trim() === '') {
            window.location.href = productUrl;
            return;
          }
          this.setInnerHTML(drawerContent, productElement.innerHTML);
          theme.a11y.trapFocus(this, this.focusElement);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: {
              productUrl: this.getAttribute('data-product-url')
            }
          }));
        }, 200);
      })
      .catch(e => {
        console.error(e);
      });
  }

  setInnerHTML(element, innerHTML) {
    element.innerHTML = innerHTML;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
customElements.define('quick-view', QuickView);

class FiltersToggle extends HoverButton {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);

    if (theme.config.hasLocalStorage) {
      const expanded = window.localStorage.getItem(`${theme.settings.themeName}:filters-toggle`) || 'false';
      this.onToggle(expanded === 'true');
    }
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get expanded() {
    return this.getAttribute('aria-expanded') === 'true';
  }

  onClick() {
    this.onToggle(this.expanded);
  }

  onToggle(expanded) {
    this.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    if (this.controlledElement) this.controlledElement.classList.toggle('xl:block', !expanded);

    if (theme.config.hasLocalStorage) {
      window.localStorage.setItem(`${theme.settings.themeName}:filters-toggle`, expanded);
    }
  }
}
customElements.define('filters-toggle', FiltersToggle, { extends: 'button' });

class RevealBanner extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
  }

  init() {
    const tracker = this.querySelector('.reveal-banner__tracker');
    Motion.scroll(
      Motion.animate(this.querySelectorAll('.banner__media'), { clipPath: ['inset(20% 35% 0% 35% round var(--rounded-block))', 'inset(0% 0%)'] }, { easing: 'linear' }),
      { target: tracker, offset: Motion.ScrollOffset.Enter }
    );

    const tracker2nd = this.querySelector('.reveal-banner__tracker2nd');
    if (tracker2nd) {
      Motion.scroll(Motion.timeline([
        [this.querySelector('.banner__overlay'), { opacity: [0, 1] }, { easing: 'linear' }],
        [this.querySelector('.banner__content'), { opacity: [0, 1], visibility: ['hidden', 'visible'], transform: ['translateY(10%)', 'translateY(0)'] }, { easing: 'linear', at: '<' }],
      ]), { target: tracker2nd, offset: Motion.ScrollOffset.Enter });
    }
  }
}
customElements.define('reveal-banner', RevealBanner);

class TestimonialParallax extends ParallaxElement {
  constructor() {
    super();
  }

  get mobileDisabled() {
    return true;
  }
}
customElements.define('testimonial-parallax', TestimonialParallax, { extends: 'div' });

class SplittingBanner extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
  }

  init() {
    const splitting = Splitting({ target: this.querySelector('.splitting'), by: 'chars', whitespace: true });

    const wrapper = this.querySelector('.splitting-wrapper');
    const tracker = this.querySelector('.reveal-banner__tracker');
    Motion.scroll(Motion.timeline([
      [splitting[0].chars, { opacity: [0.3, 1] }, { delay: theme.config.motionReduced ? 0 : Motion.stagger(0.1) }],
      //[wrapper, { transform: ['translateY(-20vh)', 'translateY(0)'] }, { easing: 'linear', at: '<' }]
    ]), { target: tracker, offset: Motion.ScrollOffset.Enter });

    Motion.scroll(
      Motion.animate(wrapper, { opacity: theme.config.mqlSmall ? [1, 0, 0] : [1, 1, 0] }, { easing: 'linear' }),
      { target: tracker, offset: Motion.ScrollOffset.Exit }
    );

    const tracker2nd = this.querySelector('.reveal-banner__tracker2nd');
    if (tracker2nd) {
      Motion.scroll(
        Motion.animate(this.querySelector('.button-wrapper'), { opacity: [0, 1, 1], transform: ['translateY(100%)', 'translateY(0)'] }, { easing: 'linear' }),
        { target: tracker2nd, offset: Motion.ScrollOffset.Enter }
      );
    }
  }
}
customElements.define('splitting-banner', SplittingBanner);

class ProductCardInfo extends ProductInfo {
  constructor() {
    super();

    this._sectionId = 'ProductCard-template';
  }

  get pickerType() {
    return this.getAttribute('data-picker-type');
  }

  buildRequestUrlWithParams(url, optionValues) {
    const params = [];
    params.push('view=bundle-card');

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }

    return `${url}?${params.join('&')}`;
  }

  updateSourceFromDestination(parsedHTML, id) {
    const source = parsedHTML.getElementById(`${id}-${this._sectionId}-${this.productId}`);
    const destination = document.querySelector(`#${id}-${this.sectionId}-${this.productId}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
      destination.removeAttribute('hidden');
      // The wrapper's own visibility class follows the new variant too (Inventory is
      // sr-only for an untracked variant and visible for a tracked one) (v2.10.25).
      destination.classList.toggle('sr-only', source.classList.contains('sr-only'));
    }
  }

  updateOptionValues(parsedHTML) {
    const variantPicker = parsedHTML.getElementById(`VariantPicker-${this._sectionId}-${this.productId}-${this.pickerType}`);
    if (variantPicker) {
      theme.HTMLUpdateUtility.viewTransition(this.variantSelectors, variantPicker, this.preProcessHtmlCallbacks);
    }
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector(`product-card-info[data-product-id="${this.productId}"] [data-selected-variant]`)?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }
}
customElements.define('product-card-info', ProductCardInfo);

class ProductBundleForm extends HTMLFormElement {
  constructor() {
    super();

    this.querySelector('[name="id"]').disabled = false;
    this.addEventListener('submit', this.onSubmitHandler);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get submitButton() {
    return this._submitButton = this._submitButton || this.querySelector('[type="submit"]');
  }

  getSelectedVariant() {
    const selectedVariant = this.querySelector('[data-selected-variant]')?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton.hasAttribute('aria-disabled')) return;

    const product = this.closest('.product-card');
    const variant = this.getSelectedVariant();
    variant.default_featured_media = this.hasAttribute('data-product-image') ? this.getAttribute('data-product-image') : '';

    this.submitButton.removeAttribute('aria-busy');
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('bundle:added', { bubbles: true, detail: { product: product, variant: variant } }));
  }

  handleErrorMessage() {
    // todo
  }

  toggleSubmitButton(disable = true, text) {
    if (!this.submitButton) return;

    const submitButtonText = this.submitButton.querySelector('.btn-text');
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text>span');

    if (disable) {
      this.submitButton.setAttribute('disabled', '');
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      }
    }
    else {
      this.submitButton.removeAttribute('disabled');
      (submitButtonTextChild || submitButtonText).textContent = theme.variantStrings.addToBundle;
    }
  }
}
customElements.define('product-bundle-form', ProductBundleForm, { extends: 'form' });

class ProductBundleRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      const variant = event.target.closest('[data-product-bundle-variant]');
      (this.controlledElement ?? this).dispatchEvent(new CustomEvent('bundle:removed', { bubbles: true, detail: { variant: variant } }));
    });
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }
}
customElements.define('product-bundle-remove-button', ProductBundleRemoveButton);

class ProductBundleToggleButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', () => {
      this.controlledElement.classList.toggle('active');
    });
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }
}
customElements.define('product-bundle-toggle-button', ProductBundleToggleButton);

class ProductBundle extends HTMLElement {
  constructor() {
    super();

    this.template;
    this.bundleCount = 0;

    this.addEventListener('bundle:added', this.onAddHandler.bind(this));
    this.addEventListener('bundle:removed', this.onRemoveHandler.bind(this));
    this.addEventListener('change', theme.utils.debounce(this.onChangeHandler.bind(this), 300));
    this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this));
  }

  get bundleMin() {
    return this.hasAttribute('data-minimum') ? parseInt(this.getAttribute('data-minimum')) : 3;
  }

  get bundleMax() {
    const minimum = this.bundleMin;
    const maximum = this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : 3;
    return maximum > minimum ? maximum : minimum;
  }

  get variants() {
    return Array.from(this.querySelectorAll('[data-product-bundle-variant]'));
  }

  get submitButton() {
    return this.querySelector('[data-product-bundle-submit]');
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get preventDuplicate() {
    return this.hasAttribute('data-prevent-duplicate');
  }

  get template() {
    return this._template = this._template || this.querySelector('[data-product-bundle-variant][available]').cloneNode(true);
  }

  onChangeHandler() {
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  onSubmitHandler(event) {
    const validateQuantity = this.variants.every((variant) => {
      const target = variant.querySelector('quantity-input').input;
      const inputValue = parseInt(target.value);
      let message = '';

      if (inputValue < parseInt(target.getAttribute('data-min'))) {
        message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
      }
      else if (inputValue > parseInt(target.max)) {
        message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
      }
      else if (inputValue % parseInt(target.step) !== 0) {
        message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
      }

      if (message) {
        target.setCustomValidity(message);
        target.reportValidity();
        target.value = target.defaultValue;
        target.select();

        return false;
      }

      return true;
    });
    if (!validateQuantity) return;

    const data = {
      items: this.variants.map(variant => ({
        id: variant.getAttribute('data-variant-id'),
        quantity: variant.querySelector('quantity-input').value || 1
      }))
    };

    if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
      theme.utils.postLink2(theme.routes.cart_add_url, {
        parameters: {
          ...data
        }
      });
      return;
    }

    event.preventDefault();
    if (this.submitButton.hasAttribute('aria-disabled')) return;
    this.activeElement = event.submitter || event.currentTarget;

    this.handleErrorMessage();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    const body = JSON.stringify({
      ...data,
      sections: sectionsToBundle,
      sections_url: window.location.pathname
    });

    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.setAttribute('aria-busy', 'true');

    fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          this.handleErrorMessage(parsedState.description);
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: parsedState.description
            }
          }));

          const submitButtonText = this.submitButton.querySelector('.btn-text>span');
          if (submitButtonText && submitButtonText.hasAttribute('data-sold-out')) {
            submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out');
            this.submitButton.setAttribute('aria-disabled', 'true');
            this.error = true;
          }

          const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle', cart: cartJson });

          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
          this.resetVariants();
          return;
        }

        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle', cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: parsedState
          }
        }));

        this.cartDrawer?.show(this.activeElement);
        this.resetVariants();
      })
      .catch((error) => {
      })
      .finally(() => {
        this.submitButton.removeAttribute('aria-busy');

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
        }
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  onAddHandler(event) {
    this.addVariant(event.detail.product, event.detail.variant);
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  onRemoveHandler(event) {
    this.clearVariant(event.detail.variant);
    this.reorderVariants();
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  updateTotal() {
    let subtotal = 0;
    this.variants.forEach((variant) => {
      if (!variant.hasAttribute('available')) {
        const price = variant.querySelector('[data-product-bundle-variant-price]');
        const quantityInput = variant.querySelector('quantity-input');
        subtotal += parseInt(price.getAttribute('data-price')) * parseInt(quantityInput.value);
      }
    });

    const element = this.querySelector('[data-product-bundle-total]');
    element.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyFormat);
  }

  updateTotalWithCurrency() {
    let subtotal = 0;
    this.variants.forEach((variant) => {
      if (!variant.hasAttribute('available')) {
        const price = variant.querySelector('[data-product-bundle-variant-price]');
        const quantityInput = variant.querySelector('quantity-input');
        subtotal += parseInt(price.getAttribute('data-price')) * parseInt(quantityInput.value);
      }
    });

    const element = this.querySelector('[data-product-bundle-total-with-currency]');
    element.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyWithCurrencyFormat);
  }

  updateProgressBar() {
    const percent = 100 * this.bundleCount / this.bundleMin;
    const element = this.querySelector('[data-product-bundle-progress-bar]');
    element.style.setProperty('--progress', `${percent}%`);
  }

  addVariant(product, variant) {
    this.bundleCount++;

    if (this.bundleCount >= this.bundleMin) {
      this.submitButton.disabled = false;
    }
    if (this.bundleCount >= this.bundleMax) {
      this.closest('.product-bundle-wrapper').setAttribute('locked', '');
    }

    let variantElement = this.getAvailableAvriant();
    if (variantElement === null) {
      variantElement = this.template.cloneNode(true);
      variantElement.style.order = this.bundleCount;
      this.querySelector('.product-bundle__body').appendChild(variantElement);
    }

    if (variantElement) {
      const preview_image = variant.featured_media ? variant.featured_media.preview_image.src : variant.default_featured_media;
      const media = variantElement.querySelector('[data-product-bundle-variant-media]');
      if (preview_image.length > 0) {
        media.removeAttribute('aria-hidden');
        media.innerHTML = `<img src="${this.getResizedImageSrc(preview_image, 180)}" srcset="${this.getResizedImageSrc(preview_image, 180)} 180w, ${this.getResizedImageSrc(preview_image, 360)} 360w, ${this.getResizedImageSrc(preview_image, 540)} 540w" loading="lazy" is="lazy-image" />`;
      }

      const content = variantElement.querySelector('[data-product-bundle-variant-content]');
      content.innerHTML = `
        <div class="flex flex-col gap-1">
          <p class="horizontal-product__title font-medium text-base leading-tight">${product.querySelector('[data-product-bundle-title]').textContent}</p>
          ${variant.options.length > 0 ? `
            <ul class="grid gap-1d5">
              ${variant.options.map(option => {
        if (option !== 'Default Title') {
          return `<li class="text-xs text-opacity leading-tight">${option}</li>`;
        }
      }).join('')}
            </ul>
          ` : ''}
        </div>
        <div class="price text-sm flex flex-wrap gap-1d5" data-price="${variant.price}" data-product-bundle-variant-price>
          ${theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat)}
        </div>`;

      variantElement.product = product;
      variantElement.setAttribute('data-variant-id', variant.id);
      variantElement.removeAttribute('available');
      if (this.preventDuplicate) {
        product.setAttribute('locked', '');
      }
    }
  }

  clearVariant(variant) {
    if (this.preventDuplicate) {
      variant.product.removeAttribute('locked');
    }
    variant.setAttribute('available', '');
    variant.removeAttribute('data-variant-id');
    variant.querySelector('[data-product-bundle-variant-media]').innerHTML = '';
    variant.querySelector('[data-product-bundle-variant-media]').classList.remove('loading');
    variant.querySelector('[data-product-bundle-variant-media]').setAttribute('aria-hidden', 'true');
    variant.querySelector('[data-product-bundle-variant-content]').innerHTML = '<span class="horizontal-product__skeleton"></span><span class="horizontal-product__skeleton"></span>';
    variant.querySelector('quantity-input').reset();

    this.bundleCount--;

    if (this.bundleCount < this.bundleMin) {
      this.submitButton.disabled = true;
    }
    else {
      variant.remove();
    }
    if (this.bundleCount < this.bundleMax) {
      this.closest('.product-bundle-wrapper').removeAttribute('locked');
    }
  }

  reorderVariants() {
    let order = 0;
    let minimum = Math.max(this.bundleMin, this.bundleCount) * -1;
    this.variants.forEach((variant) => {
      variant.style.order = variant.hasAttribute('available') ? order++ : minimum++;
    });
  }

  resetVariants() {
    this.variants.forEach((variant) => {
      this.clearVariant(variant);
    });

    this.reorderVariants();
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  getAvailableAvriant() {
    return this.querySelector('[data-product-bundle-variant][available]');
  }

  getResizedImageSrc(src, size) {
    return `${src}${src.includes("?") ? "&" : "?"}width=${size}&height=${size}`.replace(/\n|\r|\s/g, "");
  }
}
customElements.define('product-bundle', ProductBundle);

class NumberCounter extends HTMLElement {
  constructor() {
    super();

    if (theme.config.motionReduced) return;

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    const matches = this.textContent.trim().match(/\d+(?:[,. ]\d+)*/);
    const toReplace = matches[0].replace(/[,\. ]+/, "");

    Motion.animate((progress) => {
      const formattedString = Math.round(progress * parseInt(toReplace)).toString();
      this.textContent = progress === 1 ? matches[0] : formattedString;
    }, { duration: parseFloat(this.getAttribute('data-duration')), easing: [0.16, 1, 0.3, 1] });
  }
}
customElements.define('number-counter', NumberCounter);

class ScrollingBanner extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get media() {
    return this._media = this._media || Array.from(this.querySelectorAll('.image-with-text__image>.media'));
  }

  get stickyElement() {
    return this.querySelector('sticky-element');
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.detectScrollListener = theme.utils.throttle(this.onScrollHandler.bind(this));

    const mql = window.matchMedia('screen and (min-width: 1024px)');
    if (mql.matches) this.load();
    mql.onchange = (mql) => {
      mql.matches ? this.load() : this.unload();
    }
  }

  setMediaHeight() {
    let height = 0;
    this.media.forEach((media) => {
      height += media.getBoundingClientRect().height;
    });
    this.style.setProperty('--scrolling-height', `${height.toFixed(1)}px`);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;

    if (this.currentScrollTop === scrollTop) return;
    this.currentScrollTop = scrollTop;
    const offsetTop = this.getBoundingClientRect().top - parseFloat(this.stickyElement.inset);

    const mediaElements = this.media;
    const mediaCount = mediaElements.length;

    if (offsetTop < 0) {
      let activeIndex = 1;

      // Start from second element
      for (let i = 1; i < mediaCount; i++) {
        const media = mediaElements[i];
        const mediaHeight = media.getBoundingClientRect().height;
        const mediaScrollTop = mediaHeight * (i - 1);

        if (Math.abs(offsetTop) > mediaScrollTop) {
          activeIndex = i;
          let progress = 100 - Math.floor((Math.abs(offsetTop) - mediaScrollTop) / mediaHeight * 100);
          if (progress > 95) progress = 100;
          if (progress < 5) progress = 0;

          media.style.clipPath = `polygon(0 ${progress}%, 100% ${progress}%, 100% 100%, 0 100%)`;
          this.updateControlledElement(media, progress);
        }
        else {
          media.style.clipPath = '';
        }
      }

      // Update remaining elements
      for (let i = activeIndex + 1; i < mediaCount; i++) {
        const media = mediaElements[i];
        this.updateControlledElement(media, 100);
      }
    }
    else {
      // Reset all elements when scrolled to top
      for (let i = 1; i < mediaCount; i++) {
        const media = mediaElements[i];
        media.style.clipPath = '';
        this.updateControlledElement(media, 100);
      }
    }
  }

  updateControlledElement(media, progress) {
    const controlledElement = media.hasAttribute('aria-controls') ? document.getElementById(media.getAttribute('aria-controls')) : null;
    if (controlledElement) {
      if (progress === 100) controlledElement.classList.add('opacity-0', 'pointer-events-none');
      if (progress === 0) controlledElement.classList.remove('opacity-0', 'pointer-events-none');
    }
  }

  load() {
    this.setMediaHeight();
    window.addEventListener('scroll', this.detectScrollListener, false);
  }

  unload() {
    window.removeEventListener('scroll', this.detectScrollListener);
    this.style.removeProperty('--scrolling-height');
    this.media.forEach((media) => {
      media.style.clipPath = '';
    });
  }
}
customElements.define('scrolling-banner', ScrollingBanner);

class SecondaryVideo extends HTMLDivElement {
  constructor() {
    super();

    if (theme.config.motionReduced || theme.config.isTouch) return;

    if (this.videoMedia) {
      this.addEventListener('mouseenter', this.onEnterHandler);
      this.addEventListener('mouseleave', this.onLeaveHandler);
    }
  }

  get videoMedia() {
    return this.querySelector('video-media');
  }

  onEnterHandler() {
    const media = this.videoMedia;
    if (media && media.hasAttribute('autoplay')) {
      media.play();
    }
  }

  onLeaveHandler() {
    const media = this.videoMedia;
    if (media) {
      media.pause();
    }
  }
}
customElements.define('secondary-video', SecondaryVideo, { extends: 'div' });

class SocialFeed extends XModal {
  constructor() {
    super();

    this.onKeyup = this.onKeyup.bind(this);
  }

  get shouldCloseAll() {
    return true;
  }

  get lookbook() {
    return this.querySelector('lookbook-element');
  }

  get previousButton() {
    return this.querySelector('button[data-button="previous"]');
  }

  get nextButton() {
    return this.querySelector('button[data-button="next"]');
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.onKeyup);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.onKeyup);
  }

  beforeShow() {
    super.beforeShow();
    this.lookbook.closeAll();
  }

  async afterShow() {
    super.afterShow();
    Motion.inView(this.lookbook, async () => {
      await theme.utils.imageLoaded(this.lookbook.media);
      this.lookbook.animate();
    });
  }

  onKeyup(event) {
    if (event.code === 'ArrowLeft' || event.code === 'ArrowRight') {
      const button = event.code === 'ArrowLeft' ? this.previousButton : this.nextButton;
      if (button && !button.disabled) {
        event.preventDefault();
        button.dispatchEvent(new Event('click'));
      }
    }
  }
}
customElements.define('social-feed', SocialFeed);

class SocialFeedButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener('click', this.onButtonClick);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  onButtonClick() {
    if (this.controlledElement) this.controlledElement.show();
  }
}
customElements.define('social-feed-button', SocialFeedButton, { extends: 'button' });

class IconsCarousel extends HTMLDivElement {
  constructor() {
    super();

    this.onUpdateTabindex();

    this.onUpdateTabindex = this.onUpdateTabindex.bind(this);
    this.onEnterListener = this.onEnterHandler.bind(this);
    this.onLeaveListener = this.onLeaveHandler.bind(this);
  }

  get isScrollable() {
    const style = window.getComputedStyle(this);
    const hasScrollableOverflow = style.overflow === 'scroll' || style.overflow === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'auto' || style.overflowX === 'scroll' || style.overflowX === 'auto';
    const hasScrollableContent = this.scrollHeight > this.clientHeight || this.scrollWidth > this.clientWidth;

    return hasScrollableOverflow && hasScrollableContent;
  }

  connectedCallback() {
    if (theme.config.isTouch) return;

    this.addEventListener('mouseenter', this.onEnterListener);
    this.addEventListener('mouseleave', this.onLeaveListener);
    document.addEventListener('matchSmall', this.onUpdateTabindex);
    document.addEventListener('unmatchSmall', this.onUpdateTabindex);
  }

  disconnectedCallback() {
    if (theme.config.isTouch) return;

    this.removeEventListener('mouseenter', this.onEnterListener);
    this.removeEventListener('mouseleave', this.onLeaveListener);
    document.removeEventListener('matchSmall', this.onUpdateTabindex);
    document.removeEventListener('unmatchSmall', this.onUpdateTabindex);
  }

  onEnterHandler() {
    const scrollWidth = this.scrollWidth * (theme.config.rtl ? -1 : 1);
    this.scrollTo({
      left: scrollWidth,
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    });
  }

  onLeaveHandler() {
    this.scrollTo({
      left: 0,
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    });
  }

  onUpdateTabindex() {
    this.removeAttribute('tabindex');

    if (this.isScrollable) {
      this.setAttribute('tabindex', '0');
    }
  }
}
customElements.define('icons-carousel', IconsCarousel, { extends: 'div' });

class ComparisonTable extends HTMLElement {
  constructor() {
    super();

    this.highlighted = false;
    this.tableSticky = this.querySelector('.comparison-table__sticky');
    if (this.tableSticky) {
      Motion.inView(this, this.init.bind(this));
    }
  }

  get items() {
    return Array.from(this.querySelectorAll('.comparison-table__row'));
  }

  init() {
    this.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)));
    this.tableSticky.addEventListener('scroll', theme.utils.throttle(this.onStickyScrollHandler.bind(this)));

    Motion.scroll(({ y }) => {
      if (y.current >= y.targetOffset + this.tableSticky.clientHeight / 2 && y.progress < 0.85) {
        this.tableSticky.classList.add('active');
      }
      else {
        this.tableSticky.classList.remove('active');
      }
    },
      { target: this, offset: ['0 start', 'end 0'] }
    );
  }

  onScrollHandler(event) {
    this.tableSticky.scrollTo({
      left: event.target.scrollLeft
    });
  }

  onStickyScrollHandler(event) {
    this.scrollTo({
      left: event.target.scrollLeft
    });
  }

  highlight() {
    this.items.forEach((item) => {
      const classesToCheck = ['thead', 'tfoot', 'sticky'];
      if (classesToCheck.every(className => !item.classList.contains(className))) {
        if (this.highlighted) {
          const columns = Array.from(item.querySelectorAll('.comparison-table__column'));
          const first_column = columns[0];

          let highlighted = false;
          columns.forEach((column) => {
            if (first_column.innerHTML != column.innerHTML) {
              highlighted = true;
            }
          });

          item.classList.toggle('info', highlighted);
        }
        else {
          item.classList.remove('info');
        }
      }
    });
  }
}
customElements.define('comparison-table', ComparisonTable);


class ComparisonHighlight extends HTMLInputElement {
  constructor() {
    super();

    this.addEventListener('change', this.onChange);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get items() {
    return Array.from(this.controlledElement.querySelectorAll('.comparison-table__row'));
  }

  onChange() {
    if (this.controlledElement) {
      this.controlledElement.highlighted = this.checked;
      this.controlledElement.highlight();
    }
  }
}
customElements.define('comparison-highlight', ComparisonHighlight, { extends: 'input' });

class ScrollSpy extends HTMLElement {
  constructor() {
    super();
  }

  get scrollspyLinks() {
    return Array.from(this.querySelectorAll('.scrollspy-link'));
  }

  get backToTopButton() {
    return this.querySelector('.back-to-top');
  }

  get headerHeight() {
    let header = document.querySelector('.header-section header[is="sticky-header"]');
    if (this.hasAttribute('aria-controls')) {
      header = document.getElementById(this.getAttribute('aria-controls'));
    }
    return header ? (header.getBoundingClientRect().height + this.topGap) : (this.offsetHeight < window.innerHeight ? this.topGap : 0);
  }

  get topGap() {
    return this.hasAttribute('data-gap') ? parseInt(this.getAttribute('data-gap')) : 20;
  }

  get showHighlight() {
    return this.hasAttribute('data-highlight');
  }

  connectedCallback() {
    this.scrollspyLinks.forEach(link => {
      link.addEventListener('click', this.handleScrollspyLinkClick.bind(this));
    });

    if (this.backToTopButton) {
      this.backToTopButton.addEventListener('click', this.handleBackToTopClick.bind(this));
    }

    if (this.showHighlight) {
      window.addEventListener('scroll', this.updateActiveLink.bind(this));
      this.updateActiveLink.bind(this);
    }
  }

  handleScrollspyLinkClick(event) {
    event.preventDefault();
    const link = event.currentTarget;

    const targetIds = link.hasAttribute('data-target') ? link.getAttribute('data-target').split(',') : [];
    const section = document.getElementById(targetIds[0]);

    if (section) {
      // A Scroll section anchor carries Offset Y as --scroll-offset (snippets/scroll-section.liquid).
      const extra = parseFloat(getComputedStyle(section).getPropertyValue('--scroll-offset')) || 0;
      const sectionTop = window.scrollY + section.getBoundingClientRect().top - this.headerHeight - extra;
      window.scrollTo({
        top: sectionTop,
        behavior: theme.config.motionReduced ? 'auto' : 'smooth'
      });
    }
  }

  handleBackToTopClick() {
    window.scrollTo({
      top: 0,
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    });
  }

  updateActiveLink() {
    let current = '';

    this.scrollspyLinks.forEach(link => {
      const targetIds = link.hasAttribute('data-target') ? link.getAttribute('data-target').split(',') : [];

      targetIds.forEach((targetId) => {
        const section = document.getElementById(targetId);

        if (section) {
          // A Scroll section anchor is a zero-height element, so measure the
          // section it belongs to (the wrapper, or the first sibling with a
          // box when the wrapper is display: contents).
          let box = section;
          if (section.classList.contains('scroll-section')) {
            box = section.closest('.shopify-section:not(.contents)');
            if (!box) {
              box = section.nextElementSibling;
              while (box && ['STYLE', 'SCRIPT', 'NOSCRIPT', 'LINK'].includes(box.tagName)) box = box.nextElementSibling;
              box = box || section;
            }
          }
          const rect = box.getBoundingClientRect();
          const sectionTop = window.scrollY + rect.top;
          const sectionBottom = sectionTop + rect.height;

          if (window.pageYOffset >= sectionTop && window.pageYOffset < sectionBottom) {
            current = targetId;
          }
        }
      });
    });

    this.scrollspyLinks.forEach(link => {
      const targetIds = link.hasAttribute('data-target') ? link.getAttribute('data-target').split(',') : [];
      let active = false;

      targetIds.forEach((targetId) => {
        if (targetId !== '' && targetId === current) {
          active = true;
        }
      });

      if (active) {
        link.setAttribute('aria-current', 'location');
        link.classList.add('active');
      }
      else {
        link.removeAttribute('aria-current');
        link.classList.remove('active');
      }
    });
  }
};
customElements.define('scroll-spy', ScrollSpy, { extends: 'nav' });

/*
  Scroll section anchors (snippets/scroll-section.liquid, Theme editor:
  any section › Scroll section › Name). A same-page link to one — a button,
  a menu item, an announcement — scrolls there with the sticky header and
  Offset Y allowed for by CSS (div.scroll-section { scroll-margin-top }).
  The handler exists for three things the native jump gets wrong:
    1. html.modal-locked only stops the user scrolling, so a link inside an
       open drawer or modal would jump the page behind it — close it first.
    2. Smooth scrolling that honours the reduced-motion setting.
    3. A direct load of /page#name scrolls before --header-height has been
       measured (it starts at 0px), so the page is re-aligned on load.
  Links to anything that is not a scroll-section anchor are left alone.
*/
(() => {
  const anchorFor = (loc) => {
    if (!loc || !loc.hash || loc.hash.length < 2) return null;
    if (loc.origin !== location.origin || loc.pathname !== location.pathname) return null;
    let id;
    try { id = decodeURIComponent(loc.hash.slice(1)); } catch { return null; }
    const el = id && document.getElementById(id);
    return el && el.classList.contains('scroll-section') ? el : null;
  };
  const go = (el, instant) => el.scrollIntoView({ behavior: instant || theme.config.motionReduced ? 'auto' : 'smooth', block: 'start' });

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest && event.target.closest('a[href]');
    const target = anchorFor(link);
    if (!target) return;
    event.preventDefault();

    let host = link.parentElement;
    while (host && !(typeof ModalElement !== 'undefined' && host instanceof ModalElement && host.open)) host = host.parentElement;
    const closed = host ? host.hide() : Promise.resolve();
    // afterHide fires before the modal releases html.modal-locked (it unlocks
    // in a queued timeout), and a locked document ignores scrolls — so wait
    // one turn after the drawer reports closed before scrolling.
    Promise.resolve(closed).then(() => new Promise((resolve) => setTimeout(resolve, 0))).then(() => {
      if (!(window.Shopify && Shopify.designMode)) history.pushState(null, '', link.hash);
      go(target);
    });
  });

  window.addEventListener('load', () => {
    const target = anchorFor(location);
    if (target) requestAnimationFrame(() => requestAnimationFrame(() => go(target, true)));
  });
})();

(function () {
  if (!('lazyImage' in document.body.dataset)) return;
  var style = document.body.dataset.imageLoadingStyle;
  if (!style || style === 'bar' || style === 'none') return;

  function injectLoader(mediaEl) {
    // Section backgrounds (footer, banners, group blocks) are decoration: no loader (v2.10.44)
    if (mediaEl.closest('.group-block__media')) return;
    if (mediaEl.tagName === 'PICTURE') return;
    // Videos manage their own poster/loading UX
    if (mediaEl.tagName === 'VIDEO-MEDIA') return;
    // Banner media relies on ".banner__image:last-child" to reveal the desktop
    // image/video — appending a loader here would break that and hide it
    if (mediaEl.querySelector(':scope > .banner__image')) return;
    if (mediaEl.querySelector('.media-loader')) return;
    var loader = document.createElement('div');
    loader.className = 'media-loader';
    loader.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < 3; i++) loader.appendChild(document.createElement('span'));
    mediaEl.appendChild(loader);
  }

  function scan(root) {
    (root.querySelectorAll ? root.querySelectorAll('.media') : []).forEach(injectLoader);
  }

  theme.DOMready(function () {
    scan(document);
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type !== 'childList') return;
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.classList && node.classList.contains('media')) injectLoader(node);
          scan(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();

// Quick-add size pills: reveal the "More" pill when pills overflow the 3-row cap
(function () {
  function initMorePills() {
    document.querySelectorAll('.quick-add--size').forEach(function (container) {
      var track = container.querySelector('.buttons');
      var more = container.querySelector('.quick-add__more');
      if (!track || !more) return;

      requestAnimationFrame(function () {
        if (track.scrollHeight <= track.offsetHeight + 4) return;

        // Reveal More pill first so it takes up space in the layout
        more.removeAttribute('hidden');

        // Second rAF — browser has now laid out the More pill;
        // hide trailing variant forms until all content fits within the cap
        requestAnimationFrame(function () {
          var forms = Array.from(track.querySelectorAll('form'));
          for (var i = forms.length - 1; i >= 0; i--) {
            if (track.scrollHeight <= track.offsetHeight + 4) break;
            forms[i].style.display = 'none';
          }
        });
      });
    });
  }

  theme.DOMready(initMorePills);
  document.addEventListener('shopify:section:load', initMorePills);

  // The card overlay is pinned by :focus-within after clicking a pill or
  // "Show more" (focus returns to the trigger when the drawer closes).
  // For mouse users, release that focus when the pointer leaves the card;
  // keyboard navigation never fires mouseout, so a11y is unaffected.
  document.addEventListener('mouseout', function (event) {
    var card = event.target.closest && event.target.closest('.product-card');
    if (!card) return;
    if (event.relatedTarget && card.contains(event.relatedTarget)) return;
    if (card.contains(document.activeElement)) document.activeElement.blur();
  });
})();

// Card swatch behavior — swap card image on swatch click when behavior is set to "card"
(function () {
  function buildSrcset(url, widths) {
    return widths.map(function (w) {
      return url.replace(/width=\d+/, 'width=' + w) + ' ' + w + 'w';
    }).join(', ');
  }

  // Capture phase + stopPropagation: a card-mode swatch must never reach
  // the page-transition click interceptor (registered earlier on document),
  // which navigates programmatically via window.location and so ignores a
  // bubble-phase preventDefault. Capture runs before any bubble listener
  // regardless of registration order, and only for card-mode swatches —
  // every other click passes through untouched.
  document.addEventListener('click', function (event) {
    var swatch = event.target.closest && event.target.closest('a.color-swatch');
    if (!swatch) return;

    var card = swatch.closest('[data-swatch-behavior="card"]');
    if (!card) return;

    // Card mode: this click is ours — stop native nav AND any other
    // document click listeners (page transition, etc.)
    event.preventDefault();
    event.stopPropagation();

    // Update active swatch state regardless of whether there's media to swap
    var allSwatches = card.querySelectorAll('a.color-swatch');
    allSwatches.forEach(function (s) { s.removeAttribute('aria-current'); });
    swatch.setAttribute('aria-current', 'true');

    // The card now SHOWS this colour, so clicking through must OPEN this
    // colour: retarget the card's product links (media + title) at the
    // swatch's own variant URL. Without this the PDP opened the default
    // variant — a different image from the one the shopper picked (and the
    // card->PDP image handoff couldn't match either).
    if (swatch.href) {
      card.querySelectorAll('a[href*="/products/"]:not(.color-swatch)').forEach(function (a) {
        a.href = swatch.href;
      });
    }

    var li = swatch.closest('[data-card-swatch-media]');
    if (!li) return;

    // The variant gallery is a pipe-separated URL list (theme.gang_media /
    // legacy Theme.Variant_Gallery, falling back to the variant featured
    // image). The first entry is the featured image.
    var gallery = ((li.dataset.cardSwatchGallery || li.dataset.cardSwatchMedia || '')
      .split('|')).filter(Boolean);
    if (!gallery.length) return;
    var widths = [180, 360, 540, 720, 900, 1080];
    var mediaUrl = gallery[0];
    var srcset = buildSrcset(mediaUrl, widths);

    var mediaWrapper = card.querySelector('.product-card__media');
    if (!mediaWrapper) return;

    // The featured image lives inside the media link (<a class="media">). On
    // mobile (and single-image cards) it's the visible image, so swap it.
    var mediaLink = mediaWrapper.querySelector(':scope > .media') || mediaWrapper;
    var featuredImg = mediaLink.querySelector(':scope > img');
    if (featuredImg) {
      // Mobile has no hover carousel, so the featured image IS what changes —
      // dissolve it (Apple-style) instead of a hard flash. Desktop hard-sets it
      // because the secondary-media hover carousel covers it anyway.
      var isMobile = window.matchMedia('(max-width: 767px)').matches;
      if (isMobile && typeof theme !== 'undefined' && typeof theme.crossfadeCardImage === 'function') {
        theme.crossfadeCardImage(featuredImg, mediaUrl, srcset);
      } else {
        featuredImg.src = mediaUrl;
        featuredImg.srcset = srcset;
      }
    }

    // Multi-image cards overlay the featured image with a <secondary-media>
    // hover carousel that covers it on desktop (the featured <img> is hidden
    // by CSS behind it). Rebuild the carousel from the variant gallery so it
    // shows exactly that variant's images — a single image becomes a static
    // image (no scroll/dots), two images a two-up gallery, and so on. This
    // also fixes the previous blank-card bug, where hiding the carousel left
    // the CSS-hidden featured image behind it.
    var carousel = mediaWrapper.querySelector('secondary-media');
    if (carousel && typeof carousel.setGallery === 'function') {
      carousel.setGallery(gallery);
    } else if (featuredImg) {
      // No carousel — reveal the swapped featured image so the card never
      // goes blank.
      featuredImg.style.opacity = '1';
      featuredImg.style.visibility = 'visible';
      mediaLink.style.zIndex = '1';
    }

    // Single-image hover cards show a .product-card__secondary image on hover.
    // Point it at the variant's second gallery image (or the featured one) so
    // hover never reveals the previous colour.
    var hoverWrap = mediaLink.querySelector('.product-card__secondary');
    if (hoverWrap) {
      var hoverImg = hoverWrap.querySelector('img');
      if (hoverImg) {
        var hoverUrl = gallery.length > 1 ? gallery[1] : mediaUrl;
        hoverImg.src = hoverUrl;
        hoverImg.srcset = buildSrcset(hoverUrl, widths);
      }
    }
  }, true);
})();

/* ── Variant image smooth crossfade + featured-image preload ───────────────
   Make variant switching feel like Apple's product pages — the image dissolves
   from the old colour to the new one, never flashing blank.
   • PRODUCT PAGE (all viewports): preload every variant's featured image after
     idle (one product = cheap) so a colour switch renders from cache. On
     variant:change the hero crossfades to the new featured image via a fixed
     overlay carrying the OLD image (frozen) with the NEW one fading in on top,
     so the blank while the freshly-rendered gallery lazy-loads is never seen.
   • CARDS (collection / search): warm a colour's image on intent (hover / focus
     / touch) so the swap is a cache hit. On MOBILE the featured image crossfades
     on swatch tap; desktop keeps its hover-slide carousel (handled elsewhere).
   No eager preloading of every image of every variant. */
(function () {
  var warmed = {};
  // Base image URL (version kept, width candidate stripped) → 1 once the browser
  // has actually fetched it this session. Lets us reveal an already-loaded image
  // synchronously (before the next paint) so it never flashes its opacity-0
  // loading state when the gallery is rebuilt for a new variant.
  var loadedUrls = {};
  // Mirror product-media-gallery.liquid so a preloaded candidate is the exact
  // URL the hero <img> will render (a real cache hit, not a wasted fetch).
  var HERO_WIDTHS = [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800, 2000, 2200, 2400, 2600, 2800, 3000, 3200];
  var HERO_SIZES = '(max-width: 767px) calc(100vw - 40px), (max-width: 1023px) calc(100vw - 64px), 75vw';
  var CARD_WIDTHS = [180, 360, 540, 720, 900, 1080];
  // Apple-style dissolve: a leisurely, softly-eased cross-dissolve.
  var EASE = 'cubic-bezier(0.4, 0.0, 0.2, 1)';
  var HERO_FADE_MS = 450;
  var HERO_CROSSFADE = false; // see the variant:change listener below
  var CARD_FADE_MS = 380;

  function withWidth(url, w) {
    return /[?&]width=\d+/.test(url)
      ? url.replace(/width=\d+/, 'width=' + w)
      : url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function srcsetFor(url, widths) {
    return widths.map(function (w) { return withWidth(url, w) + ' ' + w + 'w'; }).join(', ');
  }
  // Strip the width candidate (keep the ?v= version) so two candidates of the
  // same image compare equal.
  function imgBase(url) {
    return (url || '').replace(/[?&]width=\d+/, '').replace(/[?&]$/, '');
  }
  function markLoaded(url) { var b = imgBase(url); if (b) loadedUrls[b] = 1; }
  // Warm the browser cache for a specific srcset + sizes combination.
  function warm(url, srcset, sizes) {
    if (!url) return;
    var key = (sizes || '') + '|' + (srcset || url);
    if (warmed[key]) return;
    warmed[key] = 1;
    var img = new Image();
    img.decoding = 'async';
    img.addEventListener('load', function () { markLoaded(img.currentSrc || url); }, { once: true });
    if (sizes) img.sizes = sizes;
    if (srcset) img.srcset = srcset;
    img.src = url;
    if (typeof img.decode === 'function') { try { img.decode().catch(function () {}); } catch (e) {} }
  }
  function widthsOf(srcset, fallback) {
    var ws = [];
    (srcset || '').split(',').forEach(function (p) {
      var m = p.trim().match(/(\d+)w$/);
      if (m) ws.push(parseInt(m[1], 10));
    });
    return ws.length ? ws : fallback;
  }
  // Warm using the same srcset widths + sizes as an existing target <img>.
  function warmLikeTarget(url, targetImg, fallbackWidths) {
    if (!url) return;
    var widths = targetImg ? widthsOf(targetImg.getAttribute('srcset'), fallbackWidths) : fallbackWidths;
    var sizes = targetImg ? targetImg.getAttribute('sizes') : null;
    warm(withWidth(url, widths[Math.floor(widths.length / 2)] || 600), srcsetFor(url, widths), sizes);
  }

  /* ── Shared: crossfade a plain <img> to a new source ───────────────────── */
  // Overlays a copy of the new image (opacity 0 → 1) over the current one, then
  // sets the real <img> to the new source behind the fully-opaque overlay and
  // removes the overlay — so the transition is a clean dissolve with no flash.
  function crossfadeImg(img, newUrl, newSrcset) {
    if (!img || !newUrl) return;
    var parent = img.parentElement;
    var apply = function () { img.src = newUrl; if (newSrcset) img.srcset = newSrcset; };
    if (!parent) { apply(); return; }
    if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
    var overlay = new Image();
    overlay.decoding = 'async';
    overlay.alt = '';
    if (newSrcset) overlay.srcset = newSrcset;
    overlay.sizes = img.getAttribute('sizes') || '';
    overlay.src = newUrl;
    var ics = getComputedStyle(img);
    overlay.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;margin:0;opacity:0;transition:opacity ' + CARD_FADE_MS + 'ms ' + EASE + ';pointer-events:none;z-index:3;';
    overlay.style.objectFit = ics.objectFit || 'cover';
    overlay.style.objectPosition = ics.objectPosition || 'center';
    overlay.style.borderRadius = ics.borderRadius || '';
    parent.appendChild(overlay);
    var done = false;
    var reveal = function () {
      if (done) return; done = true;
      apply();
      // Real <img> now carries the identical (cached) source behind the opaque
      // overlay; drop the overlay a frame later so the swap is invisible.
      setTimeout(function () { if (overlay.parentNode) overlay.remove(); }, 80);
    };
    var go = function () {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { overlay.style.opacity = '1'; });
      });
      setTimeout(reveal, CARD_FADE_MS + 30);
    };
    if (overlay.complete && overlay.naturalWidth) go();
    else {
      overlay.addEventListener('load', go, { once: true });
      overlay.addEventListener('error', function () { apply(); if (overlay.parentNode) overlay.remove(); done = true; }, { once: true });
      setTimeout(go, 500); // safety: never leave the overlay stuck
    }
  }

  /* ── PRODUCT PAGE ──────────────────────────────────────────────────────── */
  function variantFeatured(v) {
    if (!v) return null;
    if (v.featured_media && v.featured_media.preview_image && v.featured_media.preview_image.src) return v.featured_media.preview_image.src;
    if (v.featured_image && v.featured_image.src) return v.featured_image.src;
    return null;
  }
  // Strip Shopify's width param so URLs compare equal regardless of candidate.
  function baseUrl(url) { return (url || '').replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, ''); }

  // The hero the shopper is currently looking at: the largest in-viewport image
  // inside this gallery (desktop sticky preview or the active slider media).
  function currentHero(gallery) {
    var best = null, bestArea = 0;
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    gallery.querySelectorAll('.product__media img').forEach(function (img) {
      var r = img.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return;
      if (r.bottom < 0 || r.top > vh) return; // off-screen
      var area = r.width * r.height;
      if (area > bestArea) { bestArea = area; best = img; }
    });
    return best;
  }

  // Crossfade the PDP hero to `newUrl`. `oldSrc`/`oldSrcset` freeze the previous
  // image so nothing blanks while the just-rendered gallery lazy-loads.
  function crossfadeHero(gallery, heroEl, oldSrc, oldSrcset, newUrl) {
    if (!heroEl || !newUrl) return;
    var r = heroEl.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) return;
    var cs = getComputedStyle(heroEl);
    var box = document.createElement('div');
    box.style.cssText = 'position:fixed;left:' + r.left + 'px;top:' + r.top + 'px;width:' + r.width + 'px;height:' + r.height + 'px;overflow:hidden;pointer-events:none;z-index:5;border-radius:' + (cs.borderRadius || '0') + ';';
    var base = { position: 'absolute', inset: '0', width: '100%', height: '100%', margin: '0', objectFit: cs.objectFit || 'cover', objectPosition: cs.objectPosition || 'center' };
    var oldImg = new Image();
    oldImg.alt = '';
    if (oldSrcset) oldImg.srcset = oldSrcset;
    oldImg.sizes = HERO_SIZES;
    if (oldSrc) oldImg.src = oldSrc;
    Object.assign(oldImg.style, base);
    var newImg = new Image();
    newImg.decoding = 'async';
    newImg.alt = '';
    newImg.srcset = srcsetFor(newUrl, HERO_WIDTHS);
    newImg.sizes = HERO_SIZES;
    newImg.src = withWidth(newUrl, 1200);
    Object.assign(newImg.style, base);
    newImg.style.opacity = '0';
    newImg.style.transition = 'opacity ' + HERO_FADE_MS + 'ms ' + EASE;
    box.appendChild(oldImg);
    box.appendChild(newImg);
    document.body.appendChild(box);
    var removed = false;
    var remove = function () { if (removed) return; removed = true; if (box.parentNode) box.remove(); };
    // One clean cross-dissolve: old (frozen) → new. Once the dissolve finishes
    // and the real gallery hero has painted the identical image underneath, the
    // overlay is removed in a single frame — no visible second fade, no flash.
    var fadeIn = function () {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { newImg.style.opacity = '1'; });
      });
      setTimeout(function () {
        var real = currentHero(gallery);
        var drop = function () { requestAnimationFrame(remove); };
        if (!real || (real.complete && real.naturalWidth)) drop();
        else { real.addEventListener('load', drop, { once: true }); setTimeout(drop, 500); }
      }, HERO_FADE_MS + 20);
    };
    if (newImg.complete && newImg.naturalWidth) fadeIn();
    else { newImg.addEventListener('load', fadeIn, { once: true }); newImg.addEventListener('error', remove, { once: true }); setTimeout(fadeIn, 500); }
  }

  // Warm every gallery image found in a (prefetched) section render, using each
  // <img>'s own srcset + sizes so the eventual render is an exact cache hit.
  // This means switching to a hovered colour shows ALL its images instantly,
  // not just the featured one — and any image shared with another variant is
  // already in cache from the first time it was seen.
  function warmGalleryImages(html) {
    var doc;
    try { doc = new DOMParser().parseFromString(html, 'text/html'); } catch (e) { return; }
    var scope = doc.querySelector('.product__gallery') || doc;
    var imgs = scope.querySelectorAll('[data-media-id] img');
    var n = 0;
    imgs.forEach(function (img) {
      if (n >= 14) return;
      var ss = img.getAttribute('srcset');
      var src = img.getAttribute('src');
      if (!ss && !src) return;
      var sizes = img.getAttribute('sizes');
      var key = (sizes || '') + '|' + (ss || src);
      if (warmed[key]) return;
      warmed[key] = 1;
      var w = new Image();
      w.decoding = 'async';
      w.addEventListener('load', function () { markLoaded(w.currentSrc || src); }, { once: true });
      if (sizes) w.sizes = sizes;
      if (ss) w.srcset = ss;
      if (src) w.src = src;
      if (typeof w.decode === 'function') { try { w.decode().catch(function () {}); } catch (e) {} }
      n++;
    });
  }

  // The gallery DOM is fully rebuilt on every variant switch, so each image —
  // even one already in browser cache from another variant — becomes a fresh
  // lazy-image that shows the skeleton and defers its reveal through
  // requestIdleCallback(timeout:150). During a switch the main thread is busy,
  // so that reveal lags and cached images visibly "reload". Here we reveal any
  // already-complete image immediately, and reveal the rest the instant they
  // load — bypassing the idle deferral entirely.
  function revealLoadedMedia(root) {
    if (!root) return;
    root.querySelectorAll('.media.loading').forEach(function (media) {
      // Videos / 3D models clear their own loading state when the player is
      // ready — don't pre-empt them.
      if (media.querySelector('video-media, model-media, .deferred-media')) return;
      var img = media.querySelector('img');
      if (!img) return;
      var done = function () { media.classList.remove('loading'); img.classList.add('loaded'); };
      var base = imgBase(img.getAttribute('src') || img.currentSrc || '');
      // Already fetched this session (e.g. an image shared with a previously
      // viewed variant) → reveal NOW, synchronously, before the browser paints
      // the rebuilt gallery, so it never shows the opacity-0 loading flash.
      if ((img.complete && img.naturalWidth > 0) || (base && loadedUrls[base])) {
        done();
      } else {
        img.addEventListener('load', function () { markLoaded(img.currentSrc || img.getAttribute('src')); done(); }, { once: true });
      }
    });
  }

  // Seed the loaded-URL set from images already painted on the page, so the
  // first switch away-and-back reveals them without a flash.
  function seedLoaded(root) {
    (root || document).querySelectorAll('.product__gallery img').forEach(function (img) {
      if (img.complete && img.naturalWidth > 0) markLoaded(img.currentSrc || img.getAttribute('src'));
    });
  }

  // Snapshot the hero source a product-info is currently showing, so we can
  // freeze it while the next variant's gallery lazy-loads.
  // The gallery is a sibling of <product-info>, not a child: find it by id (v2.10.25 —
  // pi.querySelector('.product__gallery') was always null, so none of this ran).
  function galleryOf(pi) {
    var sid = pi.getAttribute('data-original-section-id') || pi.getAttribute('data-section-id');
    return pi.querySelector('.product__gallery') || document.getElementById('ProductGallery-' + sid + '-' + pi.getAttribute('data-product-id'));
  }

  function snapshotHero(pi) {
    var g = galleryOf(pi);
    var hero = g && currentHero(g);
    pi._lastHero = {
      src: hero ? (hero.currentSrc || hero.getAttribute('src')) : null,
      srcset: hero ? hero.getAttribute('srcset') : null
    };
  }

  function initProductInfo(pi) {
    if (pi._variantFadeInit) return;
    pi._variantFadeInit = true;

    // Preload every variant's featured image after idle (one product = cheap),
    // mirroring the hero's srcset + sizes so the switch is a real cache hit.
    var preload = function () {
      var el = pi.querySelector('[data-variants]');
      if (!el) return;
      var variants;
      try { variants = JSON.parse(el.textContent); } catch (e) { return; }
      if (!Array.isArray(variants)) return;
      var seen = {};
      variants.forEach(function (v) {
        var url = variantFeatured(v);
        if (!url || seen[url]) return;
        seen[url] = 1;
        warm(withWidth(url, 1200), srcsetFor(url, HERO_WIDTHS), HERO_SIZES);
      });
    };
    if ('requestIdleCallback' in window) requestIdleCallback(preload, { timeout: 2500 });
    else setTimeout(preload, 1200);

    snapshotHero(pi);
    seedLoaded(galleryOf(pi) || pi);
    // Catch images that finish after init (first paint).
    window.addEventListener('load', function () { seedLoaded(galleryOf(pi) || pi); }, { once: true });
  }

  // variant:change is dispatched on (productForm ?? document) WITHOUT bubbles, so the
  // document listener runs in the capture phase (v2.10.25 — as a bubbling listener it
  // never heard a form's event). Resolve the product-info from the event so multiple
  // galleries on a page each crossfade correctly.
  document.addEventListener('variant:change', function (e) {
    var pi = (e.target && e.target.closest) ? e.target.closest('product-info') : null;
    if (!pi) pi = document.querySelector('product-info');
    if (!pi) return;
    var variant = e.detail && e.detail.variant;
    var newUrl = variantFeatured(variant);
    var g = galleryOf(pi);
    // Reveal already-cached images in the just-rebuilt gallery instantly (skip
    // the lazy-image idle deferral) so shared / previously-seen images pop in
    // without the skeleton flash. Re-run next frame to catch cache reads that
    // complete just after the swap.
    if (g) {
      revealLoadedMedia(g);
      requestAnimationFrame(function () { revealLoadedMedia(g); });
      // Once this variant's images have painted, remember them so switching
      // back never flashes.
      setTimeout(function () { seedLoaded(g); }, 400);
    }
    if (!g || !newUrl) { snapshotHero(pi); return; }
    var last = pi._lastHero || {};
    // No image change for this option → nothing to crossfade.
    if (last.src && baseUrl(last.src) === baseUrl(newUrl)) { snapshotHero(pi); return; }
    // variant:change fires AFTER the gallery re-renders, so the new hero element
    // is already in place — the overlay covers it while it lazy-loads.
    // Held back (v2.10.25): this listener never ran before, and the overlay assumes the
    // on-screen hero shows the variant's featured image. In a 2-column desktop gallery it
    // often does not, so the overlay showed a different photo for ~0.5s (Paired, Fleur de
    // Luxe). Re-enable once the overlay targets the tile that will show newUrl.
    if (HERO_CROSSFADE) crossfadeHero(g, currentHero(g), last.src, last.srcset, newUrl);
    // Record the new featured image as the next "old".
    pi._lastHero = { src: withWidth(newUrl, 1200), srcset: srcsetFor(newUrl, HERO_WIDTHS) };
  }, true);

  function initPdp() {
    document.querySelectorAll('product-info').forEach(initProductInfo);
  }
  if (document.readyState !== 'loading') initPdp();
  else document.addEventListener('DOMContentLoaded', initPdp);
  window.addEventListener('load', initPdp);
  document.addEventListener('shopify:section:load', initPdp);

  /* ── CARD → PDP image handoff ──────────────────────────────────────────────
     Clicking a product card stores the card image the shopper is LOOKING AT
     (its currentSrc — guaranteed to be in the browser cache). If the product
     page's hero is the same image and its hi-res file hasn't arrived yet, the
     cached card-resolution copy paints instantly underneath and fades away the
     moment the hi-res load completes — the page feels immediate instead of
     showing a skeleton while the large image downloads. */
  var HANDOFF_KEY = 'theme:pdp-handoff';
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a[href*="/products/"]');
    if (!a) return;
    var card = a.closest('.product-card') || (a.closest('.card') || a);
    var img = card.querySelector && card.querySelector('.product-card__media img, img');
    var src = img && (img.currentSrc || img.src);
    if (!src) return;
    try {
      sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ b: baseUrl(src), s: src, t: Date.now() }));
    } catch (err) { /* storage full / disabled — the PDP just loads normally */ }
  }, true);

  function applyNavHandoff() {
    var raw;
    try { raw = sessionStorage.getItem(HANDOFF_KEY); sessionStorage.removeItem(HANDOFF_KEY); } catch (err) { return; }
    if (!raw) return;
    var data;
    try { data = JSON.parse(raw); } catch (err) { return; }
    // One-shot and fresh only: a stale entry (back/forward much later) must
    // never paint over a different product's gallery.
    if (!data || !data.s || Date.now() - (data.t || 0) > 30000) return;
    document.querySelectorAll('.product__preview .product__media img, .product__media-list .product__media img').forEach(function (img) {
      if (baseUrl(img.currentSrc || img.src) !== data.b) return;
      if (img.complete && img.naturalWidth) return; // hi-res already there
      var parent = img.parentElement;
      if (!parent || parent.querySelector('.pdp-handoff')) return;
      var low = document.createElement('img');
      low.className = 'pdp-handoff';
      low.src = data.s;
      low.alt = '';
      low.decoding = 'async';
      low.style.objectFit = getComputedStyle(img).objectFit || 'cover';
      parent.appendChild(low);
      var gone = false;
      var fade = function () {
        if (gone) return;
        gone = true;
        low.style.opacity = '0';
        setTimeout(function () { if (low.parentNode) low.remove(); }, 350);
      };
      img.addEventListener('load', fade, { once: true });
      img.addEventListener('error', fade, { once: true });
      setTimeout(fade, 6000); // safety: never leave a low-res copy stuck on top
    });
  }
  if (document.readyState !== 'loading') applyNavHandoff();
  else document.addEventListener('DOMContentLoaded', applyNavHandoff);

  /* ── CARDS — warm on intent (hover / focus / touch) ────────────────────── */
  function onCardIntent(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var li = t.closest('[data-card-swatch-gallery], [data-card-swatch-media]');
    if (!li) return;
    var first = (li.dataset.cardSwatchGallery || li.dataset.cardSwatchMedia || '')
      .split('|').filter(Boolean)[0];
    if (!first) return;
    var card = li.closest('.product-card');
    var img = card && card.querySelector('.product-card__media img');
    warmLikeTarget(first, img, CARD_WIDTHS);
  }
  ['pointerover', 'focusin', 'touchstart'].forEach(function (evt) {
    document.addEventListener(evt, onCardIntent, { passive: true });
  });

  /* ── PDP — prefetch a colour's section render on intent ────────────────── */
  // Building the exact URL the theme will request means the click reuses this
  // warmed cache (see product-info.prefetchSection / renderProductInfo), so the
  // FIRST switch to a colour is instant too — not just re-clicks.
  function pdpSectionUrl(pi, inp) {
    if (inp.hasAttribute('data-product-url')) return null; // separate product → navigates
    var picker = inp.closest('variant-picker');
    if (!picker) return null;
    var productUrl = pi.getAttribute('data-product-url');
    var sectionId = pi.getAttribute('data-original-section-id') || pi.getAttribute('data-section-id');
    if (!productUrl || !sectionId) return null;
    var hoveredName = inp.getAttribute('name');
    var hoveredId = inp.getAttribute('data-option-value-id');
    // Mirror VariantPicker.selectedOptionValues: option-value ids in DOM order,
    // swapping the hovered group's current value for the hovered one.
    var nodes = Array.prototype.slice.call(picker.querySelectorAll('select option[selected], fieldset input:checked'));
    if (!nodes.length) return null;
    var ids = nodes.map(function (n) {
      return n.getAttribute('name') === hoveredName ? hoveredId : n.getAttribute('data-option-value-id');
    });
    if (ids.indexOf(hoveredId) === -1) return null; // hovered group not represented
    return productUrl + '?section_id=' + sectionId + '&option_values=' + ids.join(',');
  }
  function onPdpSwatchIntent(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest('variant-picker')) return;
    var li = t.closest('li') || t.closest('magnet-element');
    var inp = li ? li.querySelector('input[data-option-value-id]')
                 : (t.matches && t.matches('input[data-option-value-id]') ? t : null);
    if (!inp) return;
    var pi = inp.closest('product-info');
    if (!pi || typeof pi.prefetchSection !== 'function') return;
    var url = pdpSectionUrl(pi, inp);
    if (!url) return;
    // Prefetch the section render, then warm that variant's whole gallery so the
    // click shows every image instantly (not just the featured one).
    pi.prefetchSection(url).then(function (html) { if (html) warmGalleryImages(html); });
  }
  ['pointerover', 'focusin', 'touchstart'].forEach(function (evt) {
    document.addEventListener(evt, onPdpSwatchIntent, { passive: true });
  });

  // Exposed so the card swatch handler can crossfade the featured image on
  // mobile (desktop cards keep their hover-slide carousel).
  theme.crossfadeCardImage = crossfadeImg;
})();

/* ------------------------------------------------------------------ */
/* Documentation hub (sections/docs-hub.liquid)                        */
/* Builds heading ids + "On this page" lists, highlights the heading in */
/* view, filters the sidebar, adds copy buttons to code blocks, opens   */
/* screenshots in a lightbox and shows the theme version chip.          */
/* ------------------------------------------------------------------ */
class DocsHub extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-docs-content]');
    this.buildToc();
    this.bindSearch();
    this.bindCode();
    this.bindLightbox();
    this.bindVideo();
    this.showVersion();
    this.rememberGroups();
    this.watchMobileBar();
  }

  get section() {
    return this.closest('.shopify-section') || document;
  }

  slug(text, used) {
    let base = text.toLowerCase().trim().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-+|-+$/g, '') || 'section';
    let id = base, n = 2;
    while (used.has(id) || document.getElementById(id)) id = `${base}-${n++}`;
    used.add(id);
    return id;
  }

  buildToc() {
    if (!this.content) return;
    const depth = (this.dataset.tocDepth || 'h2').split(',').map((s) => s.trim());
    const headings = Array.from(this.content.querySelectorAll(depth.join(',')));
    const used = new Set();
    headings.forEach((h) => { if (!h.id) h.id = this.slug(h.textContent, used); });

    const lists = this.section.querySelectorAll('[data-docs-toc-list]');
    if (headings.length < 2 || !lists.length) return;
    lists.forEach((list) => {
      list.innerHTML = '';
      headings.forEach((h) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = `#${h.id}`;
        a.textContent = h.textContent;
        a.dataset.level = h.tagName.substring(1);
        a.addEventListener('click', (e) => {
          e.preventDefault();
          history.replaceState(null, '', `#${h.id}`);
          h.scrollIntoView({ behavior: theme.config.motionReduced ? 'auto' : 'smooth', block: 'start' });
          const details = list.closest('details');
          if (details) details.open = false;
        });
        li.appendChild(a);
        list.appendChild(li);
      });
    });
    this.section.querySelectorAll('[data-docs-toc], [data-docs-toc-mobile], [data-docs-toc-side]').forEach((el) => el.removeAttribute('hidden'));
    this.watchBottomBar();

    if ('IntersectionObserver' in window) {
      const links = Array.from(this.section.querySelectorAll('[data-docs-toc-list] a'));
      const current = this.section.querySelectorAll('[data-docs-toc-current]');
      let activeIndex = -1;
      const setActive = (id) => {
        links.forEach((a) => a.classList.toggle('is-active', a.getAttribute('href') === `#${id}`));
        const index = headings.findIndex((el) => el.id === id);
        if (index === activeIndex) return;
        const h = headings[index];
        // the closed bar's label flips in the scroll direction: reading on,
        // the new heading rises from below; scrolling back, it drops from above
        const dir = index > activeIndex ? 'up' : 'down';
        activeIndex = index;
        current.forEach((el) => {
          el.textContent = h ? h.textContent : '';
          el.hidden = !h;
          if (theme.config.motionReduced || !h) return;
          el.classList.remove('is-flip-up', 'is-flip-down');
          void el.offsetWidth;
          el.classList.add(`is-flip-${dir}`);
        });
      };
      const observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActive(visible[0].target.id);
      }, { rootMargin: '-10% 0px -70% 0px', threshold: 0 });
      headings.forEach((h) => observer.observe(h));
    }
  }

  watchMobileBar() {
    const bar = this.section.querySelector('.docs-hub__mobilebar');
    if (!bar) return;
    let ticking = false;
    const check = () => {
      ticking = false;
      const top = parseFloat(getComputedStyle(bar).top) || 0;
      bar.classList.toggle('is-stuck', window.scrollY > 0 && bar.getBoundingClientRect().top <= top + 1);
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(check); } }, { passive: true });
    check();
  }

  watchBottomBar() {
    const bar = this.section.querySelector('.docs-hub__toc-mobile--bottom');
    if (!bar || !this.content || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.some((e) => e.isIntersecting);
      bar.classList.toggle('is-offscreen', !visible);
      if (!visible) bar.open = false;
    }, { rootMargin: '-15% 0px 0px 0px', threshold: 0 });
    observer.observe(this.content);
    const backdrop = this.section.querySelector('[data-docs-toc-backdrop]');
    document.addEventListener('click', (e) => { if (bar.open && !bar.contains(e.target)) bar.open = false; });
    bar.addEventListener('toggle', () => {
      backdrop?.classList.toggle('is-active', bar.open);
      if (bar.open) bar.querySelector('.docs-hub__toc-list a.is-active')?.scrollIntoView({ block: 'nearest' });
    });
  }

  bindSearch() {
    this.section.querySelectorAll('[data-docs-search]').forEach((input) => {
      const nav = input.closest('.drawer__scrollable, .docs-hub__sticky')?.querySelector('[data-docs-nav]');
      if (!nav) return;
      const items = Array.from(nav.querySelectorAll('[data-docs-item]'));
      const groups = Array.from(nav.querySelectorAll('[data-docs-group]'));
      const empty = nav.querySelector('[data-docs-empty]');
      const searchAll = nav.querySelector('[data-docs-search-all]');
      // fold British/American spellings and diacritics so "color" finds "Colours"
      const norm = (t) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/our\b/g, 'or').replace(/ours\b/g, 'ors').replace(/is(e|ed|es|ing|ation)\b/g, 'iz$1').replace(/ll(ed|ing)\b/g, 'l$1');
      items.forEach((li) => { li.dataset.docsNorm = norm(li.dataset.docsText || ''); });
      input.addEventListener('input', () => {
        const q = input.value.trim();
        const words = norm(q).split(/\s+/).filter(Boolean);
        let shown = 0;
        items.forEach((li) => {
          const hit = !words.length || words.every((w) => li.dataset.docsNorm.includes(w));
          li.hidden = !hit;
          if (hit) shown++;
        });
        if (searchAll) {
          const url = new URL(searchAll.getAttribute('href'), location.origin);
          url.searchParams.set('q', q);
          searchAll.href = url.pathname + url.search;
        }
        groups.forEach((g) => {
          const visible = Array.from(g.querySelectorAll('[data-docs-item]:not([hidden])'));
          g.hidden = !visible.length;
          const details = g.querySelector('details');
          if (details && q) details.open = true;
          // tree connectors: re-mark the visible ends (hidden rows still count as :first/:last-child)
          g.querySelectorAll('[data-docs-item]').forEach((li) => li.classList.remove('is-tree-first', 'is-tree-last'));
          if (q && visible.length) {
            visible[0].classList.add('is-tree-first');
            visible[visible.length - 1].classList.add('is-tree-last');
          }
        });
        if (empty) empty.hidden = shown > 0;
      });
    });
  }

  bindCode() {
    if (!this.content || !navigator.clipboard) return;
    this.content.querySelectorAll('pre').forEach((pre) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'docs-hub__copy';
      btn.textContent = this.dataset.copyLabel || 'Copy';
      btn.addEventListener('click', () => {
        navigator.clipboard.writeText(pre.innerText).then(() => {
          btn.textContent = this.dataset.copiedLabel || 'Copied';
          setTimeout(() => { btn.textContent = this.dataset.copyLabel || 'Copy'; }, 1500);
        });
      });
      pre.appendChild(btn);
    });
  }

  bindLightbox() {
    if (!this.content || this.dataset.lightbox !== 'true' || typeof HTMLDialogElement === 'undefined') return;
    this.content.setAttribute('data-lightbox-on', '');
    this.content.addEventListener('click', (e) => {
      const img = e.target.closest('img');
      if (!img || img.closest('a')) return;
      let dialog = document.getElementById('DocsLightbox');
      if (!dialog) {
        dialog = document.createElement('dialog');
        dialog.id = 'DocsLightbox';
        dialog.className = 'docs-lightbox';
        dialog.innerHTML = '<button type="button" class="docs-lightbox__close" aria-label="Close">&times;</button><img alt="">';
        dialog.addEventListener('click', (ev) => { if (ev.target === dialog || ev.target.classList.contains('docs-lightbox__close')) dialog.close(); });
        document.body.appendChild(dialog);
      }
      const big = dialog.querySelector('img');
      big.src = img.currentSrc || img.src;
      big.alt = img.alt || '';
      dialog.showModal();
    });
  }

  bindVideo() {
    this.section.querySelectorAll('[data-docs-video-play]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const wrap = btn.closest('[data-docs-video]');
        const iframe = document.createElement('iframe');
        iframe.src = btn.dataset.embed;
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.title = btn.getAttribute('aria-label') || 'Video';
        wrap.replaceChildren(iframe);
      }, { once: true });
    });
  }

  showVersion() {
    const v = window.theme && ((theme.settings && theme.settings.themeVersion) || theme.themeVersion);
    if (!v) return;
    this.section.querySelectorAll('[data-docs-version]').forEach((el) => { el.textContent = `v${v}`; });
  }

  rememberGroups() {
    const key = 'docs-hub-groups';
    let state = {};
    try { state = JSON.parse(localStorage.getItem(key) || '{}'); } catch (e) { state = {}; }
    this.section.querySelectorAll('.docs-nav--sidebar details').forEach((d) => {
      const name = d.querySelector('.docs-nav__group-title')?.textContent.trim();
      if (!name) return;
      if (name in state && !d.closest('[data-docs-group]').querySelector('.is-current')) d.open = state[name];
      d.addEventListener('toggle', () => {
        state[name] = d.open;
        try { localStorage.setItem(key, JSON.stringify(state)); } catch (e) { /* storage unavailable */ }
      });
    });
  }
}
customElements.define('docs-hub', DocsHub);
